import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Chip, Divider, Button,
  Card, CardActionArea, Grid, List, ListItemButton,
  ListItemText, Badge,
} from '@mui/material';
import ArrowBackIcon          from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon      from '@mui/icons-material/CalendarToday';
import AccessTimeIcon         from '@mui/icons-material/AccessTime';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import HistoryIcon            from '@mui/icons-material/History';
import CampaignOutlinedIcon   from '@mui/icons-material/CampaignOutlined';
import ArticleOutlinedIcon    from '@mui/icons-material/ArticleOutlined';
import LoginIcon              from '@mui/icons-material/Login';
import SendIcon               from '@mui/icons-material/Send';
import GroupAddIcon           from '@mui/icons-material/GroupAdd';
import EventIcon              from '@mui/icons-material/Event';
import EmailIcon              from '@mui/icons-material/Email';
import VerifiedIcon           from '@mui/icons-material/Verified';
import SchoolIcon             from '@mui/icons-material/School';
import WorkIcon               from '@mui/icons-material/Work';
import MenuBookIcon           from '@mui/icons-material/MenuBook';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PsychologyIcon         from '@mui/icons-material/Psychology';
import BusinessIcon           from '@mui/icons-material/Business';
import BookmarkBorderIcon     from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon           from '@mui/icons-material/Bookmark';
import PushPinIcon            from '@mui/icons-material/PushPin';
import EuroSymbolIcon         from '@mui/icons-material/EuroSymbol';
import PeopleIcon             from '@mui/icons-material/People';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import OpenInNewIcon          from '@mui/icons-material/OpenInNew';
import AutoAwesomeIcon        from '@mui/icons-material/AutoAwesome';
import TuneIcon               from '@mui/icons-material/Tune';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import BoltIcon               from '@mui/icons-material/Bolt';
import CreditCardIcon         from '@mui/icons-material/CreditCard';
import PowerSettingsNewIcon   from '@mui/icons-material/PowerSettingsNew';
import LockIcon               from '@mui/icons-material/Lock';
import LockOpenIcon           from '@mui/icons-material/LockOpen';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon       from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon       from '@mui/icons-material/InfoOutlined';
import DownloadIcon           from '@mui/icons-material/Download';
import DescriptionIcon        from '@mui/icons-material/Description';
import FolderIcon             from '@mui/icons-material/Folder';
import FolderSpecialIcon      from '@mui/icons-material/FolderSpecial';
import AccountTreeIcon        from '@mui/icons-material/AccountTree';
import AttachMoneyIcon        from '@mui/icons-material/AttachMoney';
import CampaignIcon           from '@mui/icons-material/Campaign';
import PreviewIcon            from '@mui/icons-material/Preview';
import RateReviewIcon         from '@mui/icons-material/RateReview';
import SaveIcon               from '@mui/icons-material/Save';
import EditIcon               from '@mui/icons-material/Edit';
import TaskIcon               from '@mui/icons-material/Task';
import SearchIcon             from '@mui/icons-material/Search';
import FilterListIcon         from '@mui/icons-material/FilterList';
import ChevronRightIcon       from '@mui/icons-material/ChevronRight';
import AccountBalanceIcon     from '@mui/icons-material/AccountBalance';
import logo    from 'media/otable_main.png';
import logoAlt from 'media/otable_alt.png';
import { useUserContext } from 'shared/contexts/UserContext';

const BRAND_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
const ACCENT         = '#7c3aed';
const ACCENT_LIGHT   = '#F3F0FF';


const PLATFORM_NEWS = [
  { date: 'Nov 2026', text: 'New: proposals and paper drafts are private to your team and stored encrypted. Otable staff cannot open them.' },
  { date: 'Oct 2026', text: 'New: Positions in the Collaboration Hub. Announce openings with a verified affiliation, apply with your CV.' },
  { date: 'Oct 2026', text: 'New: Labs, a page for your research group with members, news, positions, a team chat and an optional public page.' },
  { date: 'Oct 2026', text: 'New: funding calls picked for you from your profile, reviewed by our team, one email at a time.' },
  { date: 'Oct 2026', text: 'Publication Writer: autosave, change badges on tabs, and the full editor in the abstract.' },
  { date: 'Oct 2026', text: 'New: the Publication Writer — co-write papers with journal styles, references and tracked changes.' },
  { date: 'Jul 2026', text: 'Horizon Europe 2026 calls are now synced and searchable.' },
  { date: 'Jul 2026', text: 'Proposal TODO panel now supports assignee and section linking.' },
  // { date: 'Jun 2026', text: 'Otable joins the Open Research Europe publishing initiative.' },
  { date: 'Jun 2026', text: 'New: verified affiliation badges for institutional members.' },
];

const ARTICLES = [
  {
    slug: 'research-privacy',
    title: 'Your Unpublished Research Stays Yours',
    subtitle: 'Proposals and paper drafts on Otable are private to your team and stored encrypted. Here is exactly what that means, and what comes next.',
    category: 'Feature Deep-Dive',
    date: 'November 2026',
    readTime: '5 min read',
    coverEmoji: '🔒',
    tags: ['Privacy', 'Proposals', 'Publications'],
    content: [
      {
        type: 'lead',
        text: 'A proposal before submission and a paper before publication hold the most valuable thing a researcher has: an idea nobody else has yet. Writing it together online should not mean handing it over. On Otable, your drafts are private to your team, and Otable staff cannot open them.',
      },
      { type: 'h2', text: 'Who can read your drafts' },
      {
        type: 'p',
        text: 'Only the members of the consortium a proposal or publication belongs to. Otable gives a document to its members and to nobody else: there is no administrator screen that lists or opens proposals or papers, and staff accounts have no exception to this rule. Someone who leaves a proposal loses access to it at once.',
      },
      { type: 'h2', text: 'Encrypted where it is stored' },
      {
        type: 'p',
        text: 'The text of your documents is encrypted before it is written to our database: the sections, the abstract, comments on sections and work packages, the team chat and every saved version. The key is kept apart from the database, so a copy of the database or of a backup shows only unreadable ciphertext.',
      },
      { type: 'h2', text: 'AI only when you choose it, and on the record' },
      {
        type: 'p',
        text: 'Your text leaves Otable only when a member of your team uses an AI feature: the AI writing assistant, the AI review, or a prompt sent with your own API key. Each time, Otable records which section went out, who sent it and when, and your whole team can see that list.',
      },
      {
        type: 'steps',
        items: [
          { title: 'Find it in the writer', text: 'Every proposal and publication shows a Private chip at the top of the writer.' },
          { title: 'Open the Privacy tab', text: 'Click the chip to see how the document is kept and every time its text was sent to the AI provider. Members only.' },
          { title: 'Nothing listed?', text: 'Then the text has never left your team.' },
        ],
      },
      { type: 'h2', text: 'What we do not claim, yet' },
      {
        type: 'p',
        text: 'We would rather tell you the limits than overstate them. Today\'s encryption protects your drafts where they are stored. It is not end-to-end: the server holds the key so that it can show your text to your team, which means the people who run the server could, by deliberately going around Otable, decrypt it. Document titles and the results the AI returns are not encrypted yet either.',
      },
      {
        type: 'p',
        text: 'The next step is opt-in private documents with end-to-end encryption, where the key lives only in your team members\' browsers and nobody else, us included, can read the text. AI features on such a document will run only after you confirm that its text will be sent to the AI provider.',
      },
      {
        type: 'highlight',
        text: 'Transparency where you choose it, privacy where you need it: share your reflections and publications openly, and keep the work in progress between you and your team.',
      },
    ],
  },
  {
    slug: 'positions-on-otable',
    title: 'Announcing and Finding Positions on Otable',
    subtitle: 'PhD and postdoc positions, engineer and faculty roles, internships: who can post them, how to apply, and how applications are reviewed.',
    category: 'Guide',
    date: 'October 2026',
    readTime: '5 min read',
    coverEmoji: '💼',
    tags: ['Positions', 'Labs', 'Guide'],
    content: [
      {
        type: 'lead',
        text: 'Most research positions are filled through someone who knew someone. The rest are posted on a university page few people find, often in one language only. Positions on Otable puts the openings of labs, consortia, universities and companies in one place, next to the people and groups behind them.',
      },
      { type: 'h2', text: 'Who can announce a position' },
      {
        type: 'steps',
        items: [
          { title: 'Anyone with a verified affiliation', text: 'Post for your organization: a professor for their university, a founder or CEO for their company. Verify your affiliation on your profile with a document or your institutional email.' },
          { title: 'Labs', text: 'The PI, and the members the PI lets edit the lab page.' },
          { title: 'Research consortia', text: 'The coordinator or project leader. Publication consortia, which only write papers, do not post positions.' },
        ],
      },
      {
        type: 'p',
        text: 'Open the Collaboration Hub, choose Positions and click Post a position. Pick who announces it, then the title, type (PhD, postdoc, research assistant, scientist, engineer, faculty and more), full-time or part-time, fellowship or internship, on site, hybrid or remote, the location, the deadline and a description in Markdown. A salary or stipend and an external application link are optional.',
      },
      {
        type: 'highlight',
        text: 'Every opening is also announced on the Interests page, with a button to the position. It leaves the page when you close the position.',
      },
      { type: 'h2', text: 'Applying' },
      {
        type: 'p',
        text: 'Filter the Positions tab by type, by on site, hybrid or remote, and by country or city. Open a position to read it in full and click Apply on Otable: write a cover note and attach the CV on your profile with one click, or upload a PDF. Your Otable profile goes with it, so your publications and experience are one click away for the reader.',
      },
      { type: 'h2', text: 'Reviewing applications' },
      {
        type: 'p',
        text: 'Whoever posted the position, and the leaders of the lab, consortium or organization it belongs to, see the applicants in one list: their profile, cover note and CV, with a status to keep track of each one (new, shortlisted or declined). They are notified of every new application. CVs are never public files; only the people reviewing the position and the applicant can open them.',
      },
      {
        type: 'p',
        text: 'Closing a position keeps it, and its applications, under Posted / managed by me in the Positions tab, so you can reopen it later.',
      },
    ],
  },
  {
    slug: 'labs-on-otable',
    title: 'Labs on Otable: A Home Page for Your Research Group',
    subtitle: 'Members and alumni, news, open positions, projects and publications in one page, with a team chat, matching funding calls and a public page you control.',
    category: 'Guide',
    date: 'October 2026',
    readTime: '8 min read',
    coverEmoji: '🔬',
    tags: ['Labs', 'Collaboration', 'Guide'],
    content: [
      {
        type: 'lead',
        text: 'A research group outlives every single proposal it writes. People join as students and leave as postdocs, papers come out, projects start and end, and there is always an open position nobody hears about. A lab on Otable gives the group one page for all of this, run by its principal investigator and kept up to date by the people in it.',
      },
      { type: 'h2', text: '1. Create the lab' },
      {
        type: 'p',
        text: 'Open the Collaboration Hub and choose the Labs tab, then Create lab. A lab belongs to an organization, so you can create one only under an institution you are affiliated with; if the list is empty, add your affiliation on your profile first. Give the lab a name, a short description, a few keywords and a colour. You become its principal investigator.',
      },
      {
        type: 'steps',
        items: [
          { title: 'Cover and colour', text: 'Upload a cover image and pick the page colour. The whole page takes on a soft tint of it.' },
          { title: 'Members', text: 'Invite people from Otable with the title they hold in the lab, which can differ from the one on their profile: a professor on paper can be the lab manager here. They appear on the lab, with their profile\'s publications, only after they accept the invitation.' },
          { title: 'Edit rights', text: 'Let trusted members edit the page. Only the PI hands out edit rights, decides who can see the page and can delete the lab.' },
        ],
      },
      { type: 'h2', text: '2. What the page shows' },
      {
        type: 'p',
        text: 'The page brings together everything a visitor wants to know about a group. News items can feature a person, whose photo appears next to the news; an item about the lab as a whole shows the lab\'s initials instead. Active projects are listed first, and completed ones fold into a panel underneath. The PI\'s active projects from their own profile appear automatically, so they are kept in one place. Publications are gathered from the current members\' profiles, newest first and each paper once, so nobody enters them twice. Alumni can be Otable users or simply a name, with the years they spent in the lab and where they are now.',
      },
      {
        type: 'highlight',
        text: 'Your lab also appears on your profile, under My Organizations, with your title in it.',
      },
      { type: 'h2', text: 'Theses and publications from the members' },
      {
        type: 'p',
        text: 'Members add the lab\'s theses (PhD, master\'s and undergraduate, with the author, year and supervisor) and publications that are not on anyone\'s profile. The PI confirms each one before it appears; the PI\'s own entries appear at once. The PI can also hide any publication from the lab page, for example an old conference abstract, and show it again later. The description is written in Markdown, so it can have headings, lists and links.',
      },
      { type: 'h2', text: '3. Open positions and applications' },
      {
        type: 'p',
        text: 'Post a PhD, postdoc or other opening with a description and a deadline. Visitors apply on Otable with a cover note and a CV: the one on their profile with one click, or an uploaded PDF. The PI gets a notification and sees all applicants for a position in one list, with their profile, note and CV, and marks each one as new, shortlisted or declined. CVs are never public files; only the lab\'s editors and the applicant can open them.',
      },
      { type: 'h2', text: '4. Working together' },
      {
        type: 'p',
        text: 'Members have a team chat on the lab page. Instead of a notification for every message, each member gets one card with the latest message and the count, until they read it. The page also lists the open funding calls that fit the lab best, matched on its keywords, its members\' research areas and skills, and the words in its description, projects and publications.',
      },
      { type: 'h2', text: '5. Followers' },
      {
        type: 'p',
        text: 'Anyone on Otable can follow a lab from its card or page, and the PI is told who started following. Followers see the lab\'s news, new positions, new projects and new members in their activity feed. They can also suggest a funding call to the lab or send it a message; both reach the PI. The followers list itself is visible to the lab\'s members only, and the PI sees statistics on followers, page views, suggested calls and applications.',
      },
      { type: 'h2', text: '6. Who can see the page' },
      {
        type: 'steps',
        items: [
          { title: 'Lab members only', text: 'A private page for the group. It is hidden from the Collaboration Hub, search and feeds.' },
          { title: 'Everyone on Otable', text: 'The default: signed-in users can find, follow and contact the lab.' },
          { title: 'Public', text: 'Also a public page at otable.net/labs/your-lab-name that anyone can open without an account. Choose the web address in Edit lab.' },
        ],
      },
      {
        type: 'p',
        text: 'Choose the web address in Edit lab, for example otable.net/labs/soil-lab. Preview public page shows the page exactly as visitors will see it, also before you make it public. Every section has a switch there, so you decide what is shown: hide the alumni, keep the news and the theses. Followers, statistics, applications and the team chat are never public.',
      },
      {
        type: 'highlight',
        text: 'Ready? Open the Collaboration Hub, go to Labs and click Create lab.',
      },
    ],
  },
  {
    slug: 'personalised-funding-calls',
    title: 'Funding Calls Picked for You: How Personalised Call Updates Work',
    subtitle: 'Up to five open calls a time, chosen from your profile with a reason for each, checked by a person before they reach you.',
    category: 'Feature Deep-Dive',
    date: 'October 2026',
    readTime: '5 min read',
    coverEmoji: '🎯',
    tags: ['Funding', 'AI', 'Notifications'],
    content: [
      {
        type: 'lead',
        text: 'Hundreds of calls open every year across Horizon Europe, TÜBİTAK and the national agencies. Searching them is a job of its own, and the one that fits you best is easy to miss. Otable now does the first pass for you and sends you a short, personal selection.',
      },
      { type: 'h2', text: 'How the calls are chosen' },
      {
        type: 'p',
        text: 'Otable starts from the open and upcoming calls whose deadline leaves enough time to prepare an application. It ranks them against your profile: your research areas and skills count most, then the words in your headline, bio, publications and projects. An AI model then picks up to five of the best matches and writes one sentence for each on why it fits you. If the AI is unavailable, you still get the best matches with the reason they were picked.',
      },
      { type: 'h2', text: 'A person reads every email first' },
      {
        type: 'p',
        text: 'The selections are drafts until a member of our team has read them. Only approved emails are sent. You receive one personal email with all your calls, never one email per call, and a call you have already been offered is not offered again.',
      },
      {
        type: 'highlight',
        text: 'A thin profile gets generic picks. Add a few research areas and skills, and the selection becomes much sharper.',
      },
      { type: 'h2', text: 'Your choice' },
      {
        type: 'p',
        text: 'Funding-call emails are on for new accounts. You can change or switch them off at any time in Settings, together with your other email preferences. Calls you follow are handled the same way: when one changes, you get a single summary of the updates rather than an email for each.',
      },
      { type: 'h2', text: 'For labs too' },
      {
        type: 'p',
        text: 'A lab page shows the calls that fit the group, matched on the lab\'s keywords and its members\' expertise, and anyone on Otable can suggest a call to a lab, which reaches its principal investigator.',
      },
    ],
  },
  {
    slug: 'publication-writer-whats-new',
    title: "What's New in the Publication Writer",
    subtitle: 'Autosave, a page that remembers where you were, badges for co-authors\' changes, and the full toolset in the abstract.',
    category: 'Feature Deep-Dive',
    date: 'October 2026',
    readTime: '4 min read',
    coverEmoji: '🖋️',
    tags: ['Publications', 'Writing', 'Collaboration'],
    content: [
      {
        type: 'lead',
        text: 'Since the Publication Writer opened, most of the requests we received were about the small frictions of writing together: losing a paragraph to a refresh, not knowing what changed while you were away, being notified about every keystroke of a co-author. This update is about those.',
      },
      { type: 'h2', text: 'Autosave, without the noise' },
      {
        type: 'p',
        text: 'While you write, your text is saved every minute, so your co-authors see your progress. Autosaves never notify anyone and keep one version per editing session, so the history stays readable. Co-authors are notified when you press Save, with one card per editor that collects your saves until they read it. If a co-author saved the same section in the meantime, the two edits are merged; when you both changed the same lines, autosave pauses and your next Save lets you compare the versions.',
      },
      { type: 'h2', text: 'A refresh keeps your place' },
      {
        type: 'p',
        text: 'Reloading the page brings you back to the tab and the section you were editing, in full screen if you were, and any text typed since the last autosave is still there.',
      },
      { type: 'h2', text: 'See what others changed' },
      {
        type: 'p',
        text: 'Each tab carries a small red badge counting what changed since you last opened it: new authors, your co-authors\' saves to the sections or the abstract, new references, and changes to work packages and the budget. Opening the tab clears it.',
      },
      { type: 'h2', text: 'The abstract gets the full editor' },
      {
        type: 'p',
        text: 'The abstract now has the same tools as the sections: figures, comments, tracked changes in Reviewing mode and, in manuscripts, citations from your bibliography.',
      },
      {
        type: 'highlight',
        text: 'All your proposals and publications are one click away under My Works, in the top bar and in the sidebar, marked as publication or proposal, with the acronym and title.',
      },
    ],
  },
  {
    slug: 'publication-writer-guide',
    title: 'Writing a Paper with the Publication Writer: A Practical Guide',
    subtitle: 'From choosing the target journal to exporting a submission-ready Word file: how to co-write a manuscript on Otable, step by step.',
    category: 'Guide',
    date: 'October 2026',
    readTime: '9 min read',
    coverEmoji: '🖋️',
    tags: ['Publications', 'Collaboration', 'Guide'],
    content: [
      {
        type: 'lead',
        text: 'A paper with five co-authors usually lives in a dozen places: a Word file passed around by email, a reference manager only one person can open, a chat thread about who writes the Discussion. The Publication Writer puts the manuscript, the references, the review and the task list in one workspace that every co-author can open. This guide walks through it in the order you will use it.',
      },
      { type: 'h2', text: '1. Start a publication' },
      {
        type: 'p',
        text: 'Publications belong to a consortium, the same way proposals do. If you already have a project consortium, open it and go to the Publications tab. If not, create a new consortium and choose the type Publication: it only asks for a name and a deadline, with no funding call or project duration. Every member of the consortium is a potential co-author.',
      },
      {
        type: 'steps',
        items: [
          { title: 'Create Publication', text: 'On the Publications tab, give the paper a working title and choose the publication type (journal article, review, book chapter and so on).' },
          { title: 'Pick the target journal', text: 'Search for the journal by name. Otable looks it up in the open Zotero Style Repository, which covers thousands of journals, and sets the citation style from it.' },
          { title: 'Start writing', text: 'The manuscript opens with the sections of an original research article: Introduction, Materials and Methods, Results, Discussion, Conclusions, Author Contributions, Conflict of Interest and References.' },
        ],
      },
      {
        type: 'p',
        text: 'No ideas yet? The AI publication ideas button next to Create Publication suggests papers your team could write, based on the members\' profiles and publications. You will also find it under AI ideas on your profile page, next to project ideas.',
      },
      { type: 'h2', text: '2. Get the author list right' },
      {
        type: 'p',
        text: 'Authors is the first tab, because authorship is usually the first thing a team discusses. Consortium members are added automatically, with their affiliations. Move authors up or down into the order you agreed, mark the corresponding author, and add external co-authors who do not have an Otable account by name and affiliation. The order you set here is the order in the Preview and the exported manuscript.',
      },
      { type: 'h2', text: '3. Choose a journal template, and change it later' },
      {
        type: 'p',
        text: 'Under Sections, Select template offers article structures for common journal formats. Journals rarely accept on the first try, so switching is safe. When you move to a new template, every section whose title also exists in the new one keeps its text. Otable warns you only if a section you have already written has no counterpart in the new structure, so nothing disappears silently.',
      },
      {
        type: 'highlight',
        text: 'Resubmitting elsewhere? Change the target journal in Settings. Matching sections keep their text, and the in-text citations and the reference list are reformatted in the new journal\'s style.',
      },
      { type: 'h2', text: '4. Build the bibliography' },
      {
        type: 'p',
        text: 'The Bibliography tab holds the references for the whole paper. It is called Bibliography so it is not confused with the References section at the end of the manuscript. You can add references in four ways:',
      },
      {
        type: 'steps',
        items: [
          { title: 'From Google Scholar', text: 'Click the cite link under a result, choose BibTeX and paste it in. You can paste several entries at once.' },
          { title: 'From your reference manager', text: 'Export RIS or BibTeX from Zotero, Mendeley or EndNote and paste or upload it.' },
          { title: 'By DOI', text: 'Paste one or more DOIs and the metadata is fetched for you.' },
          { title: 'By search', text: 'Search Crossref by title or author and add the right hit.' },
        ],
      },
      { type: 'h2', text: '5. Cite while you write' },
      {
        type: 'p',
        text: 'In the section editor, the Insert reference button (also in the right-click menu) opens your bibliography. Pick one or more references and the citation appears at the cursor. The cited reference is added to the References section automatically, and numbering follows the order of first citation. Format references switches the whole paper between common styles such as Nature, Science, Cell, Vancouver, APA and Harvard, or uses your target journal\'s own style. Author-date styles show "(Smith et al., 2024)" right in the text.',
      },
      { type: 'h2', text: '6. Write and review together' },
      {
        type: 'p',
        text: 'The editor has two modes. In Editing mode, changes go straight into the text. Switch to Reviewing and every insertion and deletion is tracked: inserted text is underlined and deleted text is struck through, in a colour for each author. Hover over a change to see who made it and when. Accept or reject changes one by one at the cursor, or all at once. Comments work in both modes: select a passage, right-click and choose Add Comment.',
      },
      {
        type: 'p',
        text: 'A few smaller tools help with the writing itself. Indent and outdent paragraphs. Use Synonyms to get alternatives for the word at the cursor, from the free Datamuse thesaurus. Insert tables from the toolbar. Full screen mode keeps the section title, the word count and the Save and Close buttons pinned at the top while you scroll.',
      },
      {
        type: 'highlight',
        text: 'Right-click anywhere in the text for the most used tools: bold, italic, lists, indent, synonyms, insert reference, figure placeholder, table, and accept or reject a change. Ctrl + right-click still opens your browser\'s own menu, for example for spell-check suggestions.',
      },
      { type: 'h2', text: '7. Figures' },
      {
        type: 'p',
        text: 'Click Insert figure in the toolbar or the right-click menu, write the caption, and upload the image. Otable compresses it on upload: charts and diagrams are kept lossless, photographs and micrographs are compressed, and every figure is resized to at most 1600 pixels and 400 KB. Your original file is never stored, so keep it for the journal submission, which usually asks for high-resolution TIFF or EPS files anyway. Figures count towards your 5 MB of storage on Otable; images you take out of the text no longer count.',
      },
      {
        type: 'p',
        text: 'Figures are numbered automatically in the order they appear across the manuscript. If you move a section, Figure 3 becomes Figure 2 without any work from you. The Preview shows the images with their captions, and the Word export embeds them as PNG or JPEG. You can also insert a figure without an image: it appears as a dashed "Insert Figure N here" box, ready for you to add the image in your local copy. To change a caption or the image, click the figure.',
      },
      { type: 'h2', text: '8. Plan the work on Boards' },
      {
        type: 'p',
        text: 'The Boards button next to the status and version chips turns the writer into a project board. Coordinators can create as many boards and lists as the paper needs, for example Analysis, Writing and Submission. Tasks can be nested into a hierarchy. Items on the to-do list are added to the most recently edited board, and a menu next to each item lets you move it to another board.',
      },
      { type: 'h2', text: '9. Preview and export' },
      {
        type: 'p',
        text: 'The Preview tab shows the manuscript as reviewers will read it, with continuous line numbers, which many journals require. The Download menu offers three files:',
      },
      {
        type: 'steps',
        items: [
          { title: 'Manuscript (.docx)', text: 'The formatted paper with line numbers, citations in the selected style and the reference list.' },
          { title: 'EndNote version (.docx)', text: 'The same paper with EndNote temporary citations such as {Smith, 2024 #3}. Open it in Word and run EndNote\'s Update Citations and Bibliography to turn them into live EndNote fields.' },
          { title: 'References (.ris)', text: 'Your bibliography, ready to import into EndNote, Zotero or Mendeley.' },
        ],
      },
      { type: 'h2', text: '10. Use AI with your own API key' },
      {
        type: 'p',
        text: 'The AI writing assistant can draft or revise a section with the paper\'s context in mind. Besides Otable credits, you can now use your own OpenAI or Anthropic API key. The key is encrypted in your browser with a passphrase that only you know. The encrypted key never reaches our servers in readable form, and requests go from your browser directly to the AI provider. Nobody at Otable, administrators included, can see it. You pay the provider directly at their rates.',
      },
      {
        type: 'highlight',
        text: 'Ready to start? Open a consortium, go to the Publications tab and click Create Publication. It takes about a minute to get to the first sentence.',
      },
    ],
  },
  {
    slug: 'why-researchers-need-collaboration-platform',
    title: 'Why Every Research Team Needs a Dedicated Collaboration Platform',
    subtitle: 'The hidden cost of fragmented academic networks—and how structured collaboration changes outcomes.',
    category: 'Opinion',
    date: 'July 2026',
    readTime: '6 min read',
    coverEmoji: '🔬',
    tags: ['Networking', 'Collaboration', 'Opinion'],
    content: [
      {
        type: 'lead',
        text: 'Academic collaboration is not a luxury. In a funding landscape that increasingly rewards interdisciplinary consortia over lone-wolf investigators, the ability to find the right partners quickly—and coordinate them effectively—has become a core research competency.',
      },
      { type: 'h2', text: 'The Fragmentation Problem' },
      {
        type: 'p',
        text: 'Talk to any principal investigator and you will hear the same stories. A promising call for proposals appears. The deadline is ten weeks out. The required consortium must span at least three countries, cover two disciplines, and include both academic and industry partners. The PI spends the first three weeks doing nothing but sending emails, browsing LinkedIn, and working their personal network—time that should have gone to the science.',
      },
      {
        type: 'p',
        text: 'The problem is structural. Academic expertise is scattered across thousands of institutions, departments, and research groups. There is no shared, searchable layer that maps who works on what, who has collaborated with whom, and which teams have a track record of successful funded projects. Researchers navigate this landscape entirely through social capital—a resource that is highly unequally distributed.',
      },
      {
        type: 'highlight',
        text: 'Studies consistently show that collaboration quality is the single strongest predictor of research output. Yet most institutions invest almost nothing in the infrastructure that enables it.',
      },
      { type: 'h2', text: 'What Structured Collaboration Looks Like' },
      {
        type: 'p',
        text: 'A dedicated research collaboration platform changes the dynamic in three concrete ways. First, it turns expertise discovery from a social exercise into a search problem. Instead of relying on who you happen to know, you query a structured registry of researchers filtered by field, method, institution, geography, and funding history.',
      },
      {
        type: 'p',
        text: 'Second, it replaces ad-hoc email threads with a shared workspace. Consortium formation, document drafting, role assignment, and deadline tracking happen inside a single tool every member can see. Third, it creates institutional memory—when a project ends, its record is preserved and searchable. The next team learns from it rather than starting from zero.',
      },
      { type: 'h2', text: 'The Otable Approach' },
      {
        type: 'p',
        text: 'Otable was built around these three principles. Researchers create verified profiles tied to their institution and field. They join or form consortia with their own membership structure and role hierarchy. Funding calls—sourced from Horizon Europe, national agencies, and sector-specific programmes—are tracked and matched against consortium expertise.',
      },
      {
        type: 'p',
        text: 'The result is not a social network with academic branding. It is a coordination tool built for the specific workflow of research collaboration—from the first "who could we work with?" to the final "submit proposal."',
      },
    ],
  },
  {
    slug: 'building-your-first-consortium',
    title: 'Building Your First Consortium on Otable: A Step-by-Step Guide',
    subtitle: 'From an empty workspace to a fully formed, ready-to-apply research consortium.',
    category: 'Guide',
    date: 'July 2026',
    readTime: '8 min read',
    coverEmoji: '🤝',
    tags: ['Consortium', 'Collaboration', 'Getting Started'],
    preview: 'consortium_steps',
    content: [
      {
        type: 'lead',
        text: 'A consortium is more than a list of names on a cover page. It is a functioning team with defined roles, shared understanding of the scientific objective, and agreed processes for decision-making. Building one well—especially under deadline pressure—requires the right scaffolding from day one.',
      },
      { type: 'h2', text: 'Step 1: Define Your Consortium Before You Recruit' },
      {
        type: 'p',
        text: 'Resist the urge to start inviting people immediately. Before your first outreach, write a one-paragraph statement of intent: what problem are you tackling, what expertise does the consortium need, and what is the target funding call? This statement anchors every subsequent conversation and filters out mismatched candidates early.',
      },
      { type: 'consortium_step', step: 1 },
      { type: 'h2', text: 'Step 2: Use Expert Suggestions to Fill Expertise Gaps' },
      {
        type: 'p',
        text: 'Once you have created your consortium and described its scope, Otable\'s suggestion engine analyses your existing team and highlights expertise gaps. If your consortium covers computational biology but lacks clinical partners, the system surfaces verified researchers with clinical trial experience and relevant publication records.',
      },
      {
        type: 'highlight',
        text: 'Consortium formation works best when you treat it as a structured search problem, not a networking exercise. Define the gaps first; then fill them.',
      },
      { type: 'consortium_step', step: 2 },
      { type: 'h2', text: 'Step 3: Assign Roles and Set Expectations Early' },
      {
        type: 'p',
        text: 'Every Otable consortium uses a role system: Coordinator, Principal Investigator, Work Package Leader, and Collaborator. Assign roles as soon as partners confirm. Clear role assignment prevents the two most common consortium failure modes: duplicated effort and unclaimed responsibility.',
      },
      { type: 'consortium_step', step: 3 },
      { type: 'h2', text: 'Step 4: Move Coordination into the Shared Workspace' },
      {
        type: 'p',
        text: 'Once your consortium is formed, resist the temptation to manage it via email. Use the consortium messaging system for decisions that need a record. Use the proposal TODO panel for task tracking—each item can be assigned to a specific member, tagged by work package, and given a due date.',
      },
      { type: 'consortium_step', step: 4 },
      { type: 'h2', text: 'What to Avoid' },
      {
        type: 'p',
        text: 'Avoid building a consortium around availability rather than expertise. A partner who can start next month but lacks the required competency is worse than no partner—they will consume coordination overhead without adding scientific value.',
      },
    ],
  },
  {
    slug: 'tracking-funding-calls',
    title: 'Never Miss a Funding Call Again: Tracking Opportunities with Otable',
    subtitle: 'How to turn the chaos of research funding into a manageable, searchable pipeline.',
    category: 'Guide',
    date: 'July 2026',
    readTime: '5 min read',
    coverEmoji: '📋',
    tags: ['Funding', 'Horizon Europe', 'Calls'],
    preview: 'funding_platform',
    content: [
      {
        type: 'lead',
        text: 'The European Commission publishes over 200 Horizon Europe calls per year. Add national programmes, sector-specific foundations, and bilateral agreements, and the funding landscape becomes genuinely unmanageable without tooling. Researchers routinely discover relevant calls after the deadline has passed.',
      },
      { type: 'h2', text: 'The Discovery Problem' },
      {
        type: 'p',
        text: 'Most researchers rely on a patchwork of email lists, institutional newsletters, and RSS feeds to stay informed about funding. This approach has two failure modes: too much noise (every call from every programme, regardless of relevance) and unexpected gaps (a relevant call from an unfamiliar programme that never appeared in any subscribed feed).',
      },
      {
        type: 'highlight',
        text: 'The average time between a call opening and a team\'s first awareness of it is 23 days. Given that most calls run for 60–90 days, this is a significant fraction of available preparation time lost to slow discovery.',
      },
      { type: 'funding_platform' },
      { type: 'h2', text: 'How Otable Structures Funding Tracking' },
      {
        type: 'p',
        text: 'Otable integrates directly with Horizon Europe\'s open data API and aggregates calls from a growing list of funding agencies. Each call entry includes the programme, budget envelope, eligibility conditions, deadline, and a plain-language summary of the expected outcomes.',
      },
      { type: 'h2', text: 'Expert Matching Against Open Calls' },
      {
        type: 'p',
        text: 'For calls that require specific expertise you do not yet have in your consortium, the suggestion system works in reverse: start from the call requirements and find researchers whose profiles match. This is particularly useful for ERC Synergy grants where the evaluation panel explicitly assesses complementarity.',
      },
      { type: 'h2', text: 'Building a Funding Pipeline' },
      {
        type: 'p',
        text: 'Research groups that succeed consistently at external funding treat it as a pipeline, not a series of one-off bets. They maintain a rolling shortlist of calls matching their strategic direction and start informal consortium conversations months before a call opens. Otable\'s bookmarking and notification system supports exactly this pattern.',
      },
    ],
  },
  {
    slug: 'navigating-funding-calls',
    title: 'Finding the Right Call: How to Navigate Otable\'s Funding Browser',
    subtitle: 'A step-by-step walkthrough of the agency → programme → call → details hierarchy that turns hundreds of annual opportunities into an actionable shortlist.',
    category: 'Guide',
    date: 'August 2026',
    readTime: '5 min read',
    coverEmoji: '🔎',
    tags: ['Funding', 'Horizon Europe', 'Calls', 'Getting Started'],
    preview: 'funding_workflow',
    content: [
      {
        type: 'lead',
        text: 'The European Commission alone publishes over 200 Horizon Europe calls per year — and that number grows when you include national agencies, bilateral programmes, and sector-specific foundations. Without structure, the search for the right call becomes a research project in itself. Otable\'s funding browser is built around a simple hierarchy: you start broad and narrow down, one level at a time.',
      },
      { type: 'h2', text: 'Step 1 — Choose a Funding Agency' },
      {
        type: 'p',
        text: 'The first screen shows all funding agencies currently active in Otable\'s database. These range from the European Commission and its sub-bodies (ERC, EIC, MSCA) to national agencies such as DFG in Germany, ANR in France, and NWO in the Netherlands. Clicking an agency takes you inside its programme structure — you are not committing to anything, just entering a search context.',
      },
      {
        type: 'p',
        text: 'If you already know which agency you want, the search bar at the top of the agency screen filters the list in real time. You can also pin agencies you return to frequently — pinned agencies appear at the top of the list on every visit.',
      },
      { type: 'funding_agency_selector' },
      { type: 'h2', text: 'Step 2 — Select a Programme' },
      {
        type: 'p',
        text: 'Inside an agency, calls are grouped by programme. For the European Commission this means clusters: Cluster 4 for Digital, Industry and Space; Cluster 6 for Food, Bioeconomy, Natural Resources, Agriculture and Environment; ERC for frontier research; MSCA for mobility and training. Each programme shows a live count of currently open calls and displays the call deadline range, so you can immediately see whether the timing works before reading further.',
      },
      {
        type: 'highlight',
        text: 'Programmes are not just administrative categories — they have different evaluation criteria, consortium size requirements, and TRL expectations. Choosing the wrong programme level to apply to is a structural mistake that no amount of good writing can fix.',
      },
      { type: 'funding_program_selector' },
      { type: 'h2', text: 'Step 3 — Browse and Filter Calls' },
      {
        type: 'p',
        text: 'Inside a programme, the call list shows every active opportunity with its reference number, full title, budget envelope, and days remaining until the deadline. Three filter chips — All, Open, and Closing Soon — let you narrow the view without losing context. "Closing Soon" highlights calls within 30 days of their deadline, which is when preparation time becomes genuinely tight.',
      },
      {
        type: 'p',
        text: 'The search bar within the call list performs a full-text search across call titles, topics, and keywords. If you are looking for calls involving "soil microbiome" or "federated learning," typing those terms surfaces matching calls regardless of which cluster they sit in. Bookmarking a call at this stage adds it to your shortlist on the home dashboard and enables deadline notifications.',
      },
      { type: 'funding_call_selector' },
      { type: 'h2', text: 'Step 4 — Read the Call Detail' },
      {
        type: 'p',
        text: 'The call detail panel is where the funding browser becomes a decision tool. It surfaces the information you need to decide whether to invest proposal time: the budget per project, the minimum number of partners and countries required, the TRL range expected at the start and end of the project, and the explicit list of keywords the call text uses. These keywords matter — they are the terms the evaluation panel will have in mind when they read your proposal.',
      },
      {
        type: 'p',
        text: 'Below the call metadata, Otable shows a consortium match indicator: based on the research areas covered by your current consortium members, how many of the call\'s stated expertise requirements does your team already meet? A consortium that covers 5 out of 6 requirements has a clear signal about where to focus recruitment or restructure work packages.',
      },
      { type: 'funding_call_detail' },
      { type: 'h2', text: 'From Call to Consortium' },
      {
        type: 'p',
        text: 'Once you have identified a call worth pursuing, the logical next step is to assess your consortium against it. If your consortium does not yet exist, this is the point to create one — the call detail page links directly to the consortium creation flow. If you have an existing consortium, the AI assistant can use the call objectives as context when analysing expertise gaps and drafting the first proposal sections. The funding browser is not the end of the workflow; it is the beginning.',
      },
    ],
  },
  // reviewed
  {
    slug: 'collaborative-proposal-writing',
    title: 'Collaborative Proposal Writing: From Outline to Submission',
    subtitle: 'A look inside Otable\'s ProposalWriter and the practices that make joint proposals work.',
    category: 'Feature Deep-Dive',
    date: 'July 2026',
    readTime: '7 min read',
    coverEmoji: '✍️',
    tags: ['Proposals', 'Collaboration', 'Writing'],
    content: [
      {
        type: 'lead',
        text: 'The technical merit of a research idea rarely determines whether a proposal succeeds. Proposal quality—structure, clarity, internal consistency, and explicit response to the evaluation criteria—is where most applications fall short. Proposal quality is almost always an editing and coordination problem, not a scientific one.',
      },
      { type: 'h2', text: 'The Multi-Author Problem' },
      {
        type: 'p',
        text: 'Consortium proposals are inherently multi-author documents. The methodology section comes from one team; the management plan from another; the dissemination strategy from a third. When assembled from separate text documents and email attachments, the result almost always reads like three separate applications stapled together.',
      },
      { type: 'h2', text: 'The Otable Proposal Environment' },
      {
        type: 'p',
        text: 'Otable\'s ProposalWriter organises a proposal into sections mapping to typical programme templates. Each section has a section key (e.g., 1.1, 2.3) and can be assigned to a specific team member as primary author. All content is live-edited in a shared workspace in a collaborative manner where authors are able to track changes in real-time.',
      },
      {
        type: 'highlight',
        text: 'The most time-consuming part of multi-author proposal writing is not the writing—it is the integration. A shared workspace eliminates the integration step entirely.',
      },
      { type: 'h2', text: 'Work Packages and Objectives' },
      {
        type: 'p',
        text: 'Work packages are the structural backbone of most European research proposals. In Otable, objectives, tasks, deliverables, milestones, and budget allocations are defined as structured objects—not free text—so the system can cross-reference them automatically. A deliverable that appears in the work plan also appears in the budget, kept in sync without manual updating.',
      },
      { type: 'h2', text: 'Review and Submission' },
      {
        type: 'p',
        text: 'Before submission, the review workflow allows designated reviewers to annotate sections and flag issues. The coordinator sees all comments in one view, resolves them in the document, and marks sections as cleared. By the time the proposal leaves Otable, every section has a review record.',
      },

      { type: 'h2', text: 'Project Management' },
      {
        type: 'p',
        text: 'Integrated Work Package and Task Management allows the consortium to transition seamlessly from proposal to project execution. The same structured objectives, deliverables, and milestones defined in the proposal become actionable items in the project dashboard, with assigned owners, due dates, and progress tracking.',
      },


    ],
  },
  {
    slug: 'strong-profile-page-for-proposals',
    title: 'Why a Strong Profile Page Builds a Stronger Proposal',
    subtitle: 'Every field in your Otable profile connects directly to your consortium\'s proposal. Here\'s why completeness is a scientific argument, not a formality.',
    category: 'Guide',
    date: 'August 2026',
    readTime: '7 min read',
    coverEmoji: '👤',
    tags: ['Profile', 'Visibility', 'Verification'],
    preview: 'profile_card',
    content: [
      {
        type: 'lead',
        text: 'When a review panel evaluates a European research proposal, they are not only assessing the idea. They are assessing the team. Specifically, they want evidence that the people named in the consortium can actually do what the proposal claims. Your Otable profile is where that evidence lives—and it flows directly into the proposal document.',
      },
      { type: 'h2', text: 'Profiles Are Not Optional Extras' },
      {
        type: 'p',
        text: 'Many researchers treat their profile page as an administrative checkbox: fill in the name, add an institution, move on. This is a significant missed opportunity. In Otable, member profiles are not decorative. They are data sources. The education, experience, publications, and skills you enter are referenced by the proposal system when generating team overviews, competence matrices, and partner descriptions.',
      },
      {
        type: 'p',
        text: 'A proposal section asking for a description of the key personnel draws on the structured fields in each member\'s profile. If those fields are sparse, the generated content is sparse. If they are detailed and current, the output becomes a substantive, evaluator-facing argument for why this specific team has the credentials to succeed.',
      },
      {
        type: 'highlight',
        text: 'Horizon Europe evaluation criteria explicitly score "quality and experience of the individual team members." A well-structured profile is not bureaucracy—it is direct input to the highest-weighted criterion.',
      },
      { type: 'profile_card' },
      { type: 'h2', text: 'Education and Experience: The Track Record Signal' },
      {
        type: 'p',
        text: 'The education section of your profile captures degrees, institutions, and fields of study—the formal credentials that establish your disciplinary base. Experience entries go further: they record roles, organisations, and time periods, painting a picture of career trajectory. Review panels look for seniority signals. A team where the PI has led two previous multi-partner projects and the work package leaders have relevant postdoctoral positions is perceived as lower risk than a team of equally talented but less explicitly described researchers.',
      },
      {
        type: 'p',
        text: 'Otable links these fields to consortium membership records, so when a proposal references a work package leader, the system can automatically pull their relevant experience and attach it to the management section. This is only possible when the experience entries exist.',
      },
      { type: 'h2', text: 'Publications: The Evidence Layer' },
      {
        type: 'p',
        text: 'Publications are the most direct evidence that a researcher has worked in the claimed area. Otable\'s publication list is attached to the proposal system: when AI-assisted section drafts reference "recent work by the team on soil microbiome dynamics," that claim is drawn from actual publication records, not fabricated from scratch.',
      },
      {
        type: 'p',
        text: 'The visual CV (vis CV) feature renders a compact, proposal-ready summary of a member\'s academic profile—publications, projects, affiliations, and expertise in a single shareable view. For proposals that require partner CVs as annexes, the vis CV eliminates manual formatting entirely.',
      },
      { type: 'h2', text: 'Organisation Profiles and Infrastructure' },
      {
        type: 'p',
        text: 'Proposals do not only need strong people—they need strong institutions. Organisation profiles in Otable capture the infrastructure and facilities that a partner institution contributes: laboratories, computing resources, clinical trial capacity, field stations, and specialised equipment. These details matter because proposals are increasingly evaluated on whether the consortium has the physical means to deliver its work plan, not just the intellectual ones.',
      },
      {
        type: 'p',
        text: 'An organisation\'s profile is also the home for its collaboration record: how many consortia it has joined, in what roles, and with what outcomes. This institutional track record is distinct from individual researcher experience, and reviewers look for both.',
      },
      {
        type: 'highlight',
        text: 'Consortium strength comes from the combination of person and institution. A brilliant researcher at an under-resourced lab may be less competitive than the same researcher at an institution whose profile demonstrates the infrastructure to support the work.',
      },
      { type: 'h2', text: 'Verify Your Organisation with an Official Email Address' },
      {
        type: 'p',
        text: 'Organisation verification on Otable requires confirmation via an official institutional email address—the domain registered to the institution, not a personal account. This step is non-negotiable, and it is not bureaucratic friction: it is a trust signal. Proposals that reference verified partner organisations carry an implicit quality marker that unverified listings cannot. Reviewers, partners, and funding officers all use verification status as a proxy for institutional commitment.',
      },
      {
        type: 'p',
        text: 'If your institution is not yet verified, the process takes minutes. Navigate to your organisation\'s page, select the verification option, and enter an email address at your institutional domain. Once confirmed, the verified badge appears on the organisation profile and is visible to any researcher searching for partners.',
      },
      { type: 'verified_badge' },
      { type: 'h2', text: 'Completeness as a Scientific Argument' },
      {
        type: 'p',
        text: 'There is a final, less obvious reason to maintain a complete profile: discoverability. Otable\'s suggestion engine, which surfaces researcher profiles to other consortia looking for partners, weights completeness heavily. A profile with a single research interest listed and no publications is nearly invisible in partner search results. A profile with a detailed expertise tree, a full publication list, and an annotated experience record surfaces reliably for the calls and consortia that match it.',
      },
      { type: 'completeness_bar' },
      {
        type: 'p',
        text: 'In short: every field you fill in is both a contribution to your current proposal and an investment in every future collaboration request that might reach you.',
      },
    ],
  },
  {
    slug: 'collaboration-hub-four-functions',
    title: 'Four Ways to Reach a Collaborator: The CollaborationHub Function Buttons',
    subtitle: 'Suggest, Invite, Meet, Message — a guided tour of the interaction tools built into every researcher card on Otable\'s CollaborationHub.',
    category: 'Feature Deep-Dive',
    date: 'August 2026',
    readTime: '5 min read',
    coverEmoji: '🤝',
    tags: ['Networking', 'Consortium', 'Collaboration'],
    preview: 'action_buttons',
    content: [
      {
        type: 'lead',
        text: 'The CollaborationHub is where Otable\'s researcher directory becomes a live collaboration space. When you find a researcher whose profile matches a gap in your consortium, you have four distinct ways to act on that discovery—each designed for a different stage of the relationship.',
      },
      { type: 'h2', text: 'The Four Actions at a Glance' },
      {
        type: 'p',
        text: 'Every user card in the CollaborationHub displays up to four function buttons: Suggest, Invite, Meet, and Message. These are not redundant options—each one opens a different kind of interaction with a different intended outcome. Understanding which to use when is the difference between a cold introduction and a productive first contact.',
      },
      { type: 'action_buttons' },
      { type: 'h2', text: 'Suggest: Recommend a Researcher to a Consortium', buttonKey: 'suggest' },
      {
        type: 'p',
        text: 'The Suggest button lets you nominate a researcher for an existing consortium—either one you belong to or one that has enabled external suggestions. This is the right tool when you spot a researcher whose expertise fills a gap in a consortium you care about, but you do not have the authority to issue a formal invitation yourself.',
      },
      {
        type: 'p',
        text: 'When you submit a suggestion, the consortium coordinator receives a notification with your reasoning and the researcher\'s profile summary. The coordinator can then review the suggestion and decide whether to extend a formal invitation. Suggest is a lightweight, low-commitment signal: it opens a conversation at the consortium level without creating any obligation for the researcher you are recommending.',
      },
      {
        type: 'highlight',
        text: 'Suggestion is the mechanism that lets the whole Otable network contribute to consortium quality—not just coordinators. A well-timed suggestion from a trusted colleague can change the outcome of a proposal.',
      },
      { type: 'h2', text: 'Invite: Extend a Formal Consortium Membership Offer', buttonKey: 'invite' },
      {
        type: 'p',
        text: 'The Invite button is a formal action. It opens the consortium invitation dialog, where you select which consortium you are inviting the researcher to join, specify the proposed role, and optionally include a personalised message. The researcher receives a structured invitation they can accept or decline.',
      },
      {
        type: 'p',
        text: 'Invite is appropriate when you have already decided you want this researcher in your consortium and have the authority (as coordinator or with coordinator approval) to make that offer. It creates a record in both parties\' activity logs and triggers the consortium\'s membership onboarding workflow. Use it when the conversation has progressed past "would you be interested?" and reached "here is a formal offer."',
      },
      { type: 'h2', text: 'Meet: Schedule a Meeting Directly', buttonKey: 'meet' },
      {
        type: 'p',
        text: 'The Meet button launches the meeting scheduling interface. You select a proposed date and time, add an agenda, and send the request. The other researcher receives a meeting proposal they can confirm or suggest an alternative time for. This is the fastest path from "I want to talk to this person" to an actual calendar event.',
      },
      {
        type: 'p',
        text: 'Meet is most useful early in the relationship, before any formal consortium commitment, when you need a conversation to establish scientific fit. It is also the right tool when you have an asynchronous message exchange that has reached a complexity level that benefits from real-time discussion—a common pattern in consortium formation.',
      },
      { type: 'h2', text: 'Message: Start a Private Conversation', buttonKey: 'message' },
      {
        type: 'p',
        text: 'The Message button opens a direct message thread with the researcher. Unlike email, messages in Otable are persistent, searchable, and tied to the platform context—if the conversation leads to a consortium invitation or meeting, those connections are traceable. Messages are the right first step for a casual introduction or when you want to share context before making a formal proposal.',
      },
      {
        type: 'p',
        text: 'A good first message names how you found them (through the CollaborationHub, based on their work in a specific area), what you are working on, and what you are looking for. Keep it short: the goal of a first message is to earn a reply, not to explain the entire proposal.',
      },
      { type: 'h2', text: 'Using All Four Together' },
      {
        type: 'p',
        text: 'In practice, the four functions map to a natural progression: you Message first to introduce yourself; if there is mutual interest, you Meet to discuss scientific fit; once the fit is confirmed, you Invite them to the consortium; and if you encounter other researchers along the way who might belong in someone else\'s consortium, you Suggest them. Each function is a step in a relationship, not a one-off action.',
      },
    ],
  },
  {
    slug: 'proposal-rejection-ai-review',
    title: 'The Hidden Cost of a Rejected Proposal — and What to Do the Week Before You Submit',
    subtitle: 'A competitive research proposal represents hundreds of person-hours. An AI review that takes minutes can catch the structural weaknesses that cause a good proposal to score poorly.',
    category: 'Opinion',
    date: 'August 2026',
    readTime: '6 min read',
    coverEmoji: '⏱',
    tags: ['AI', 'Proposals', 'Funding'],
    preview: 'rejection_preview',
    content: [
      {
        type: 'lead',
        text: 'Horizon Europe accepts roughly 12 percent of submitted proposals. The remaining 88 percent are rejected — and behind each rejection is a team that spent months of concentrated effort, often across multiple countries, writing something that will never be funded. The scientific quality of those proposals is not always the reason they failed.',
      },
      { type: 'h2', text: 'The Time Behind a Competitive Proposal' },
      {
        type: 'p',
        text: 'A realistic accounting of the time invested in a Horizon Europe proposal is uncomfortable. A principal investigator typically spends 80 to 120 hours on the strategic and scientific narrative. Each co-investigator contributes 40 to 60 hours on their sections and the budget. Postdocs and research staff who prepare the work plan, manage files, and coordinate partners add another 20 to 40 hours each. Administrative support for compliance and formatting adds another 20 hours. For a consortium of four institutions, the total can easily exceed 400 person-hours — the equivalent of ten working weeks of one researcher\'s time.',
      },
      { type: 'rejection_cost' },
      {
        type: 'highlight',
        text: 'At a conservative rate of €300 per researcher day, a typical four-partner consortium invests between €75,000 and €120,000 in proposal preparation time before a single euro of funding is awarded.',
      },
      { type: 'h2', text: 'What Reviewers Actually Penalise' },
      {
        type: 'p',
        text: 'Rejection feedback from Horizon Europe evaluators clusters around a predictable set of failures. The impact pathway is vague — the proposal claims broad societal relevance without specifying the mechanism by which research outputs become real-world changes. The work plan lacks specificity — deliverables are described at a level of abstraction that makes it impossible to assess feasibility. The team\'s expertise is either not matched to the stated tasks or the connection is not made explicit in the text. The budget narrative does not justify the equipment line or the person-month allocation against the actual work. These are not scientific failures. They are presentation and structure failures, and they are correctable — if you catch them before submission.',
      },
      { type: 'h2', text: 'The Problem with Asking Colleagues' },
      {
        type: 'p',
        text: 'The traditional solution is to ask a colleague who has reviewed for the relevant programme to read the draft. This works, but it has real costs. You are asking someone to invest two to four hours of their time, which creates a social obligation you will be expected to reciprocate. Reviewers who are colleagues tend to be generous — they focus on what is good and soften criticism to preserve the relationship. And unless they have the specific call text in front of them as they read, their feedback is against a general standard rather than the criteria your proposal will actually be judged on.',
      },
      { type: 'h2', text: 'What an AI Review Checks' },
      {
        type: 'p',
        text: 'Otable\'s AI review reads your complete proposal text alongside the funding call objectives and evaluation criteria, then produces a structured report. Each section receives a score from 1 to 10 and specific, actionable feedback — not "the impact section is weak" but "the impact section does not address TRL progression from the current TRL 4 to the target TRL 7, which is an explicit evaluation criterion for this call." Across the whole proposal, the review identifies the top improvement suggestions ranked by likely impact on the score.',
      },
      { type: 'ai_review_report' },
      { type: 'h2', text: 'When to Run It' },
      {
        type: 'p',
        text: 'The right moment is not the final 24 hours before submission. By then, there is no time to act on structural feedback. The right moment is when the first complete draft is ready — every section has at least a substantive placeholder, the work plan is mapped out, and the budget is entered. At that point, running a review and finding that the impact section scores 4/10 leaves enough time to rewrite it. Running it at midnight before the deadline does not.',
      },
      {
        type: 'p',
        text: 'Many coordinators run the review twice: once at draft-complete to catch structural problems, and once after the revision to confirm the issues were resolved. Because each review costs a single credit and takes under two minutes, this is a trivial additional investment against the total effort already committed.',
      },
      { type: 'h2', text: 'What It Does Not Replace' },
      {
        type: 'p',
        text: 'Expert scientific peer review remains irreplaceable for assessing whether the research questions are original, whether the methodology is sound, and whether the claims are credible. That is not what the AI review is for. It is for the layer of problems that expert reviewers often miss precisely because they are reading for scientific content — the structural weaknesses, the argumentative gaps, the criterion-matching failures that cause an evaluator to mark down a scientifically strong proposal because the text did not make the case clearly enough. Both types of review are worth doing. Only one of them requires calling in a favour.',
      },
    ],
  },
  {
    slug: 'proposal-writer-strengths',
    title: 'Inside the ProposalWriter: How Otable Manages the Full Proposal Lifecycle',
    subtitle: 'A structured workspace for collaborative writing, work package management, budget planning, and one-click Word export — built around how EU proposals actually work.',
    category: 'Feature Deep-Dive',
    date: 'August 2026',
    readTime: '7 min read',
    coverEmoji: '📝',
    tags: ['Proposals', 'Collaboration', 'AI'],
    preview: 'pw_overview',
    content: [
      {
        type: 'lead',
        text: 'Writing a competitive research proposal requires more than a shared document. It requires a structured workspace that understands the anatomy of a funding call, tracks who is responsible for what, enforces word limits, and keeps the team\'s work visible without getting in the way. Otable\'s ProposalWriter is built around that requirement rather than retrofitted to it.',
      },
      { type: 'h2', text: 'A Tab for Every Stage of the Proposal' },
      {
        type: 'p',
        text: 'The ProposalWriter is organised into six tabs that correspond to the actual structure of a modern research proposal: Abstract, Sections, Work Packages, Budget, Dissemination, and a live Preview. Each tab is a focused workspace — you do not need to scroll through a single enormous document to find what you are working on. The Preview tab renders the full proposal in reading order, combining all tabs into the document a reviewer would see.',
      },
      { type: 'pw_tabs' },
      { type: 'h2', text: 'Section Editing with Locking and Status Tracking' },
      {
        type: 'p',
        text: 'Each proposal section is a standalone rich-text editor with its own word limit, status indicator, and version history. Sections move through a status lifecycle — Draft, In Review, Final — so coordinators can see at a glance which parts of the proposal are still open and which are approved. Section locking prevents simultaneous edits: a team member who locks a section for revision makes that clear to everyone else without requiring a separate conversation.',
      },
      {
        type: 'p',
        text: 'Version history is tracked per section, not just at the document level. If a collaborator overwrites a passage that was better before, you can retrieve and restore any previous version without affecting other sections. This is the level of granularity a team writing under deadline pressure actually needs.',
      },
      { type: 'pw_section_editor' },
      { type: 'h2', text: 'Work Package Architecture' },
      {
        type: 'p',
        text: 'Work packages in Otable are not just labels. Each work package has a start and end month, a description, a list of objectives, tasks with assigned leads and effort, deliverables with due months and dissemination levels, milestones, and risk entries. The ProposalWriter uses this structured data to auto-generate the standard Horizon Europe work package tables — List of Work Packages, List of Deliverables, List of Milestones, Critical Risks — so they never need to be maintained by hand.',
      },
      {
        type: 'highlight',
        text: 'The work package tables in the Preview and Word export are generated directly from the structured data. Renumber a deliverable, and all table references update automatically — there is no separate table to maintain.',
      },
      { type: 'pw_work_packages' },
      { type: 'h2', text: 'Budget and Person-Month Tracking' },
      {
        type: 'p',
        text: 'Budget entries are attached to tasks, not to work packages or the proposal as a whole. This means the budget breakdown reflects the actual distribution of work, not a top-down allocation. Personnel costs are entered as person-months per task per member; the system aggregates these into a Staff Effort Summary table and a budget totals view that match what the application form expects. Equipment and other cost categories are tracked separately.',
      },
      { type: 'pw_budget' },
      { type: 'h2', text: 'Built-In Completeness Warnings' },
      {
        type: 'p',
        text: 'Before exporting, the ProposalWriter runs a completeness check against the proposal structure. It flags missing required content (sections with no text, work packages with no tasks), incomplete optional fields (deliverables without descriptions, work packages without objectives), and informational suggestions (missing month ranges, unset word limits). Warnings are grouped by category and can be dismissed individually as you work through them.',
      },
      { type: 'pw_warnings' },
      { type: 'h2', text: 'One-Click Word Export' },
      {
        type: 'p',
        text: 'The export produces a .docx file that mirrors the Preview tab exactly — including all generated tables, structured heading numbering, and formatted budget breakdowns. Table labels are computed dynamically: if the Work Plan section is numbered 2.1, tables become 2.1a, 2.1b, and so on, so renumbering sections in the proposal settings propagates through the export without manual correction. The output is formatted to be pasted directly into the official submission form or shared with reviewers.',
      },
      { type: 'h2', text: 'AI Writing Integrated, Not Bolted On' },
      {
        type: 'p',
        text: 'The AI writing assistant is available on any section with a single button. Because the assistant draws on the actual consortium data — member profiles, publications, call objectives, word limit, section type — the output is specific rather than generic. Credits are consumed per generation and are visible at all times in the editor toolbar. For teams with heavier usage, credit packs can be purchased directly from the billing panel.',
      },
    ],
  },
  {
    slug: 'ai-powered-proposal-assistant',
    title: 'AI That Knows Your Consortium: Otable\'s Proposal Assistant',
    subtitle: 'How Otable uses the actual composition and expertise of your consortium—not generic templates—to generate proposal content that reflects your team.',
    category: 'Feature Deep-Dive',
    date: 'August 2026',
    readTime: '6 min read',
    coverEmoji: '🤖',
    tags: ['AI', 'Proposals', 'Writing'],
    preview: 'ai_assistant',
    content: [
      {
        type: 'lead',
        text: 'Most AI writing tools know nothing about you. They generate text from a prompt and nothing else. Otable\'s proposal assistant works differently: it knows who is in your consortium, what they have published, what infrastructure they bring, and what funding call you are responding to. That context produces something much closer to a usable first draft than a blank-page generator ever could.',
      },
      { type: 'h2', text: 'The Context Advantage' },
      {
        type: 'p',
        text: 'When you activate the AI assistant for a proposal section, the system automatically assembles a structured context before making any request. This context includes the full list of consortium members with their research interests, skills, and most recent publications; the organisation profiles of all partner institutions including their stated facilities and infrastructure; the funding call title and programme objectives; the section type and any word limit set by the template.',
      },
      {
        type: 'p',
        text: 'The result is that a generated draft for the "Team Qualifications" section actually names your team, references their real outputs, and reflects their genuine disciplinary spread. It is not a generic paragraph about a hypothetical interdisciplinary consortium—it is a specific argument for your specific consortium.',
      },
      {
        type: 'highlight',
        text: 'The quality of an AI-generated proposal section is directly proportional to the quality of the profiles behind it. Complete, current profiles produce drafts that need light editing. Empty profiles produce drafts that need rewriting.',
      },
      { type: 'h2', text: 'Analysing Consortium Strengths and Gaps' },
      {
        type: 'p',
        text: 'Before generating section content, the AI assistant performs an implicit analysis of the consortium\'s collective profile. It identifies which research areas are covered by multiple members (strength) and which required areas—based on the call description—are covered by only one or zero members (gap). This analysis informs the tone and content of generated sections: a strong area is described confidently; a gap may be acknowledged as a deliberate focus of an incoming recruitment or collaboration, rather than left as a silent weakness that reviewers will find independently.',
      },
      {
        type: 'p',
        text: 'For coordinators, this analysis is also useful outside the writing process. Identifying expertise gaps before a proposal deadline gives time to recruit a missing partner or restructure the work plan to avoid overclaiming in an area the consortium cannot substantiate.',
      },
      { type: 'h2', text: 'Section-by-Section Generation' },
      {
        type: 'p',
        text: 'The proposal assistant works at the section level, not the document level. You generate each section independently, which means you can use the AI for the parts where generic text is hardest to avoid—team descriptions, impact statements, management plans—while writing the scientific content yourself, where originality is most critical. The generated sections appear in the shared workspace immediately and are editable by any authorised team member.',
      },
      { type: 'ai_section_editor' },
      {
        type: 'p',
        text: 'Each generation uses a credit. The credit system is transparent: the platform tells you how many credits remain, what a typical section costs, and allows you to purchase more if needed. Credits cover the actual token cost of the underlying language model request, so pricing scales with usage rather than being a flat subscription surcharge.',
      },
      { type: 'ai_credits' },
      { type: 'h2', text: 'Custom Instructions Per Section' },
      {
        type: 'p',
        text: 'Before generating, you can add a custom instruction to the section: "Emphasise the clinical translation pathway," "Use the phrase \'open science by default\' as a framing device," "Avoid mentioning partner X in this section since they are joining late." These instructions are appended to the structured context and shape the output without replacing the underlying consortium data.',
      },
      { type: 'ai_custom_instruction' },
      {
        type: 'p',
        text: 'The prompt preview feature lets you see exactly what context is being sent before you spend a credit. This transparency is deliberate: you should know whether the AI has access to the information it needs before you ask it to write.',
      },
      { type: 'h2', text: 'What the AI Does Not Do' },
      {
        type: 'p',
        text: 'The proposal assistant does not fabricate citations, invent consortium members, or make scientific claims beyond what the profiles support. If a section requires specific quantitative claims—percentage reductions, target sample sizes, projected outputs—those come from your team and your work plan, not the AI. The assistant fills in the connective tissue: the framing, the competence narrative, the impact language. The substance remains yours.',
      },
    ],
  },
  // reviewed
  {
    slug: 'who-is-otable-for',
    title: 'Who Is Otable For? A Platform for Every Stage of a Research Career',
    subtitle: 'From first-year undergraduates building a CV entry to tenured professors coordinating multi-million-euro consortia — here is how each user group gets value from day one.',
    category: 'Guide',
    date: 'August 2026',
    readTime: '7 min read',
    coverEmoji: '🎓',
    tags: ['Getting Started', 'Networking', 'Students', 'Opinion'],
    preview: 'target_groups',
    content: [
      {
        type: 'lead',
        text: 'Research collaboration has a reputation for being the domain of established professors with long grant histories and deep personal networks. Otable was built to challenge that assumption. The platform works as well for a third-year undergraduate looking for their first research credential as it does for a principal investigator managing a seven-partner consortium — but for entirely different reasons.',
      },
      { type: 'h2', text: 'A Platform for Every Stage of a Research Career' },
      {
        type: 'p',
        text: 'The research community is not a single demographic. It includes students exploring their first scientific interest, doctoral researchers seeking visibility, early-career academics trying to build a publication record, established investigators looking for partners, grant administrators coordinating across institutions, and industry R&D teams seeking academic expertise. Otable was designed so that each of these groups finds something immediately useful — not after they have accumulated years of experience.',
      },
      { type: 'user_personas' },
      { type: 'h2', text: 'Undergraduate Students: Your First Real Research Credential' },
      {
        type: 'p',
        text: 'Undergraduate students are underrepresented in European research collaboration, not because they lack ability, but because there has never been a structured way for them to enter the ecosystem. Most collaboration happens through personal introductions that students simply do not have access to yet.',
      },
      {
        type: 'p',
        text: 'On Otable, an undergraduate can create a verified profile linked to their institution, list their research interests, and follow active consortia working in their area. When a consortium opens a call for junior contributors — a common pattern in Horizon Europe doctoral networks (MSCA DN) and the European Research Council\'s starting grants — they appear in the pool of discoverable candidates.',
      },
      {
        type: 'highlight',
        text: 'The most valuable thing an undergraduate can do before finishing their degree is accumulate verified research participation. A single consortium contribution documented on a platform is more legible to future supervisors than a paragraph on a personal statement.',
      },
      {
        type: 'p',
        text: 'Participating as a junior member in a proposal process — even in a support role — exposes undergraduates to a side of research that no coursework covers: how funding calls are structured, how consortium partners divide work packages, how a multi-institutional budget is negotiated. This is knowledge that postdoctoral researchers often encounter for the first time.',
      },
      { type: 'cv_entry' },
      { type: 'h2', text: 'What to Put on Your CV' },
      {
        type: 'p',
        text: 'When documenting consortium participation on a CV or application form, be specific about the role and the call. "Contributed to work package scoping for a Horizon Europe MSCA Doctoral Network application targeting machine learning in climate modelling, coordinated by [University]" is a concrete, verifiable entry that signals professional research exposure. "Helped with a research project" is not.',
      },
      {
        type: 'p',
        text: 'If the proposal was submitted (even if not funded), include the reference number. If it was funded, state this explicitly — it transforms the entry from "attempted" to "delivered." If you were acknowledged in any section of the proposal document, note that. Specificity is the difference between a credential and a vague claim.',
      },
      { type: 'h2', text: 'Master\'s and PhD Researchers: Visibility Before the Thesis' },
      {
        type: 'p',
        text: 'For master\'s and doctoral researchers, Otable serves a different but equally critical function: visibility to the people who will define their next career step. A thesis supervisor sees only what happens inside their own lab. A completed Otable profile — with research interests, methods, affiliated institution, and a record of consortium participation — is visible to every principal investigator searching for talent across all registered institutions.',
      },
      {
        type: 'p',
        text: 'PhD researchers who appear in consortium searches are often those who secure postdoctoral offers from outside their immediate network. The mechanism is simple: a PI assembling a new consortium sees a doctoral researcher whose profile covers a gap in their team\'s expertise, sends a message, and a professional relationship begins. This does not require cold-emailing or conference attendance. It requires a complete, findable profile.',
      },
      {
        type: 'highlight',
        text: 'The academic job market is global, but most PhD graduates search in a radius defined by the conferences they attended and the supervisors who know them personally. A searchable profile collapses that radius to zero.',
      },
      { type: 'h2', text: 'Postdocs and Early-Career Researchers: Building a Track Record' },
      {
        type: 'p',
        text: 'The transition from funded doctoral position to independent career is the most precarious stage in academic research. Fellowship applications — whether MSCA Postdoctoral Fellowships, ERC Starting Grants, or national equivalents — increasingly require evidence of collaborative experience in addition to publication record. A consortium role as a co-investigator or work package contributor is exactly this kind of evidence.',
      },
      {
        type: 'p',
        text: 'Postdocs can also use Otable to initiate their own consortium formation around a research idea before they have a permanent position. A compelling consortium concept, with verified profiles attached and a matched funding call identified, is a fundable object — regardless of whether the coordinator yet holds a chair.',
      },
      { type: 'h2', text: 'Established Researchers and Principal Investigators' },
      {
        type: 'p',
        text: 'For established academics, Otable is primarily an efficiency tool. Finding the right interdisciplinary partners for a specific call used to involve weeks of email exchanges and conference corridor conversations. The researcher directory, filtered by field, method, institution, country, and consortium history, compresses that process. Expert suggestions — generated by comparing a consortium\'s existing profile against a funding call\'s stated requirements — surface names that manual search would miss.',
      },
      {
        type: 'p',
        text: 'Beyond recruitment, the proposal environment replaces the usual patchwork of shared drives, email attachments, and tracking spreadsheets. When a seven-partner consortium writes a 100-page proposal in Otable\'s ProposalWriter, every section has an assigned author, every work package links to its deliverables, and the full document is available to all partners at all times. The coordinator role becomes manageable rather than all-consuming.',
      },
      { type: 'h2', text: 'Research Support Staff and Grant Officers' },
      {
        type: 'p',
        text: 'Research support professionals — grant officers, knowledge transfer staff, European programme specialists — often manage relationships with five to twenty active consortia simultaneously. The platform\'s consortium directory and message system give them a single interface where they can track status, review membership, and communicate with coordinators without maintaining separate records for each partnership.',
      },
      {
        type: 'p',
        text: 'Grant officers with institutional accounts can also add verified organisation information, update facility descriptions, and ensure the institution appears correctly in partner searches — details that directly affect whether the institution is approached as a consortium partner by researchers at other universities.',
      },
      { type: 'h2', text: 'Industry and Applied Research Teams' },
      {
        type: 'p',
        text: 'European research funding increasingly requires industry-academic partnerships. Horizon Europe\'s collaborative projects, EIC Pathfinder grants, and KIC partnerships all mandate mixed consortia. Industry R&D teams that want access to this funding need a fast path to credible academic partners in specific domains — not a general professional network, but a searchable registry of verified researchers with publication records and consortium history.',
      },
      {
        type: 'p',
        text: 'Industry members on Otable create organisation profiles under their company or research unit, list their technology readiness levels, and appear in searches alongside academic institutions. The platform treats industry partners as first-class participants, not as optional annexes to an academic application.',
      },
      { type: 'h2', text: 'The Common Thread' },
      {
        type: 'p',
        text: 'What all of these groups share is a need for structured, verifiable participation in research collaboration — something that was previously available only through the luck of personal network proximity. Otable does not replace the relationships that make research work. It makes those relationships findable before they form, and traceable after they do. Whether you are logging in for the first time with a student email address or coordinating your fifth multi-partner consortium, the value is the same: you can be found by the people who need what you bring.',
      },
    ],
  },
  {
    slug: 'academic-networking-beyond-linkedin',
    title: 'Academic Networking Beyond Professional Social Media Platforms: Why Context Matters',
    subtitle: 'General-purpose professional networks miss what researchers actually need. Here is what a purpose-built alternative offers.',
    category: 'Opinion',
    date: 'July 2026',
    readTime: '5 min read',
    coverEmoji: '🌐',
    tags: ['Networking', 'Visibility', 'Opinion'],
    content: [
      {
        type: 'lead',
        text: 'Professional social media platforms are excellent at what they were designed for: B2B sales, job seeking, and corporate brand building. They are a poor fit for academic collaboration—not because academics do not use them, but because the information that matters in research networking is either absent or buried under professional endorsements and engagement posts.',
      },
      { type: 'h2', text: 'The Context Gap' },
      {
        type: 'p',
        text: 'When a researcher evaluates a potential collaborator, they need context that professional social media platforms cannot provide. Not just "Professor of Biology at University X" but: What specific organisms? What experimental methods? Has this person led a consortium before? What funding bodies have they worked with? Searching LinkedIn for "systems biologist with NGS experience and Horizon Europe track record" returns results sorted by connection degree, not scientific relevance.',
      },
      {
        type: 'highlight',
        text: 'Purpose-built tools are not a replacement for general networks. They are a complement: maintain your presence in other professional social media platforms for public visibility, and use Otable for work that requires scientific precision.',
      },
      { type: 'h2', text: 'What Purpose-Built Looks Like' },
      {
        type: 'p',
        text: 'An Otable researcher profile captures the dimensions that matter for collaboration: research fields (hierarchical, from broad area to specific topic), methods and tools, institutional affiliations with department-level precision, consortium history, and proposal participation. The profile is not a CV—it is a collaboration interface.',
      },
      { type: 'h2', text: 'The Verification Layer' },
      {
        type: 'p',
        text: 'One structural advantage a research-specific platform can offer is affiliation verification. An Otable membership claim is verified against the institution\'s record before it appears as confirmed on the profile. This removes the noise of self-proclaimed expertise and makes the directory genuinely trustworthy as a recruitment tool.',
      },
      { type: 'h2', text: 'Starting Small' },
      {
        type: 'p',
        text: 'You do not need your entire institution to adopt a platform for it to be useful. Otable works best when a single research group or department uses it consistently for one funding cycle. After one successful consortium formation, the value becomes concrete and spreads organically.',
      },
    ],
  },
  {
    slug: 'interdisciplinary-proposal-writing',
    title: 'Beyond the Single Discipline: Building Interdisciplinary Research Consortia',
    subtitle: 'European funders now explicitly reward teams that cross methodological and disciplinary boundaries. Here is how to make interdisciplinary collaboration work — from partner discovery to project execution.',
    category: 'Feature Deep-Dive',
    date: 'August 2026',
    readTime: '8 min read',
    coverEmoji: '🔀',
    tags: ['Proposals', 'Collaboration', 'Funding', 'Opinion'],
    preview: 'interdisciplinary',
    content: [
      {
        type: 'lead',
        text: 'The scientific problems that attract the largest funding pools — climate adaptation, pandemic preparedness, digital health, sustainable agriculture — do not sit inside a single discipline. They require biologists and data scientists, social scientists and engineers, clinical researchers and ethicists, all working on overlapping problems at the same time. European funders have drawn the same conclusion, and the implications for how consortia are built and how proposals are written are significant.',
      },
      { type: 'h2', text: 'Why Funders Reward Interdisciplinarity' },
      {
        type: 'p',
        text: 'Horizon Europe\'s architecture makes the preference structural, not merely rhetorical. The Missions — Climate, Cancer, Oceans, Cities, Soil — are defined around societal outcomes, not academic fields. The EIC Pathfinder funds "high-risk, high-gain" research that almost by definition crosses disciplinary lines. MSCA Doctoral Networks require research training across at least three institutions in different countries, and the scientific content increasingly spans methodological families.',
      },
      {
        type: 'p',
        text: 'Evaluation panels score proposals on "soundness of approach," and within that criterion, reviewers routinely ask: does this team have the combined expertise to actually do this? A proposal claiming to build an AI-assisted clinical decision tool authored only by computer scientists scores lower than the same proposal with a clinical lead, an ethicist, and a health economist co-authoring their respective sections.',
      },
      {
        type: 'highlight',
        text: 'Interdisciplinarity is no longer a bonus point in European research funding — it is an eligibility signal. A proposal that cannot demonstrate genuine cross-disciplinary integration is not competitive, regardless of the quality of the science within each discipline.',
      },
      { type: 'h2', text: 'The Two Failure Modes' },
      {
        type: 'p',
        text: 'Most interdisciplinary proposals fail in one of two ways. The first is superficial interdisciplinarity: a consortium assembled from diverse departments where each partner writes their own work package in isolation, the sections are concatenated, and the word "interdisciplinary" appears in the abstract without being demonstrated anywhere in the body. Experienced reviewers identify this pattern within minutes.',
      },
      {
        type: 'p',
        text: 'The second failure is coordination collapse. The consortium is genuinely interdisciplinary — the researchers really do need each other — but the project management structure has not been designed for cross-disciplinary handoffs. A computational biologist delivers a dataset at month 18 that the clinical team was supposed to validate at month 12. A social scientist produces findings about user behaviour that the engineering team never integrated into the design iteration. The disciplines contributed in parallel rather than in sequence and interaction.',
      },
      { type: 'interdisciplinary_map' },
      { type: 'h2', text: 'Discovery: Finding Partners Across Disciplinary Boundaries' },
      {
        type: 'p',
        text: 'The first structural barrier to genuine interdisciplinary collaboration is discovery. A bioinformatician at a research institute in Copenhagen has no reliable way to know that a social epidemiologist at a university in Porto is working on a methodologically adjacent problem and looking for a computational partner for an upcoming call. The personal networks that most researchers rely on are disciplinarily homophilous — they include people who attended the same conferences, were trained in the same methods, and read the same journals.',
      },
      {
        type: 'p',
        text: 'Otable\'s researcher directory treats discovery as a search problem, not a social one. Filtering by field, method, institution, country, and consortium history surfaces researchers whose profiles match specified criteria regardless of whether there is any existing connection. A filter for "machine learning" combined with "clinical validation experience" and "Horizon Europe track record" returns a list that a personal network built within a single discipline simply cannot produce.',
      },
      {
        type: 'highlight',
        text: 'The most productive interdisciplinary partnerships are rarely between people who already know each other. They are between people who needed each other and had a mechanism to find out.',
      },
      { type: 'h2', text: 'Building a Shared Language' },
      {
        type: 'p',
        text: 'When researchers from different disciplines begin writing a joint proposal, the first substantive problem is vocabulary. A "model" means something different to a mathematician, a climate scientist, a cognitive psychologist, and an economist. A "validation study" has different methodological requirements in each of those fields. "Scalability" means different things to a software engineer and a social scientist studying behaviour change.',
      },
      {
        type: 'p',
        text: 'This is not a trivial problem. Evaluation panels assess whether a proposal\'s methods section demonstrates that the team has genuinely integrated their approaches — not just listed them. A proposal where the computational section uses terminology that contradicts the empirical section signals a team that has not yet had the necessary conversations.',
      },
      {
        type: 'p',
        text: 'Otable\'s ProposalWriter enforces shared language by design. When multiple authors write sections within a single structured document, inconsistencies surface immediately in context rather than being discovered during a final pre-submission read. The section review workflow — where any designated reviewer can annotate any section — creates the cross-disciplinary reading that an emailed Word document exchange never does.',
      },
      { type: 'interdisciplinary_wps' },
      { type: 'h2', text: 'Work Package Design: Where Integration Lives or Dies' },
      {
        type: 'p',
        text: 'Work package structure is where interdisciplinarity either materialises or evaporates. The most common mistake is designing work packages that map cleanly onto disciplines: WP1 for the biologists, WP2 for the computer scientists, WP3 for the social scientists. This structure is administratively tidy and scientifically incoherent. It ensures that disciplines contribute sequentially at best and in parallel at worst, with a single integration work package at the end that cannot compensate for three years of siloed activity.',
      },
      {
        type: 'p',
        text: 'Productive interdisciplinary work package design assigns deliverables and tasks that require multi-disciplinary contribution by construction. A deliverable of "validated computational model with clinical input" requires the computational team and the clinical team to collaborate on the same object. A milestone of "co-produced design specification" requires the engineering and social science teams to have had a substantive conversation before month six. The work plan is not a description of who does what — it is a commitment to specific interactions at specific times.',
      },
      {
        type: 'p',
        text: 'In Otable, work packages, tasks, deliverables, and milestones are structured objects. Assigning a task to contributors from two different institutions and two different disciplines is a single operation that creates a traceable connection. The same task appears in both contributors\' views, linked to its deliverable and its month of completion. When one contributor is delayed, the other sees it. The integration is not implied by the proposal language — it is encoded in the project structure.',
      },
      { type: 'h2', text: 'The Coordinator Problem' },
      {
        type: 'p',
        text: 'Coordinating an interdisciplinary consortium is a different job from coordinating a single-discipline one. The coordinator does not need to be the most eminent scientist in the room — they need to understand enough of each discipline to mediate between them, identify when a terminology clash is masking a substantive disagreement, and make resource decisions that feel fair across methodological communities that value different things.',
      },
      {
        type: 'p',
        text: 'Otable\'s role system separates the scientific lead from the management lead. A Work Package Leader can hold the scientific authority for a cluster of tasks without carrying the administrative overhead of the full consortium. The Coordinator role manages invitations, membership, messaging, and proposal coordination. Distributing these responsibilities across the team reduces the bottleneck that kills many interdisciplinary projects: everything waiting on a single person who is simultaneously the best scientist in the room and the project manager.',
      },
      {
        type: 'highlight',
        text: 'The best coordinators of interdisciplinary consortia are translators, not domain experts. Their value is in making disciplines legible to each other — and to the evaluation panel.',
      },
      { type: 'h2', text: 'From Proposal to Project: Keeping Disciplines Integrated' },
      {
        type: 'p',
        text: 'The integration challenge does not end at submission. Many interdisciplinary projects begin to silo during execution as each partner returns to their familiar patterns and the coordination overhead of cross-disciplinary work rises. Progress reviews that are attended only by the relevant WP leader, without cross-disciplinary participants, fail to catch the divergences that accumulate between milestones.',
      },
      {
        type: 'p',
        text: 'The structured proposal document that a consortium wrote in Otable becomes the baseline against which the project is managed. Objectives, tasks, and deliverables defined in the proposal are the same objects tracked during execution. When a work package falls behind, the dependency graph — which tasks in other disciplines were waiting on that output — is immediately visible. The proposal is not a document that goes in a drawer after submission; it is the operational specification the project runs on.',
      },
      {
        type: 'p',
        text: 'This continuity from proposal to project is where interdisciplinary research most often gains or loses its momentum. A project that starts with a well-integrated proposal written in a shared environment, with role assignments, cross-disciplinary deliverables, and explicit integration milestones, has a structural advantage over one that treats the proposal as a funding document and the project as a separate activity starting from scratch after the award letter arrives.',
      },
    ],
  },
  {
    slug: 'reflections-the-story-behind-the-line',
    title: 'The Story Behind the Line',
    subtitle: 'On professional profiles, informal memory, and the moments that quietly change us.',
    category: 'Feature Deep-Dive',
    date: 'August 2026',
    readTime: '7 min read',
    coverEmoji: '✍️',
    tags: ['Reflections', 'Profiles', 'Research Culture'],
    content: [
      {
        type: 'lead',
        text: '"The most useful thing my supervisor ever told me wasn\'t in any meeting room. It was in the corridor after a grant rejection — and it has shaped every proposal I\'ve written since."',
      },
      {
        type: 'svg_visual',
        caption: 'The career line — and what it cannot carry',
        svg: `<svg viewBox="0 0 620 178" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto;display:block;">
  <line x1="28" y1="148" x2="592" y2="148" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.25"/>
  <circle cx="80"  cy="148" r="4.5" fill="currentColor" opacity="0.3"/>
  <circle cx="185" cy="148" r="4.5" fill="currentColor" opacity="0.3"/>
  <circle cx="300" cy="148" r="4.5" fill="currentColor" opacity="0.3"/>
  <circle cx="415" cy="148" r="4.5" fill="currentColor" opacity="0.3"/>
  <circle cx="520" cy="148" r="4.5" fill="currentColor" opacity="0.3"/>
  <path d="M 185 148 Q 168 102 148 48" stroke="#7c3aed" stroke-width="1.2" fill="none" stroke-dasharray="4 3" opacity="0.7"/>
  <circle cx="148" cy="46" r="3" fill="#7c3aed" opacity="0.6"/>
  <text x="152" y="43" font-family="Georgia, serif" font-style="italic" font-size="11.5" fill="#7c3aed" opacity="0.72">the corridor after the rejection</text>
  <path d="M 300 148 Q 292 96 280 28" stroke="#7c3aed" stroke-width="1.2" fill="none" stroke-dasharray="4 3" opacity="0.52"/>
  <circle cx="280" cy="26" r="3" fill="#7c3aed" opacity="0.42"/>
  <text x="284" y="23" font-family="Georgia, serif" font-style="italic" font-size="11.5" fill="#7c3aed" opacity="0.52">three months on a failed dataset</text>
  <path d="M 415 148 Q 428 96 442 44" stroke="#7c3aed" stroke-width="1.2" fill="none" stroke-dasharray="4 3" opacity="0.36"/>
  <circle cx="442" cy="42" r="3" fill="#7c3aed" opacity="0.3"/>
  <text x="446" y="39" font-family="Georgia, serif" font-style="italic" font-size="11.5" fill="#7c3aed" opacity="0.38">an offhand remark that changed the thesis</text>
  <text x="68"  y="168" font-family="system-ui,sans-serif" font-size="8.5" fill="currentColor" opacity="0.45">PhD</text>
  <text x="162" y="168" font-family="system-ui,sans-serif" font-size="8.5" fill="currentColor" opacity="0.45">Postdoc</text>
  <text x="280" y="168" font-family="system-ui,sans-serif" font-size="8.5" fill="currentColor" opacity="0.45">Fellow</text>
  <text x="392" y="168" font-family="system-ui,sans-serif" font-size="8.5" fill="currentColor" opacity="0.45">Lecturer</text>
  <text x="500" y="168" font-family="system-ui,sans-serif" font-size="8.5" fill="currentColor" opacity="0.45">Professor</text>
</svg>`,
      },
      {
        type: 'h2',
        text: 'There is a version of your professional history that lives on your CV',
      },
      {
        type: 'p',
        text: 'It is precise, navigable, and almost entirely wrong about you. Not factually — the dates are right, the institutions are real. But a CV presents a career as a sequence of outcomes: positions held, papers published, projects completed. It has no room for the afternoon you spent in a foreign university library trying to read a paper in a language you don\'t speak, and understood enough to change the direction of your dissertation.',
      },
      {
        type: 'p',
        text: 'It has no room for the visiting professor whose offhand comment about your methodology planted a seed that didn\'t flower for three years. It has no room for failure — not as an absence of achievement, but as something that happened, that you lived through, and that left you more capable on the other side.',
      },
      {
        type: 'p',
        text: 'Research careers accumulate invisible experience. The ratio of private struggle to public output in a typical academic career is extraordinary. For every paper accepted, there are grant applications that died quietly, experimental designs that didn\'t survive contact with data, conference presentations that felt like failures at the time and revealed something important in retrospect. None of this appears anywhere.',
      },
      { type: 'h2', text: 'What gets left out' },
      {
        type: 'p',
        text: 'Professional profiles are optimized for legibility at a glance. Credentials, affiliations, outputs. The logic is understandable: when you\'re being evaluated by a hiring committee or a funding panel, they need to move fast and they need signal. The CV is a compression algorithm, and it compresses well.',
      },
      {
        type: 'p',
        text: 'What it compresses away is context. And context is where meaning lives.',
      },
      {
        type: 'svg_visual',
        caption: 'The compression algorithm at work',
        svg: `<svg viewBox="0 0 580 118" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto;display:block;">
  <circle cx="68"  cy="38"  r="6"   fill="#7c3aed" opacity="0.18"/>
  <circle cx="95"  cy="66"  r="4"   fill="#7c3aed" opacity="0.26"/>
  <circle cx="50"  cy="60"  r="7.5" fill="#7c3aed" opacity="0.13"/>
  <circle cx="112" cy="42"  r="4.5" fill="#7c3aed" opacity="0.22"/>
  <circle cx="78"  cy="84"  r="5"   fill="#7c3aed" opacity="0.16"/>
  <circle cx="56"  cy="34"  r="3.5" fill="#7c3aed" opacity="0.2"/>
  <circle cx="125" cy="70"  r="5.5" fill="#7c3aed" opacity="0.12"/>
  <circle cx="44"  cy="76"  r="4"   fill="#7c3aed" opacity="0.24"/>
  <circle cx="100" cy="88"  r="3.5" fill="#7c3aed" opacity="0.17"/>
  <circle cx="130" cy="52"  r="4"   fill="#7c3aed" opacity="0.14"/>
  <circle cx="62"  cy="94"  r="3"   fill="#7c3aed" opacity="0.19"/>
  <circle cx="86"  cy="22"  r="4.5" fill="#7c3aed" opacity="0.25"/>
  <text x="88" y="113" text-anchor="middle" font-family="system-ui,sans-serif" font-size="8.5" letter-spacing="1.2" fill="currentColor" opacity="0.4">EXPERIENCE</text>
  <line x1="172" y1="58" x2="280" y2="58" stroke="currentColor" stroke-width="1" stroke-opacity="0.22"/>
  <polygon points="280,53 294,58 280,63" fill="currentColor" opacity="0.22"/>
  <text x="232" y="50" text-anchor="middle" font-family="system-ui,sans-serif" font-size="8" letter-spacing="0.8" fill="currentColor" opacity="0.38">compress</text>
  <rect x="316" y="44" width="160" height="28" rx="2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-opacity="0.28"/>
  <rect x="330" y="53" width="105" height="9" rx="2" fill="currentColor" opacity="0.12"/>
  <rect x="330" y="66" width="70"  height="7" rx="2" fill="currentColor" opacity="0.08"/>
  <text x="395" y="113" text-anchor="middle" font-family="system-ui,sans-serif" font-size="8.5" letter-spacing="1.2" fill="currentColor" opacity="0.4">CV ENTRY</text>
</svg>`,
      },
      {
        type: 'p',
        text: 'The colleague who spent two hours explaining a statistical method you didn\'t understand — who did it without making you feel slow, and who you\'ve tried to emulate in every teaching interaction since — doesn\'t appear in your publication record. The month you spent on a dataset that ultimately produced nothing usable, but during which you developed a physical intuition for what good data looks like, left no trace.',
      },
      {
        type: 'highlight',
        text: '"The CV compresses well. What it compresses away is context — and context is where meaning lives."',
      },
      { type: 'h2', text: 'The mechanism of resonance' },
      {
        type: 'p',
        text: 'Something happens when someone with experience describes, in their own words, what a particular phase of their career actually felt like. Not what it produced. What it felt like.',
      },
      {
        type: 'p',
        text: 'It creates recognition. Other researchers — at any stage — read it and think: I am not the only person for whom this was hard. I am not the only one who didn\'t know what they were doing and did it anyway. This is not sentiment. It is information. It recalibrates what is normal, what is survivable, what is worth attempting.',
      },
      {
        type: 'p',
        text: 'The specificity matters enormously. A general account of how grant rejection is discouraging tells you almost nothing. A specific account — naming the project, the moment, the decision that came out of it — gives you something you can carry. Vague wisdom passes through us. Specific stories stay.',
      },
      { type: 'h2', text: 'Reflections on Otable' },
      {
        type: 'p',
        text: 'Otable includes a feature called Reflections. They attach to specific items on your profile — a position you held, a project you contributed to, a paper you published — and are written in your own voice. Text, audio, or video. Other researchers can comment and respond.',
      },
      {
        type: 'svg_visual',
        caption: 'A reflection, anchored to the experience it describes',
        svg: `<svg viewBox="0 0 560 198" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:auto;display:block;">
  <rect x="16" y="14" width="528" height="54" rx="3" fill="none" stroke="currentColor" stroke-width="1.2" stroke-opacity="0.2"/>
  <circle cx="40" cy="41" r="8" fill="#7c3aed" opacity="0.45"/>
  <rect x="58"  y="33" width="200" height="9"  rx="2" fill="currentColor" opacity="0.15"/>
  <rect x="58"  y="47" width="100" height="7"  rx="2" fill="currentColor" opacity="0.09"/>
  <rect x="380" y="36" width="140" height="8"  rx="2" fill="currentColor" opacity="0.08"/>
  <line x1="40" y1="68" x2="40" y2="88" stroke="#7c3aed" stroke-width="1.5" stroke-opacity="0.4" stroke-dasharray="3 2"/>
  <rect x="18" y="86" width="82" height="18" rx="9" fill="#7c3aed" opacity="0.14"/>
  <text x="59" y="99" text-anchor="middle" font-family="system-ui,sans-serif" font-size="9" font-weight="700" letter-spacing="1" fill="#7c3aed">REFLECTION</text>
  <rect x="16" y="110" width="528" height="74" rx="3" fill="none" stroke="#7c3aed" stroke-width="1.3" stroke-opacity="0.32"/>
  <circle cx="42" cy="133" r="11" fill="#7c3aed" opacity="0.18"/>
  <rect x="62" y="124" width="440" height="8"  rx="2" fill="currentColor" opacity="0.11"/>
  <rect x="62" y="137" width="400" height="8"  rx="2" fill="currentColor" opacity="0.08"/>
  <rect x="62" y="150" width="290" height="8"  rx="2" fill="currentColor" opacity="0.06"/>
  <circle cx="66"  cy="172" r="4.5" fill="currentColor" opacity="0.12"/>
  <rect x="76"  y="169" width="32" height="6"  rx="2" fill="currentColor" opacity="0.08"/>
  <circle cx="124" cy="172" r="4.5" fill="currentColor" opacity="0.12"/>
  <rect x="134" y="169" width="40" height="6"  rx="2" fill="currentColor" opacity="0.08"/>
</svg>`,
      },
      {
        type: 'p',
        text: 'A researcher who ran a consortium project for three years, listing it as a single entry, might write a reflection about the moment the proposal nearly collapsed — what they learned about negotiating between institutions that saved it. A postdoc might record an audio reflection on having a paper rejected twice before acceptance at a better venue: not a cautionary tale, but a genuine account of how the pressure of revision changed the work itself.',
      },
      {
        type: 'p',
        text: 'These reflections stay attached to the item they describe. They accumulate. Over time, they produce a textured account of a career that a CV alone could never convey. And because they live on a profile within a research network — visible to collaborators, potential partners, students — they reach people at the moment those people are trying to understand what a certain kind of work actually involves.',
      },
      { type: 'h2', text: 'What informal experience is worth' },
      {
        type: 'p',
        text: 'There is a widespread assumption in professional life that informal knowledge is somehow less rigorous than what appears in formal outputs. This assumption is false, and most experienced researchers know it. The knowledge that moves a field forward often circulates outside formal channels: what approaches are dead ends, which collaborations are worth the cost, what it takes to bring a project across the line.',
      },
      {
        type: 'p',
        text: 'Reflections is a bet that this knowledge is worth capturing — not in the stripped-down language of a CV entry, but in the fuller language of someone who was there.',
      },
      {
        type: 'p',
        text: 'Vulnerability and specificity are the engine of professional connection. The most useful thing your supervisor ever said to you probably wasn\'t planned. But it can still reach the next generation — if you write it down.',
      },
    ],
  },
];

const CATEGORY_COLORS = {
  'Opinion':           { bg: 'rgba(118,75,162,0.12)', text: '#764ba2' },
  'Guide':             { bg: 'rgba(102,126,234,0.12)', text: '#5b6fd4' },
  'Feature Deep-Dive': { bg: 'rgba(124,58,237,0.10)', text: '#7c3aed' },
};

// ─── Derived data ────────────────────────────────────────────────────────────

function useBlogData() {
  return useMemo(() => {
    const categoryCounts = {};
    const archiveCounts  = {};
    const tagCounts      = {};
    ARTICLES.forEach(a => {
      categoryCounts[a.category] = (categoryCounts[a.category] || 0) + 1;
      archiveCounts[a.date]      = (archiveCounts[a.date]      || 0) + 1;
      (a.tags || []).forEach(t => { tagCounts[t] = (tagCounts[t] || 0) + 1; });
    });
    // Sort tags by count desc, then alpha
    const sortedTags = Object.entries(tagCounts)
      .sort(([a, ca], [b, cb]) => cb - ca || a.localeCompare(b))
      .map(([tag, count]) => ({ tag, count }));
    return { categoryCounts, archiveCounts, tagCounts, sortedTags };
  }, []);
}

// ─── Reading progress bar ────────────────────────────────────────────────────

function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const fn = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setPct(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, height: 3, zIndex: 9999,
      width: `${pct}%`, background: BRAND_GRADIENT,
      transition: 'width 0.1s linear', pointerEvents: 'none',
    }} />
  );
}

// ─── Shared subcomponents ────────────────────────────────────────────────────

function CategoryChip({ category, active, onClick }) {
  const colors = CATEGORY_COLORS[category] || { bg: 'rgba(118,75,162,0.12)', text: '#764ba2' };
  return (



    <Chip
      label={category}
      size="small"
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: active ? colors.text : colors.bg,
        color: active ? '#fff' : colors.text,
        fontWeight: 600, fontSize: '0.93rem', letterSpacing: 0.4,
        border: `1px solid ${colors.text}40`,
        transition: 'all 0.15s',
        '&:hover': onClick ? { bgcolor: colors.text, color: '#fff' } : {},
      }}
    />
  );
}

function SidebarSection({ icon, title, children }) {
  return (
    <Box sx={{
      mb: 2.5, borderRadius: 2,
      border: '1px solid',
      borderColor: `${ACCENT}20`,
      overflow: 'hidden',
    }}>
      {/* Section header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1.25,
        background: `linear-gradient(90deg, ${ACCENT}18 0%, ${ACCENT}08 100%)`,
        borderBottom: '1px solid', borderColor: `${ACCENT}20`,
      }}>
        {React.cloneElement(icon, { sx: { fontSize: '1.05rem', color: ACCENT } })}
        <Typography sx={{
          fontSize: '0.82rem', fontWeight: 700,
          letterSpacing: 1.1, textTransform: 'uppercase',
          color: ACCENT,
        }}>
          {title}
        </Typography>
      </Box>
      {/* Section body */}
      <Box sx={{ px: 1.5, py: 1.5, bgcolor: `${ACCENT}05` }}>
        {children}
      </Box>
    </Box>
  );
}

function BlogSidebar({ activeCategory, onCategory, activeDate, onDate, activeTag, onTag, onArticle, onLogin }) {
  const { categoryCounts, archiveCounts, sortedTags } = useBlogData();
  const recent = ARTICLES.slice(0, 4); 

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, ml:5 }}>

      {/* Login CTA */}
      {/* <Box sx={{
        p: 2.5, borderRadius: 2, mb: 3,
        background: BRAND_GRADIENT,
        textAlign: 'center',
      }}>
        <Box component="img" src={logo} alt="Otable"
          sx={{ height: 24, width: 'auto', mb: 1.5, display: 'block', mx: 'auto' }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', mb: 2, lineHeight: 1.5 }}>
          Join thousands of researchers already collaborating on Otable.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          startIcon={<LoginIcon sx={{ fontSize: '1rem !important' }} />}
          onClick={onLogin}
          sx={{
            bgcolor: '#fff', color: ACCENT, fontWeight: 700,
            fontSize: '0.97rem', textTransform: 'none', borderRadius: 1.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: ACCENT_LIGHT, boxShadow: 'none' },
          }}
        >
          Sign In to Otable
        </Button>
      </Box> */}

      {/* Categories / Tags */}
      <SidebarSection icon={<LocalOfferOutlinedIcon />} title="Categories">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <ListItemButton
            onClick={() => onCategory(null)}
            selected={!activeCategory}
            sx={{
              borderRadius: 1.5, py: 0.6, px: 1.5, mb: 0.25,
              '&.Mui-selected': { bgcolor: ACCENT_LIGHT, color: ACCENT },
              '&.Mui-selected:hover': { bgcolor: ACCENT_LIGHT },
            }}
          >
            <ListItemText
              primary="All articles"
              primaryTypographyProps={{ fontSize: '1.25rem', fontWeight: activeCategory ? 400 : 600 }}
            />
            <Badge badgeContent={ARTICLES.length} sx={{
              '& .MuiBadge-badge': {
                bgcolor: activeCategory ? 'action.selected' : ACCENT,
                color: activeCategory ? 'text.secondary' : '#fff',
                fontSize: '0.85rem', minWidth: 18, height: 18,
              },
            }} />
          </ListItemButton>
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <ListItemButton
              key={cat}
              onClick={() => onCategory(cat === activeCategory ? null : cat)}
              selected={cat === activeCategory}
              sx={{
                borderRadius: 1.5, py: 0.6, px: 1.5, mb: 0.25,
                '&.Mui-selected': { bgcolor: ACCENT_LIGHT, color: ACCENT },
                '&.Mui-selected:hover': { bgcolor: ACCENT_LIGHT },
              }}
            >
              <ListItemText
                primary={cat}
                primaryTypographyProps={{
                  fontSize: '1.15rem',
                  fontWeight: cat === activeCategory ? 600 : 400,
                  color: cat === activeCategory ? ACCENT : 'text.primary',
                }}
              />
              <Badge badgeContent={count} sx={{
                '& .MuiBadge-badge': {
                  bgcolor: cat === activeCategory ? ACCENT : 'action.selected',
                  color: cat === activeCategory ? '#fff' : 'text.secondary',
                  fontSize: '0.85rem', minWidth: 18, height: 18,
                },
              }} />
            </ListItemButton>
          ))}
        </Box>
      </SidebarSection>

      {/* Tags */}
      <SidebarSection icon={<LocalOfferOutlinedIcon />} title="Tags">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 0.5, pb: 0.5 }}>
          {sortedTags.map(({ tag, count }) => {
            const isActive = tag === activeTag;
            return (
              <Box
                key={tag}
                onClick={() => onTag(isActive ? null : tag)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1.1, py: 0.4, borderRadius: 5, cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isActive ? ACCENT : 'divider',
                  bgcolor: isActive ? ACCENT : 'transparent',
                  transition: 'all .15s',
                  '&:hover': { borderColor: ACCENT, bgcolor: isActive ? ACCENT : `${ACCENT}10` },
                }}
              >
                <Typography sx={{ fontSize: '1.1rem', fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'text.primary', lineHeight: 1 }}>
                  {tag}
                </Typography>
                <Box sx={{ minWidth: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isActive ? 'rgba(255,255,255,0.25)' : 'action.selected' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: isActive ? '#fff' : 'text.secondary', lineHeight: 1 }}>{count}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </SidebarSection>

      {/* Archive */}
      <SidebarSection icon={<HistoryIcon />} title="Archive">
        <List dense disablePadding>
          {Object.entries(archiveCounts).map(([month, count]) => (
            <ListItemButton
              key={month}
              onClick={() => onDate(month === activeDate ? null : month)}
              selected={month === activeDate}
              sx={{
                borderRadius: 1.5, px: 1.5, py: 0.6,
                '&.Mui-selected': { bgcolor: ACCENT_LIGHT, color: ACCENT },
                '&.Mui-selected:hover': { bgcolor: ACCENT_LIGHT },
              }}
            >
              <ListItemText
                primary={month}
                primaryTypographyProps={{
                  fontSize: '1.15rem',
                  fontWeight: month === activeDate ? 600 : 400,
                  color: month === activeDate ? ACCENT : 'text.primary',
                }}
              />
              <Typography sx={{
                fontSize: '1.05rem', color: month === activeDate ? ACCENT : 'text.disabled',
                fontWeight: month === activeDate ? 600 : 400,
              }}>
                {count} {count === 1 ? 'article' : 'articles'}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      </SidebarSection>


      {/* Recent Articles */}
      <SidebarSection icon={<ArticleOutlinedIcon />} title="Recent">
        <List dense disablePadding>
          {recent.map(a => (
            <ListItemButton
              key={a.slug}
              onClick={() => onArticle(a)}
              sx={{ borderRadius: 1.5, px: 1, py: 0.75, alignItems: 'flex-start', gap: 1 }}
            >
              <Box sx={{ fontSize: '1.2rem', lineHeight: 1, pt: 0.2, flexShrink: 0 }}>
                {a.coverEmoji}
              </Box>
              <Box>
                <Typography sx={{
                  fontSize: '1.15rem', lineHeight: 1.4, fontWeight: 500,
                  color: 'text.primary',
                  display: '-webkit-box', WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {a.title}
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', color: 'text.disabled', mt: 0.25 }}>
                  {a.date} · {a.readTime}
                </Typography>
              </Box>
            </ListItemButton>
          ))}
        </List>
      </SidebarSection>




      {/* Platform News */}
      <SidebarSection icon={<CampaignOutlinedIcon />} title="Platform News">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {PLATFORM_NEWS.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: ACCENT, mt: 0.65, flexShrink: 0,
              }} />
              <Box>
                <Typography sx={{ fontSize: '1.15rem', lineHeight: 1.45, color: 'text.primary' }}>
                  {item.text}
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', color: 'text.disabled', mt: 0.25 }}>
                  {item.date}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </SidebarSection>

    </Box>
  );
}

// ─── Interaction button definitions ──────────────────────────────────────────

const ACTION_BUTTONS_META = [
  {
    key: 'suggest',
    label: 'Suggest',
    desc: 'Nominate a researcher for a consortium you care about',
    color: '#ad89ad',
    hoverColor: '#896b89',
    icon: <SendIcon />,
  },
  {
    key: 'invite',
    label: 'Invite',
    desc: 'Extend a formal consortium membership offer',
    color: '#667eea',
    hoverColor: '#4f63d2',
    icon: <GroupAddIcon />,
  },
  {
    key: 'meet',
    label: 'Meet',
    desc: 'Schedule a meeting to discuss scientific fit',
    color: '#2196f3',
    hoverColor: '#1976d2',
    icon: <EventIcon />,
  },
  {
    key: 'message',
    label: 'Message',
    desc: 'Open a private, persistent conversation thread',
    color: '#9e9e9e',
    hoverColor: '#757575',
    icon: <EmailIcon />,
  },
];

// ─── Article content renderer ────────────────────────────────────────────────

function ArticleContent({ blocks }) {
  return (
    <Box>
      {blocks.map((block, i) => {
        if (block.type === 'lead') return (
          <Typography key={i} sx={{
            fontSize: '1.55rem', lineHeight: 1.8, color: 'text.primary',
            mb: 4, fontFamily: 'Georgia, serif',
            borderLeft: `3px solid ${ACCENT}`, pl: 2.5, py: 0.5,
          }}>{block.text}</Typography>
        );

        if (block.type === 'h2') {
          const meta = block.buttonKey
            ? ACTION_BUTTONS_META.find(b => b.key === block.buttonKey)
            : null;
          return (
            <Box key={i} sx={{ mt: 5, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              {meta && (
                <Box sx={{
                  width: 42, height: 42, borderRadius: '50%', bgcolor: meta.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, color: '#fff',
                }}>
                  {React.cloneElement(meta.icon, { sx: { fontSize: '1.25rem' } })}
                </Box>
              )}
              <Typography variant="h5" sx={{
                fontFamily: 'Georgia, serif', fontWeight: 700,
                color: 'text.primary', fontSize: '1.6rem',
              }}>{block.text}</Typography>
            </Box>
          );
        }

        if (block.type === 'highlight') return (
          <Box key={i} sx={{
            my: 4, p: 3, borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(102,126,234,0.07) 0%, rgba(118,75,162,0.07) 100%)',
            borderLeft: `4px solid ${ACCENT}`,
          }}>
            <Typography sx={{
              fontFamily: 'Georgia, serif', fontSize: '1.55rem',
              fontStyle: 'italic', lineHeight: 1.75, color: 'text.primary',
            }}>{block.text}</Typography>
          </Box>
        );

        if (block.type === 'profile_card') return (
          <Box key={i} sx={{
            my: 4, borderRadius: 2.5,
            border: '1px solid', borderColor: `${ACCENT}22`,
            overflow: 'hidden',
          }}>
            {/* Header label */}
            <Box sx={{
              px: 2.5, py: 1,
              background: `linear-gradient(90deg, ${ACCENT}14 0%, ${ACCENT}06 100%)`,
              borderBottom: '1px solid', borderColor: `${ACCENT}18`,
            }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT }}>
                Researcher Profile — Example
              </Typography>
            </Box>

            {/* Profile header */}
            <Box sx={{
              px: 2.5, pt: 2.5, pb: 2,
              background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.05) 100%)',
              display: 'flex', alignItems: 'flex-start', gap: 2,
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {/* Avatar */}
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '1.35rem',
                boxShadow: '0 4px 16px rgba(102,126,234,0.35)',
              }}>
                MK
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Name + verified */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.4 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'text.primary' }}>
                    Dr. Maria Chen
                  </Typography>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.4,
                    bgcolor: 'rgba(102,126,234,0.12)', color: '#667eea',
                    borderRadius: 5, px: 0.9, py: 0.2,
                  }}>
                    <VerifiedIcon sx={{ fontSize: '0.85rem' }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.3 }}>Verified</Typography>
                  </Box>
                </Box>

                {/* Institution */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <BusinessIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '1.05rem', color: 'text.secondary' }}>
                    University of Amsterdam · Computational Biology
                  </Typography>
                </Box>

                {/* Research interest chips */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {['Machine Learning', 'Genomics', 'Climate Adaptation', 'Bioinformatics'].map(tag => (
                    <Box key={tag} sx={{
                      bgcolor: `${ACCENT}12`, color: ACCENT, borderRadius: 5,
                      px: 1.1, py: 0.25, fontSize: '0.78rem', fontWeight: 600,
                      border: `1px solid ${ACCENT}25`,
                    }}>{tag}</Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Stats row */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {[
                { icon: <MenuBookIcon />, value: '24', label: 'Publications' },
                { icon: <WorkIcon />,     value: '6',  label: 'Projects' },
                { icon: <GroupAddIcon />, value: '4',  label: 'Consortia' },
              ].map(({ icon, value, label }, idx) => (
                <Box key={label} sx={{
                  textAlign: 'center', py: 1.5,
                  borderRight: idx < 2 ? '1px solid' : 'none', borderColor: 'divider',
                }}>
                  {React.cloneElement(icon, { sx: { fontSize: '1.1rem', color: ACCENT, mb: 0.25, display: 'block', mx: 'auto' } })}
                  <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'text.primary', lineHeight: 1 }}>{value}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', mt: 0.25 }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Section completion rows */}
            <Box sx={{ px: 2.5, py: 1.75 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase', color: 'text.disabled', mb: 1.25 }}>
                Profile Sections
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                {[
                  { icon: <SchoolIcon />,    label: 'Education',           detail: '3 entries',  done: true  },
                  { icon: <WorkIcon />,      label: 'Experience',          detail: '5 roles',    done: true  },
                  { icon: <MenuBookIcon />,  label: 'Publications',        detail: '24 entries', done: true  },
                  { icon: <PsychologyIcon />,label: 'Skills & Methods',    detail: '8 skills',   done: true  },
                  { icon: <BusinessIcon />,  label: 'Affiliations',        detail: 'Verified',   done: true  },
                  { icon: <VerifiedIcon />,  label: 'Keywords / Topics',   detail: 'Incomplete', done: false },
                ].map(({ icon, label, detail, done }) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {done
                      ? <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50', flexShrink: 0 }} />
                      : <RadioButtonUncheckedIcon sx={{ fontSize: '1rem', color: 'text.disabled', flexShrink: 0 }} />
                    }
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: done ? 600 : 400, color: done ? 'text.primary' : 'text.disabled' }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: done ? 'text.secondary' : 'text.disabled' }}>{detail}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

        if (block.type === 'verified_badge') return (
          <Box key={i} sx={{
            my: 4, borderRadius: 2.5,
            border: '1px solid', borderColor: `${ACCENT}22`,
            overflow: 'hidden',
          }}>
            <Box sx={{
              px: 2.5, py: 1,
              background: `linear-gradient(90deg, ${ACCENT}14 0%, ${ACCENT}06 100%)`,
              borderBottom: '1px solid', borderColor: `${ACCENT}18`,
            }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT }}>
                Organisation Verification — Before & After
              </Typography>
            </Box>

            <Box sx={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              background: 'linear-gradient(135deg, rgba(102,126,234,0.03) 0%, rgba(118,75,162,0.03) 100%)',
            }}>
              {/* Unverified */}
              <Box sx={{ p: 2.5, borderRight: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.disabled', mb: 1.5 }}>
                  Unverified
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                    bgcolor: 'action.disabledBackground',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BusinessIcon sx={{ color: 'text.disabled', fontSize: '1.4rem' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', color: 'text.secondary' }}>University of Amsterdam</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: 'text.disabled' }}>Amsterdam, Netherlands</Typography>
                  </Box>
                </Box>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  border: '1px dashed', borderColor: 'text.disabled',
                  borderRadius: 1, px: 1, py: 0.4,
                }}>
                  <RadioButtonUncheckedIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>Not verified</Typography>
                </Box>
              </Box>

              {/* Verified */}
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#4caf50', mb: 1.5 }}>
                  Verified ✓
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BusinessIcon sx={{ color: '#fff', fontSize: '1.4rem' }} />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'text.primary' }}>University of Amsterdam</Typography>
                      <VerifiedIcon sx={{ fontSize: '1.1rem', color: '#667eea' }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>Amsterdam, Netherlands</Typography>
                  </Box>
                </Box>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  bgcolor: 'rgba(102,126,234,0.12)',
                  borderRadius: 1, px: 1, py: 0.4,
                  border: '1px solid rgba(102,126,234,0.25)',
                }}>
                  <VerifiedIcon sx={{ fontSize: '0.85rem', color: '#667eea' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: '#667eea', fontWeight: 600 }}>Institution verified</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{
              px: 2.5, py: 1.25,
              borderTop: '1px solid', borderColor: 'divider',
              bgcolor: 'rgba(76,175,80,0.05)',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <CheckCircleIcon sx={{ fontSize: '1rem', color: '#4caf50', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '1.05rem', color: 'text.secondary', lineHeight: 1.4 }}>
                Verification is confirmed against the institution's registered domain. The badge appears on your organisation page and in partner search results.
              </Typography>
            </Box>
          </Box>
        );

        if (block.type === 'completeness_bar') {
          const sections = [
            { label: 'Profile photo',       pct: 100, done: true  },
            { label: 'Bio & position',       pct: 100, done: true  },
            { label: 'Education',            pct: 100, done: true  },
            { label: 'Experience',           pct: 100, done: true  },
            { label: 'Publications',         pct: 100, done: true  },
            { label: 'Skills & methods',     pct: 100, done: true  },
            { label: 'Research interests',   pct: 100, done: true  },
            { label: 'Keywords / topics',    pct:  20, done: false },
          ];
          const overall = Math.round(sections.reduce((s, r) => s + r.pct, 0) / sections.length);
          return (
            <Box key={i} sx={{
              my: 4, borderRadius: 2.5,
              border: '1px solid', borderColor: `${ACCENT}22`,
              overflow: 'hidden',
            }}>
              <Box sx={{
                px: 2.5, py: 1,
                background: `linear-gradient(90deg, ${ACCENT}14 0%, ${ACCENT}06 100%)`,
                borderBottom: '1px solid', borderColor: `${ACCENT}18`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT }}>
                  Profile Completeness
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#4caf50' }}>{overall}%</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#4caf50', fontWeight: 600 }}>High discoverability</Typography>
                </Box>
              </Box>

              {/* Overall bar */}
              <Box sx={{ px: 2.5, pt: 2, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                  <Box sx={{
                    height: '100%', width: `${overall}%`, borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #4caf50 100%)',
                    transition: 'width 0.6s ease',
                  }} />
                </Box>
              </Box>

              {/* Section breakdown */}
              <Box sx={{ px: 2.5, py: 1.75 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {sections.map(({ label, pct, done }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {done
                        ? <CheckCircleIcon sx={{ fontSize: '0.95rem', color: '#4caf50', flexShrink: 0 }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled', flexShrink: 0 }} />
                      }
                      <Typography sx={{ fontSize: '1.05rem', color: done ? 'text.primary' : 'text.disabled', width: 160, flexShrink: 0 }}>
                        {label}
                      </Typography>
                      <Box sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', width: `${pct}%`, borderRadius: 3,
                          bgcolor: done ? '#4caf50' : 'text.disabled',
                        }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.85rem', color: done ? '#4caf50' : 'text.disabled', width: 28, textAlign: 'right', fontWeight: 600 }}>
                        {pct}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          );
        }

        // ── AI section editor ──────────────────────────────────────────────
        // ── ProposalWriter: tab navigation ────────────────────────────────
        // ── Rejection cost breakdown ───────────────────────────────────────
        if (block.type === 'rejection_cost') {
          const roles = [
            { label: 'Principal Investigator',    count: 1, hoursEach: 110, color: '#5b6fd4' },
            { label: 'Co-Investigator',            count: 3, hoursEach:  55, color: '#764ba2' },
            { label: 'Research Staff / Postdoc',   count: 4, hoursEach:  30, color: '#2196f3' },
            { label: 'Administrative Support',     count: 2, hoursEach:  20, color: '#9e9e9e' },
          ];
          const totalHours = roles.reduce((s, r) => s + r.count * r.hoursEach, 0);
          const costEst = Math.round(totalHours * 37.5 / 100) * 100; // ~€37.5/h
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: 'rgba(244,67,54,0.2)', overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(244,67,54,0.06)', borderBottom: '1px solid rgba(244,67,54,0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: '1rem', color: '#f44336' }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#f44336' }}>Proposal Preparation — Time Breakdown</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#f44336', lineHeight: 1 }}>{totalHours}h</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>total person-hours</Typography>
                </Box>
              </Box>
              {/* Roles */}
              <Box sx={{ p: 2, background: 'linear-gradient(135deg, rgba(244,67,54,0.03) 0%, rgba(244,67,54,0.01) 100%)' }}>
                {roles.map((r, ri) => {
                  const hours = r.count * r.hoursEach;
                  const pct   = Math.round(hours / totalHours * 100);
                  return (
                    <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: ri < roles.length - 1 ? 1.25 : 0 }}>
                      {/* Avatar dots for count */}
                      <Box sx={{ display: 'flex', gap: 0.3, flexShrink: 0, width: 60, justifyContent: 'flex-end' }}>
                        {Array.from({ length: r.count }).map((_, di) => (
                          <Box key={di} sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: r.color, opacity: 0.85 }} />
                        ))}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: 'text.primary', fontWeight: 500 }}>{r.label}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: r.color }}>{hours}h</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1, height: 7, borderRadius: 2, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                            <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: r.color, borderRadius: 2 }} />
                          </Box>
                          <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', width: 30, textAlign: 'right', flexShrink: 0 }}>{pct}%</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.25 }}>{r.count} person{r.count > 1 ? 's' : ''} × {r.hoursEach}h each</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              {/* Sunk cost footer */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, borderTop: '1px solid rgba(244,67,54,0.12)', bgcolor: 'rgba(244,67,54,0.05)' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.5 }}>
                    At a conservative rate of <strong>€300 per researcher day</strong>, this represents approximately <strong style={{ color: '#f44336' }}>€{(costEst).toLocaleString()}</strong> of researcher time — before any funding is awarded.
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', flexShrink: 0, px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.2)' }}>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: '#f44336', lineHeight: 1 }}>12%</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1.3 }}>Horizon<br />acceptance rate</Typography>
                </Box>
              </Box>
            </Box>
          );
        }

        // ── AI review report ───────────────────────────────────────────────
        if (block.type === 'ai_review_report') {
          const AI = '#7c3aed';
          const overallScore = 6.2;
          const scoreColor = overallScore >= 7 ? '#4caf50' : overallScore >= 5 ? '#ff9800' : '#f44336';
          const sections = [
            { title: 'Team Qualifications',    score: 8, feedback: 'Consortium expertise is well-matched to WP leads. Consider adding one sentence explicitly linking Dr. Osei\'s field-trial record to the cross-regional criterion in the call text.', suggestions: ['Cite field-trial publications directly in this section'] },
            { title: 'Scientific Excellence',  score: 7, feedback: 'Research questions are novel and the methodology is sound. The connection between the soil microbiome analysis and the climate modelling work package is not made explicit.', suggestions: ['Add a 2-sentence bridge explaining how WP1 outputs feed into WP2 models'] },
            { title: 'Impact Pathway',         score: 4, feedback: 'This section does not address TRL progression from the current TRL 4 to the target TRL 7, which is an explicit evaluation criterion. The pathway from research output to policy uptake is asserted rather than demonstrated.', suggestions: ['Add a TRL roadmap table', 'Name at least two specific policy instruments that the outputs would inform'] },
            { title: 'Work Plan Feasibility',  score: 6, feedback: 'Month ranges are set for all WPs but WP2 has no tasks assigned, making it impossible to assess whether the timeline is achievable for the analysis phase.', suggestions: ['Assign at least three tasks to WP2 with estimated person-months'] },
            { title: 'Budget Justification',   score: 5, feedback: 'The equipment line (€55,000) is listed without a justification narrative. Evaluators routinely request clarification on equipment costs above €10,000.', suggestions: ['Add a one-paragraph equipment justification to the budget section'] },
          ];
          const strengths = [
            'Strong and verifiable consortium with three verified institutions and complementary expertise across all stated WPs',
            'Abstract clearly states the research problem, objective, and approach in accessible language',
            'All deliverables include dissemination levels and due months — work plan tables are complete',
          ];
          const weaknesses = [
            'Impact pathway does not address required TRL progression — this is the most common reason for below-threshold scores in this call',
            'WP2 has no tasks, making timeline assessment impossible for evaluators',
            'Equipment budget line lacks narrative justification — a common trigger for Clarification Requests',
          ];
          const topSuggestions = [
            'Add a TRL progression section to Impact Pathway — this is an explicit criterion worth up to 3 points in the evaluation grid',
            'Assign tasks to WP2 and link person-months to named team members before final submission',
            'Write a one-paragraph equipment justification that explains why the listed items cannot be sourced through existing infrastructure',
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${AI}30`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, background: `linear-gradient(90deg, ${AI}12 0%, ${AI}05 100%)`, borderBottom: '1px solid', borderColor: `${AI}20` }}>
                <RateReviewIcon sx={{ fontSize: '1.1rem', color: AI }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: AI }}>AI Review Report</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>vs. Horizon Europe Cluster 6 SFS-01-2026 evaluation criteria</Typography>
                </Box>
                {/* Overall score dial */}
                <Box sx={{ textAlign: 'center', px: 1.75, py: 0.75, borderRadius: 2, bgcolor: `${scoreColor}12`, border: `2px solid ${scoreColor}40`, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{overallScore}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1.2 }}>/ 10<br />overall</Typography>
                </Box>
              </Box>
              {/* Summary */}
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: `${AI}04`, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.6 }}>
                  "The proposal demonstrates strong scientific ambition and a credible team, but the Impact section does not address TRL progression — an explicit evaluation criterion — and WP2 lacks task assignments, which prevents timeline assessment. These two issues are likely responsible for most of the score gap below threshold."
                </Typography>
              </Box>
              {/* Strengths / Weaknesses */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider', bgcolor: 'rgba(76,175,80,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: '0.85rem', color: '#4caf50' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: '#4caf50' }}>Strengths</Typography>
                  </Box>
                  {strengths.map((s, si) => (
                    <Box key={si} sx={{ display: 'flex', gap: 0.75, mb: si < strengths.length - 1 ? 0.85 : 0 }}>
                      <Typography sx={{ fontSize: '0.82rem', color: '#4caf50', flexShrink: 0, mt: 0.1 }}>✓</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.5 }}>{s}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ p: 2, bgcolor: 'rgba(244,67,54,0.03)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <ErrorOutlineIcon sx={{ fontSize: '0.85rem', color: '#f44336' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: '#f44336' }}>Weaknesses</Typography>
                  </Box>
                  {weaknesses.map((w, wi) => (
                    <Box key={wi} sx={{ display: 'flex', gap: 0.75, mb: wi < weaknesses.length - 1 ? 0.85 : 0 }}>
                      <Typography sx={{ fontSize: '0.82rem', color: '#f44336', flexShrink: 0, mt: 0.1 }}>✗</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.5 }}>{w}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {/* Section scores */}
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.disabled', mb: 1.25 }}>Section-by-Section Scores</Typography>
                {sections.map((sec, si) => {
                  const sc = sec.score;
                  const sc_color = sc >= 7 ? '#4caf50' : sc >= 5 ? '#ff9800' : '#f44336';
                  return (
                    <Box key={si} sx={{ mb: si < sections.length - 1 ? 1.5 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.primary', flex: 1 }}>{sec.title}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, px: 0.9, py: 0.2, borderRadius: 1.5, bgcolor: `${sc_color}12`, border: '1px solid', borderColor: `${sc_color}30`, flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: sc_color, lineHeight: 1 }}>{sc}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>/10</Typography>
                        </Box>
                      </Box>
                      {/* Score bar */}
                      <Box sx={{ height: 4, borderRadius: 1, bgcolor: 'action.disabledBackground', overflow: 'hidden', mb: 0.6 }}>
                        <Box sx={{ width: `${sc * 10}%`, height: '100%', bgcolor: sc_color, borderRadius: 1 }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.5, mb: sec.suggestions.length ? 0.5 : 0 }}>{sec.feedback}</Typography>
                      {sec.suggestions.map((sug, sugi) => (
                        <Box key={sugi} sx={{ display: 'flex', gap: 0.5, mt: 0.35 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: AI, flexShrink: 0 }}>→</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: AI, fontStyle: 'italic' }}>{sug}</Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })}
              </Box>
              {/* Top suggestions */}
              <Box sx={{ p: 2, background: `linear-gradient(135deg, ${AI}05 0%, ${AI}02 100%)` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                  <AutoAwesomeIcon sx={{ fontSize: '0.88rem', color: AI }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: AI }}>Top Improvement Suggestions</Typography>
                </Box>
                {topSuggestions.map((sug, sugi) => (
                  <Box key={sugi} sx={{ display: 'flex', gap: 1.25, mb: sugi < topSuggestions.length - 1 ? 1 : 0 }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: AI, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.15 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#fff', fontWeight: 800 }}>{sugi + 1}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', lineHeight: 1.55 }}>{sug}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }

        if (block.type === 'pw_tabs') {
          const PW = '#5b6fd4';
          const TABS = [
            { icon: <DescriptionIcon sx={{ fontSize: '0.95rem' }} />, label: 'Abstract',      active: false },
            { icon: <FolderIcon      sx={{ fontSize: '0.95rem' }} />, label: 'Sections',      active: true  },
            { icon: <FolderSpecialIcon sx={{ fontSize: '0.95rem' }} />, label: 'Work Packages', active: false },
            { icon: <AttachMoneyIcon sx={{ fontSize: '0.95rem' }} />, label: 'Budget',        active: false },
            { icon: <CampaignIcon    sx={{ fontSize: '0.95rem' }} />, label: 'Dissemination', active: false },
            { icon: <PreviewIcon     sx={{ fontSize: '0.95rem' }} />, label: 'Preview',       active: false },
          ];
          const DESCS = [
            'Opening summary auto-compiled from section content, with AI assist and word limit tracker',
            'Nested section tree with rich-text editor, per-section locking, status tracking, and version history',
            'WPs with tasks, deliverables, milestones, risks — auto-generates all Annex 1 tables',
            'Person-month matrix, equipment and other costs — outputs Staff Effort Summary and budget breakdown',
            'Publication targets, training events, and policy outreach planning',
            'Full rendered proposal in reading order — what reviewers see — matches the Word export exactly',
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${PW}25`, overflow: 'hidden' }}>
              {/* Tab bar */}
              <Box sx={{ display: 'flex', bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                {TABS.map((t, ti) => (
                  <Box key={ti} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.6,
                    px: 1.5, py: 1.1, flexShrink: 0, cursor: 'default',
                    borderBottom: t.active ? `2px solid ${PW}` : '2px solid transparent',
                    bgcolor: t.active ? `${PW}08` : 'transparent',
                    color: t.active ? PW : 'text.secondary',
                    transition: 'all .15s',
                  }}>
                    {React.cloneElement(t.icon, { sx: { fontSize: '0.95rem', color: t.active ? PW : 'text.disabled' } })}
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: t.active ? 700 : 400, whiteSpace: 'nowrap', color: t.active ? PW : 'text.secondary' }}>{t.label}</Typography>
                  </Box>
                ))}
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.4, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'default' }}>
                    <DownloadIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Export .docx</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.4, borderRadius: 1, bgcolor: PW, cursor: 'default' }}>
                    <AutoAwesomeIcon sx={{ fontSize: '0.8rem', color: '#fff' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: '#fff', fontWeight: 700 }}>AI Review</Typography>
                  </Box>
                </Box>
              </Box>
              {/* Tab descriptions */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: `linear-gradient(135deg, ${PW}04 0%, ${PW}02 100%)` }}>
                {TABS.map((t, ti) => (
                  <Box key={ti} sx={{
                    display: 'flex', gap: 1, p: 1.5,
                    borderRight: (ti % 3 < 2) ? '1px solid' : 'none', borderColor: 'divider',
                    borderBottom: ti < 3 ? '1px solid' : 'none',
                    bgcolor: t.active ? `${PW}06` : 'transparent',
                  }}>
                    <Box sx={{ pt: 0.15, flexShrink: 0, color: t.active ? PW : 'text.disabled' }}>
                      {React.cloneElement(t.icon, { sx: { fontSize: '1rem', color: t.active ? PW : 'text.disabled' } })}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: t.active ? PW : 'text.primary', mb: 0.25 }}>{t.label}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.45 }}>{DESCS[ti]}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }

        // ── ProposalWriter: section editor ────────────────────────────────
        if (block.type === 'pw_section_editor') {
          const PW = '#5b6fd4';
          const STATUSES = [
            { key: 'draft',     label: 'Draft',     color: '#9e9e9e' },
            { key: 'in_review', label: 'In Review', color: '#ff9800' },
            { key: 'final',     label: 'Final',     color: '#4caf50' },
          ];
          const draftLines = [
            'The Climate Adaptation Research Consortium (CARC) brings together four interdisciplinary groups with complementary expertise across soil microbiome analysis, remote sensing technology, and climate modelling.',
            'Dr. Maria Chen (Univ. Amsterdam) leads the consortium with 18 peer-reviewed publications in computational soil ecology and a track record of three successfully completed Horizon Europe projects.',
            'Dr. Amara Osei (ETH Zürich) contributes expertise in large-scale field trials across sub-Saharan Africa and Mediterranean climate zones, directly relevant to the call\'s cross-regional objective.',
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${PW}25`, overflow: 'hidden' }}>
              {/* Section header */}
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: 'text.primary', flex: 1 }}>2.1 &nbsp; Team Qualifications</Typography>
                {/* Status badges */}
                {STATUSES.map((s, si) => (
                  <Box key={s.key} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.4,
                    px: 0.9, py: 0.3, borderRadius: 5, cursor: 'default',
                    bgcolor: si === 1 ? `${s.color}15` : 'transparent',
                    border: '1px solid', borderColor: si === 1 ? `${s.color}50` : 'divider',
                  }}>
                    {si === 1 && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />}
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: si === 1 ? 700 : 400, color: si === 1 ? s.color : 'text.disabled' }}>{s.label}</Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.3, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'default' }}>
                    <LockOpenIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>Lock</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.3, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'default' }}>
                    <HistoryIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>History</Typography>
                  </Box>
                </Box>
              </Box>
              {/* Word limit bar */}
              <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: `${PW}04`, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', flexShrink: 0 }}>Word limit</Typography>
                <Box sx={{ flex: 1, height: 5, borderRadius: 2, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                  <Box sx={{ width: '43%', height: '100%', bgcolor: PW, borderRadius: 2 }} />
                </Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: PW, flexShrink: 0 }}>342 / 800 words</Typography>
              </Box>
              {/* Rich text area */}
              <Box sx={{ p: 2.5, bgcolor: 'background.paper' }}>
                {draftLines.map((line, li) => (
                  <Typography key={li} sx={{ fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.65, mb: li < draftLines.length - 1 ? 1 : 0 }}>{line}</Typography>
                ))}
                {/* Cursor */}
                <Box sx={{ display: 'inline-block', width: 2, height: '1.1em', bgcolor: PW, ml: 0.25, verticalAlign: 'text-bottom', animation: 'none', opacity: 0.7 }} />
              </Box>
              {/* Toolbar */}
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.75, borderTop: '1px solid', borderColor: 'divider', bgcolor: `${PW}04` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.45, borderRadius: 1.5, background: `linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)`, cursor: 'default' }}>
                  <AutoAwesomeIcon sx={{ fontSize: '0.82rem', color: '#fff' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#fff', fontWeight: 700 }}>AI Generate</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.45, borderRadius: 1.5, border: '1px solid', borderColor: `${PW}40`, bgcolor: `${PW}08`, cursor: 'default' }}>
                  <SaveIcon sx={{ fontSize: '0.82rem', color: PW }} />
                  <Typography sx={{ fontSize: '0.75rem', color: PW, fontWeight: 600 }}>Save</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                {/* Locked section note */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon sx={{ fontSize: '0.75rem', color: '#ff9800' }} />
                  <Typography sx={{ fontSize: '0.7rem', color: '#ff9800' }}>Section 2.2 locked by Dr. Nakamura</Typography>
                </Box>
              </Box>
            </Box>
          );
        }

        // ── ProposalWriter: work packages ─────────────────────────────────
        if (block.type === 'pw_work_packages') {
          const PW = '#5b6fd4';
          const wps = [
            { num: 1, title: 'Data Collection & Field Trials',  start: 1,  end: 12, tasks: 4, lead: 'Dr. Osei',     deliverables: 2, milestones: 1 },
            { num: 2, title: 'Data Analysis & Modelling',       start: 6,  end: 20, tasks: 5, lead: 'Dr. Chen',     deliverables: 3, milestones: 2 },
            { num: 3, title: 'Dissemination & Policy Outreach', start: 18, end: 24, tasks: 3, lead: 'Dr. Nakamura', deliverables: 2, milestones: 1 },
          ];
          const deliverables = [
            { code: 'D1.1', title: 'Field Survey Dataset v1',    wp: 1, month: 6,  level: 'Public',        status: 'Report' },
            { code: 'D2.1', title: 'Soil Microbiome Analysis',   wp: 2, month: 14, level: 'Restricted',    status: 'Dataset' },
            { code: 'D2.2', title: 'Climate Model Integration',  wp: 2, month: 20, level: 'Public',        status: 'Other' },
            { code: 'D3.1', title: 'Policy Brief for EC',        wp: 3, month: 24, level: 'Public',        status: 'Report' },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${PW}25`, overflow: 'hidden' }}>
              {/* WP list table */}
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: `${PW}08`, borderBottom: '1px solid', borderColor: `${PW}20` }}>
                <AccountTreeIcon sx={{ fontSize: '0.95rem', color: PW }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: PW }}>Work Packages</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                  {['WPs', 'Deliverables', 'Milestones', 'Risks'].map((t, ti) => (
                    <Box key={t} sx={{ px: 0.9, py: 0.25, borderRadius: 1, bgcolor: ti === 0 ? PW : 'transparent', border: '1px solid', borderColor: ti === 0 ? PW : 'divider', cursor: 'default' }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: ti === 0 ? '#fff' : 'text.secondary' }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {wps.map((wp, wi) => (
                <Box key={wi} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, borderBottom: wi < wps.length - 1 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: 'background.paper', '&:hover': { bgcolor: `${PW}04` } }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: `${PW}12`, border: '1px solid', borderColor: `${PW}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: PW }}>WP{wp.num}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'text.primary' }}>{wp.title}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>Lead: {wp.lead}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Box sx={{ bgcolor: `${PW}08`, border: '1px solid', borderColor: `${PW}20`, borderRadius: 1, px: 0.75, py: 0.2, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: PW, fontWeight: 600 }}>M{wp.start}–M{wp.end}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'action.disabledBackground', borderRadius: 1, px: 0.75, py: 0.2 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>{wp.tasks} tasks</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#4caf5010', border: '1px solid #4caf5025', borderRadius: 1, px: 0.75, py: 0.2 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#4caf50', fontWeight: 600 }}>D×{wp.deliverables}</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#ff980010', border: '1px solid #ff980025', borderRadius: 1, px: 0.75, py: 0.2 }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#ff9800', fontWeight: 600 }}>MS×{wp.milestones}</Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              {/* Deliverables sub-table */}
              <Box sx={{ borderTop: '2px solid', borderColor: `${PW}15` }}>
                <Box sx={{ px: 2, py: 0.75, bgcolor: `${PW}05`, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: PW }}>Auto-generated: List of Deliverables (Table 2.1a)</Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '3.5rem 1fr 3.5rem 4rem 6rem 5rem', bgcolor: `${PW}06`, px: 2, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  {['No.', 'Deliverable', 'WP', 'Month', 'Dissem. Level', 'Type'].map(h => (
                    <Typography key={h} sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', letterSpacing: 0.5, textTransform: 'uppercase' }}>{h}</Typography>
                  ))}
                </Box>
                {deliverables.map((d, di) => (
                  <Box key={di} sx={{ display: 'grid', gridTemplateColumns: '3.5rem 1fr 3.5rem 4rem 6rem 5rem', px: 2, py: 0.65, bgcolor: 'background.paper', borderBottom: di < deliverables.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: PW }}>{d.code}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.primary' }}>{d.title}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>WP{d.wp}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>M{d.month}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: d.level === 'Public' ? '#4caf50' : '#ff9800', fontWeight: 500 }}>{d.level}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{d.status}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }

        // ── ProposalWriter: budget ─────────────────────────────────────────
        if (block.type === 'pw_budget') {
          const PW = '#5b6fd4';
          const members = [
            { name: 'Dr. M. Chen',     wps: [6, 3, 1], total: 10 },
            { name: 'Dr. A. Osei',     wps: [3, 6, 2], total: 11 },
            { name: 'Dr. B. Nakamura', wps: [2, 4, 1], total: 7  },
            { name: 'Dr. C. Dubois',   wps: [1, 2, 1], total: 4  },
          ];
          const wpTotals = [12, 15, 5];
          const budget = [
            { label: 'Personnel',        amount: '€ 420 000', pct: 84, color: PW },
            { label: 'Equipment',        amount: '€  55 000', pct: 11, color: '#9333ea' },
            { label: 'Other direct costs', amount: '€  25 000', pct: 5,  color: '#2196f3' },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${PW}25`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: `${PW}08`, borderBottom: '1px solid', borderColor: `${PW}20` }}>
                <AttachMoneyIcon sx={{ fontSize: '0.95rem', color: PW }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: PW }}>Budget — Staff Effort Summary (Table 2.1f)</Typography>
                <Box sx={{ ml: 'auto', px: 1.25, py: 0.3, borderRadius: 5, bgcolor: PW }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#fff', fontWeight: 700 }}>Total: € 500 000</Typography>
                </Box>
              </Box>
              {/* Person-months grid */}
              <Box sx={{ overflowX: 'auto', bgcolor: 'background.paper' }}>
                {/* Column headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '9rem repeat(3, 4rem) 5rem', px: 2, py: 0.6, bgcolor: `${PW}06`, borderBottom: '1px solid', borderColor: 'divider', minWidth: 360 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase' }}>Member</Typography>
                  {['WP1', 'WP2', 'WP3'].map(w => (
                    <Typography key={w} sx={{ fontSize: '0.65rem', fontWeight: 700, color: PW, textTransform: 'uppercase', textAlign: 'right' }}>{w}</Typography>
                  ))}
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', textAlign: 'right' }}>Total PM</Typography>
                </Box>
                {members.map((m, mi) => (
                  <Box key={mi} sx={{ display: 'grid', gridTemplateColumns: '9rem repeat(3, 4rem) 5rem', px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', minWidth: 360, bgcolor: 'background.paper' }}>
                    <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', fontWeight: 500 }}>{m.name}</Typography>
                    {m.wps.map((pm, pi) => (
                      <Typography key={pi} sx={{ fontSize: '0.82rem', color: pm > 0 ? 'text.primary' : 'text.disabled', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pm > 0 ? pm : '—'}</Typography>
                    ))}
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: PW, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.total}</Typography>
                  </Box>
                ))}
                {/* Totals row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '9rem repeat(3, 4rem) 5rem', px: 2, py: 0.75, bgcolor: `${PW}06`, minWidth: 360, borderTop: '2px solid', borderColor: `${PW}20` }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>Total</Typography>
                  {wpTotals.map((t, ti) => (
                    <Typography key={ti} sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t}</Typography>
                  ))}
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: PW, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>32</Typography>
                </Box>
              </Box>
              {/* Budget breakdown */}
              <Box sx={{ p: 2, borderTop: '2px solid', borderColor: `${PW}15`, background: `linear-gradient(135deg, ${PW}04 0%, ${PW}02 100%)` }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>Budget Breakdown</Typography>
                {budget.map((b, bi) => (
                  <Box key={bi} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: bi < budget.length - 1 ? 0.85 : 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', width: 130, flexShrink: 0 }}>{b.label}</Typography>
                    <Box sx={{ flex: 1, height: 7, borderRadius: 2, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                      <Box sx={{ width: `${b.pct}%`, height: '100%', bgcolor: b.color, borderRadius: 2 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: b.color, width: 80, textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{b.amount}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', width: 28, flexShrink: 0 }}>{b.pct}%</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }

        // ── ProposalWriter: completeness warnings ─────────────────────────
        if (block.type === 'pw_warnings') {
          const PW = '#5b6fd4';
          const groups = [
            {
              category: 'Work Packages',
              items: [
                { severity: 'error',   msg: 'WP2 "Data Analysis": has no tasks assigned' },
                { severity: 'warning', msg: 'WP3 "Dissemination": description is missing' },
                { severity: 'info',    msg: 'WP2 "Data Analysis": end month not set' },
              ],
            },
            {
              category: 'Sections',
              items: [
                { severity: 'error',   msg: 'Section 3.1 "Impact": no content written yet' },
                { severity: 'warning', msg: 'Section 2.2 "Gender Equality": content is very short (< 50 words)' },
              ],
            },
            {
              category: 'Budget',
              items: [
                { severity: 'info', msg: 'Dr. Dubois has no person-months assigned in WP2' },
              ],
            },
          ];
          const SEV = {
            error:   { icon: <ErrorOutlineIcon  sx={{ fontSize: '0.85rem', color: '#f44336' }} />, color: '#f44336', bg: '#f4433610', border: '#f4433625', label: 'Missing'    },
            warning: { icon: <WarningAmberIcon  sx={{ fontSize: '0.85rem', color: '#ff9800' }} />, color: '#ff9800', bg: '#ff980010', border: '#ff980025', label: 'Incomplete' },
            info:    { icon: <InfoOutlinedIcon  sx={{ fontSize: '0.85rem', color: '#2196f3' }} />, color: '#2196f3', bg: '#2196f310', border: '#2196f325', label: 'Suggested'  },
          };
          const counts = { error: 2, warning: 2, info: 2 };
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${PW}25`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: `${PW}08`, borderBottom: '1px solid', borderColor: `${PW}20` }}>
                <CheckCircleIcon sx={{ fontSize: '1rem', color: PW }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: PW }}>Completeness Check</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75 }}>
                  {Object.entries(SEV).map(([k, s]) => (
                    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.3, borderRadius: 5, bgcolor: s.bg, border: '1px solid', borderColor: s.border }}>
                      {s.icon}
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: s.color }}>{counts[k]} {s.label}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {/* Warning groups */}
              {groups.map((g, gi) => (
                <Box key={gi} sx={{ borderBottom: gi < groups.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Box sx={{ px: 2, py: 0.75, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6 }}>{g.category} ({g.items.length})</Typography>
                  </Box>
                  {g.items.map((item, ii) => {
                    const s = SEV[item.severity];
                    return (
                      <Box key={ii} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 0.85, borderBottom: ii < g.items.length - 1 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: 'background.paper' }}>
                        {s.icon}
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', flex: 1 }}>{item.msg}</Typography>
                        <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, bgcolor: s.bg, border: '1px solid', borderColor: s.border, flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.65rem', color: s.color, fontWeight: 700 }}>{s.label}</Typography>
                        </Box>
                        <RadioButtonUncheckedIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0 }} />
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === 'ai_section_editor') {
          const AI_COLOR = '#7c3aed';
          const sectionLines = [
            'The Climate Adaptation Research Consortium brings together four research groups with complementary expertise across soil microbiome analysis, remote sensing, and climate modelling.',
            'Dr. Maria Chen (Univ. Amsterdam) leads the consortium with 18 peer-reviewed publications in computational soil ecology. Dr. Amara Osei (ETH Zürich) contributes a track record of large-scale field trials across sub-Saharan Africa and Mediterranean climates.',
            'Together, the consortium covers all required expertise areas for Horizon Europe Cluster 6 SFS-01-2026, with particular strength in interdisciplinary field-to-lab methodology.',
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${AI_COLOR}30`, overflow: 'hidden' }}>
              {/* Section header */}
              <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(90deg, ${AI_COLOR}12 0%, ${AI_COLOR}04 100%)`, borderBottom: '1px solid', borderColor: `${AI_COLOR}20` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesomeIcon sx={{ fontSize: '1rem', color: AI_COLOR }} />
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary' }}>Team Qualifications</Typography>
                  <Box sx={{ bgcolor: `${AI_COLOR}10`, border: '1px solid', borderColor: `${AI_COLOR}25`, borderRadius: 1, px: 0.75, py: 0.2 }}>
                    <Typography sx={{ fontSize: '0.68rem', color: AI_COLOR, fontWeight: 600 }}>Section 2 of 7</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>Word limit: 800</Typography>
              </Box>
              {/* Toolbar */}
              <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                {/* Enable AI toggle — active */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.5, borderRadius: 1.5, background: `linear-gradient(135deg, ${AI_COLOR} 0%, #9333ea 100%)`, cursor: 'default', flexShrink: 0 }}>
                  <PowerSettingsNewIcon sx={{ fontSize: '0.85rem', color: '#fff' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: '#fff', fontWeight: 700 }}>AI Enabled</Typography>
                </Box>
                {/* Generate Text */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.1, py: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: `${AI_COLOR}40`, bgcolor: `${AI_COLOR}08`, cursor: 'default', flexShrink: 0 }}>
                  <BoltIcon sx={{ fontSize: '0.85rem', color: AI_COLOR }} />
                  <Typography sx={{ fontSize: '0.78rem', color: AI_COLOR, fontWeight: 700 }}>Generate Text</Typography>
                </Box>
                {/* Preview Context */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: 'default', flexShrink: 0 }}>
                  <VisibilityOutlinedIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Preview Context</Typography>
                </Box>
                {/* Custom Instructions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: 'default', flexShrink: 0 }}>
                  <TuneIcon sx={{ fontSize: '0.8rem', color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Instructions</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                  <AutoAwesomeIcon sx={{ fontSize: '0.8rem', color: '#f59e0b' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>21 credits</Typography>
                </Box>
              </Box>
              {/* Generated text body */}
              <Box sx={{ p: 2.5, bgcolor: 'background.paper' }}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: `${AI_COLOR}20`, bgcolor: `${AI_COLOR}04`, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: '0.75rem', color: AI_COLOR }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: AI_COLOR }}>AI Draft — click to edit</Typography>
                  </Box>
                  {sectionLines.map((line, li) => (
                    <Typography key={li} sx={{ fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.65, mb: li < sectionLines.length - 1 ? 1 : 0 }}>{line}</Typography>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: '0.8rem', color: '#4caf50' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>1 credit used · 21 remaining</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Box sx={{ px: 1, py: 0.4, borderRadius: 1, border: '1px solid', borderColor: 'divider', cursor: 'default' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Regenerate</Typography>
                    </Box>
                    <Box sx={{ px: 1, py: 0.4, borderRadius: 1, background: `linear-gradient(135deg, ${AI_COLOR} 0%, #9333ea 100%)`, cursor: 'default' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#fff', fontWeight: 700 }}>Accept</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }

        // ── AI credits purchase ────────────────────────────────────────────
        if (block.type === 'ai_credits') {
          const AI_COLOR = '#7c3aed';
          const packs = [
            { name: 'Starter', credits: 10,  price: '€4',  desc: 'One proposal section draft', popular: false },
            { name: 'Standard', credits: 50, price: '€15', desc: 'A full proposal run-through', popular: true  },
            { name: 'Pro',     credits: 200, price: '€45', desc: 'Multi-proposal teams',        popular: false },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${AI_COLOR}30`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `linear-gradient(90deg, ${AI_COLOR}12 0%, ${AI_COLOR}04 100%)`, borderBottom: '1px solid', borderColor: `${AI_COLOR}20` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon sx={{ fontSize: '1rem', color: AI_COLOR }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: AI_COLOR }}>AI Credits</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 5, bgcolor: `${AI_COLOR}10`, border: '1px solid', borderColor: `${AI_COLOR}25` }}>
                  <AutoAwesomeIcon sx={{ fontSize: '0.85rem', color: '#f59e0b' }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>21 credits remaining</Typography>
                </Box>
              </Box>
              {/* Credit packs */}
              <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, background: `linear-gradient(135deg, ${AI_COLOR}04 0%, ${AI_COLOR}02 100%)` }}>
                {packs.map((pack) => (
                  <Box key={pack.name} sx={{
                    display: 'flex', flexDirection: 'column', p: 1.75,
                    borderRadius: 2, bgcolor: 'background.paper',
                    border: '1px solid', borderColor: pack.popular ? AI_COLOR : 'divider',
                    position: 'relative',
                    ...(pack.popular && { boxShadow: `0 0 0 2px ${AI_COLOR}18` }),
                  }}>
                    {pack.popular && (
                      <Box sx={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', bgcolor: AI_COLOR, borderRadius: 5, px: 1, py: 0.25 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' }}>Most popular</Typography>
                      </Box>
                    )}
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.disabled', mb: 0.5 }}>{pack.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                      <AutoAwesomeIcon sx={{ fontSize: '1rem', color: '#f59e0b', mb: 0.25 }} />
                      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>{pack.credits}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>credits</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.25, lineHeight: 1.4 }}>{pack.desc}</Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        pt: 1, borderTop: '1px solid', borderColor: 'divider', mb: 1,
                      }}>
                        <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: pack.popular ? AI_COLOR : 'text.primary' }}>{pack.price}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>one-time</Typography>
                      </Box>
                      <Box sx={{
                        width: '100%', py: 0.6, borderRadius: 1.5, cursor: 'default', textAlign: 'center',
                        background: pack.popular
                          ? `linear-gradient(135deg, ${AI_COLOR} 0%, #9333ea 100%)`
                          : 'transparent',
                        border: '1px solid',
                        borderColor: pack.popular ? AI_COLOR : 'divider',
                      }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: pack.popular ? '#fff' : 'text.secondary' }}>Purchase</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Footer */}
              <Box sx={{ px: 2.5, py: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper' }}>
                <AutoAwesomeIcon sx={{ fontSize: '0.8rem', color: AI_COLOR }} />
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>1 credit = 1 section generation · Credits never expire · Secure payment via Stripe</Typography>
              </Box>
            </Box>
          );
        }

        // ── AI custom instruction form ─────────────────────────────────────
        if (block.type === 'ai_custom_instruction') {
          const AI_COLOR = '#7c3aed';
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${AI_COLOR}30`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, background: `linear-gradient(90deg, ${AI_COLOR}12 0%, ${AI_COLOR}04 100%)`, borderBottom: '1px solid', borderColor: `${AI_COLOR}20` }}>
                <TuneIcon sx={{ fontSize: '1rem', color: AI_COLOR }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: AI_COLOR }}>Custom Instructions — Team Qualifications</Typography>
              </Box>
              <Box sx={{ p: 2.5, background: `linear-gradient(135deg, ${AI_COLOR}04 0%, ${AI_COLOR}02 100%)` }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', mb: 0.75 }}>Instruction for this section</Typography>
                <Box sx={{ border: '1px solid', borderColor: `${AI_COLOR}35`, borderRadius: 1.5, p: 1.25, bgcolor: 'background.paper', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.55, fontStyle: 'italic' }}>
                    "Emphasise the clinical translation pathway in the impact paragraph. Avoid mentioning partner KU Leuven in this section — they are joining after the submission deadline."
                  </Typography>
                </Box>
                {/* Context preview panel */}
                <Box sx={{ border: '1px solid', borderColor: `${AI_COLOR}20`, borderRadius: 1.5, overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: `${AI_COLOR}08`, borderBottom: '1px solid', borderColor: `${AI_COLOR}15` }}>
                    <VisibilityOutlinedIcon sx={{ fontSize: '0.85rem', color: AI_COLOR }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: AI_COLOR }}>Context preview</Typography>
                    <Box sx={{ ml: 'auto', bgcolor: '#4caf5015', borderRadius: 1, px: 0.75, border: '1px solid #4caf5030' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: '#4caf50', fontWeight: 600 }}>~3 200 tokens</Typography>
                    </Box>
                  </Box>
                  {[
                    { label: 'Consortium', value: 'Climate Adaptation Research Consortium · 4 members' },
                    { label: 'Call',       value: 'Horizon Europe Cluster 6 SFS-01-2026 · €8M ceiling' },
                    { label: 'Members',    value: 'Dr. Maria Chen, Dr. Amara Osei, Dr. Ben Nakamura' },
                    { label: 'Section',    value: 'Team Qualifications · 800 word limit' },
                    { label: 'Instruction', value: '"Emphasise clinical translation…"' },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', px: 1.5, py: 0.6, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' }, bgcolor: 'background.paper' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', width: 90, flexShrink: 0 }}>{label}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.primary', fontWeight: 500 }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Box sx={{ px: 1.25, py: 0.55, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: 'default' }}>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>Clear</Typography>
                  </Box>
                  <Box sx={{ px: 1.5, py: 0.55, borderRadius: 1.5, background: `linear-gradient(135deg, ${AI_COLOR} 0%, #9333ea 100%)`, cursor: 'default', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <BoltIcon sx={{ fontSize: '0.85rem', color: '#fff' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#fff', fontWeight: 700 }}>Generate Text</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }

        if (block.type === 'consortium_step') {
          const step = block.step;
          const STEP_COLORS = ['#667eea', '#ad89ad', '#2196f3', '#4caf50'];
          const STEP_LABELS = ['Define', 'Discover', 'Assign Roles', 'Coordinate'];
          const color = STEP_COLORS[step - 1];

          // ── Step 1: Create Consortium form ──────────────────────────────
          if (step === 1) return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${color}30`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, background: `linear-gradient(90deg, ${color}14 0%, ${color}06 100%)`, borderBottom: '1px solid', borderColor: `${color}20` }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800 }}>1</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color }}>Create Consortium — Statement of Intent</Typography>
              </Box>
              <Box sx={{ p: 2.5, background: `linear-gradient(135deg, ${color}04 0%, ${color}02 100%)` }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                  {[
                    { label: 'Consortium Name', value: 'Climate Adaptation Research Consortium' },
                    { label: 'Acronym', value: 'CARC' },
                  ].map(({ label, value }) => (
                    <Box key={label}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', mb: 0.5 }}>{label}</Typography>
                      <Box sx={{ border: '1px solid', borderColor: `${color}35`, borderRadius: 1, px: 1.25, py: 0.75, bgcolor: 'background.paper' }}>
                        <Typography sx={{ fontSize: '0.9rem', color: 'text.primary', fontWeight: 500 }}>{value}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', mb: 0.5 }}>Statement of Intent</Typography>
                  <Box sx={{ border: '1px solid', borderColor: `${color}35`, borderRadius: 1, p: 1.25, bgcolor: 'background.paper', minHeight: 56 }}>
                    <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary', lineHeight: 1.55, fontStyle: 'italic' }}>
                      "Interdisciplinary team addressing soil resilience under climate stress. Target: Horizon Europe Cluster 6 SFS-01-2026. Seeking expertise in remote sensing and molecular biology to complement our climate modelling core."
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', mb: 0.75 }}>Required Expertise</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {[
                      { label: 'Soil Microbiome', done: true },
                      { label: 'Remote Sensing', done: true },
                      { label: 'Climate Modelling', done: true },
                      { label: 'Molecular Biology', done: false },
                      { label: 'Clinical Partners', done: false },
                    ].map(({ label, done }) => (
                      <Box key={label} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1, py: 0.35, borderRadius: 5,
                        bgcolor: done ? `${color}12` : 'rgba(255,152,0,0.1)',
                        border: '1px solid', borderColor: done ? `${color}30` : 'rgba(255,152,0,0.35)',
                      }}>
                        {done
                          ? <CheckCircleIcon sx={{ fontSize: '0.8rem', color }} />
                          : <RadioButtonUncheckedIcon sx={{ fontSize: '0.8rem', color: '#ff9800' }} />
                        }
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: done ? color : '#ff9800' }}>{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.5, borderTop: '1px solid', borderColor: `${color}15` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: 1.5, background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`, cursor: 'default' }}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#fff', fontWeight: 700 }}>Create Consortium</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>2 expertise gaps will be highlighted for you</Typography>
                </Box>
              </Box>
            </Box>
          );

          // ── Step 2: Expert suggestion engine ────────────────────────────
          if (step === 2) return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${color}30`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, background: `linear-gradient(90deg, ${color}14 0%, ${color}06 100%)`, borderBottom: '1px solid', borderColor: `${color}20` }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800 }}>2</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color }}>Expertise Gap Analysis & Suggestions</Typography>
              </Box>
              <Box sx={{ p: 2.5, background: `linear-gradient(135deg, ${color}04 0%, ${color}02 100%)` }}>
                {/* Gap summary bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, mb: 2, borderRadius: 1.5, bgcolor: 'rgba(255,152,0,0.07)', border: '1px solid rgba(255,152,0,0.25)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(255,152,0,0.15)', flexShrink: 0 }}>
                    <PsychologyIcon sx={{ fontSize: '1.1rem', color: '#ff9800' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: 'text.primary', mb: 0.25 }}>2 expertise gaps identified in your consortium</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {['Molecular Biology', 'Clinical Partners'].map(g => (
                        <Box key={g} sx={{ bgcolor: 'rgba(255,152,0,0.12)', border: '1px solid rgba(255,152,0,0.3)', borderRadius: 5, px: 0.9, py: 0.2 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#ff9800', fontWeight: 600 }}>{g}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#ff9800', lineHeight: 1 }}>3/5</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>areas covered</Typography>
                  </Box>
                </Box>
                {/* Suggested researchers */}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>Suggested Researchers</Typography>
                {[
                  { initials: 'AK', name: 'Dr. A. Kovač', inst: 'Charles University', field: 'Molecular Biology · Genomics', match: 94, gap: 'Molecular Biology', color: '#667eea' },
                  { initials: 'BS', name: 'Dr. B. Santos', inst: 'Radboud University', field: 'Clinical Trials · Immunology', match: 87, gap: 'Clinical Partners', color: '#2196f3' },
                ].map((r, ri) => (
                  <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, mb: 1, borderRadius: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: `${color}20` }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${r.color} 0%, ${r.color}99 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>{r.initials}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>{r.name}</Typography>
                        <VerifiedIcon sx={{ fontSize: '0.8rem', color: r.color }} />
                        <Box sx={{ bgcolor: 'rgba(255,152,0,0.1)', borderRadius: 5, px: 0.75, border: '1px solid rgba(255,152,0,0.25)' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#ff9800', fontWeight: 700 }}>fills: {r.gap}</Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>{r.inst} · {r.field}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#4caf50', lineHeight: 1 }}>{r.match}%</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>match</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Box sx={{ px: 0.9, py: 0.4, borderRadius: 1, bgcolor: `${color}12`, border: '1px solid', borderColor: `${color}30`, cursor: 'default' }}>
                        <Typography sx={{ fontSize: '0.72rem', color, fontWeight: 600 }}>Suggest</Typography>
                      </Box>
                      <Box sx={{ px: 0.9, py: 0.4, borderRadius: 1, background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`, cursor: 'default' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#fff', fontWeight: 600 }}>Invite</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          );

          // ── Step 3: Role assignment ──────────────────────────────────────
          if (step === 3) {
            const roles = [
              { initials: 'MC', name: 'Dr. Maria Chen',      inst: 'Univ. Amsterdam',    role: 'Coordinator',            roleColor: '#667eea', avatarColor: '#667eea' },
              { initials: 'AO', name: 'Dr. Amara Osei',    inst: 'ETH Zürich',         role: 'Principal Investigator', roleColor: '#764ba2', avatarColor: '#764ba2' },
              { initials: 'BN', name: 'Dr. Ben Nakamura',  inst: 'TU Delft',           role: 'Work Package Leader',    roleColor: '#2196f3', avatarColor: '#2196f3' },
              { initials: 'CD', name: 'Dr. Clara Dubois',  inst: 'KU Leuven',          role: 'Collaborator',           roleColor: '#9e9e9e', avatarColor: '#9e9e9e' },
            ];
            return (
              <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${color}30`, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, background: `linear-gradient(90deg, ${color}14 0%, ${color}06 100%)`, borderBottom: '1px solid', borderColor: `${color}20` }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800 }}>3</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color }}>CARC — Member Roles</Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: '0.85rem', color: '#4caf50' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#4caf50', fontWeight: 600 }}>4 members · all roles assigned</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 2, background: `linear-gradient(135deg, ${color}04 0%, ${color}02 100%)` }}>
                  {roles.map((r, ri) => (
                    <Box key={ri} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      p: 1.25, mb: ri < roles.length - 1 ? 0.75 : 0,
                      borderRadius: 1.5, bgcolor: 'background.paper',
                      border: '1px solid', borderColor: ri === 0 ? `${r.roleColor}35` : 'divider',
                      ...(ri === 0 && { boxShadow: `0 0 0 2px ${r.roleColor}18` }),
                    }}>
                      <Box sx={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${r.avatarColor} 0%, ${r.avatarColor}99 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                        ...(ri === 0 && { boxShadow: `0 2px 10px ${r.avatarColor}50` }),
                      }}>{r.initials}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary' }}>{r.name}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>{r.inst}</Typography>
                      </Box>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        px: 1.1, py: 0.35, borderRadius: 5,
                        bgcolor: `${r.roleColor}12`, border: '1px solid', borderColor: `${r.roleColor}30`,
                      }}>
                        {ri === 0 && <VerifiedIcon sx={{ fontSize: '0.8rem', color: r.roleColor }} />}
                        <Typography sx={{ fontSize: '0.78rem', color: r.roleColor, fontWeight: 700 }}>{r.role}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        {ACTION_BUTTONS_META.filter(b => ['message', 'meet'].includes(b.key)).map(b => (
                          <Box key={b.key} sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: b.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {React.cloneElement(b.icon, { sx: { fontSize: '0.7rem', color: '#fff' } })}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, border: '1px dashed', borderColor: `${color}30`, cursor: 'default' }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '50%', border: '2px dashed', borderColor: `${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <GroupAddIcon sx={{ fontSize: '1rem', color: `${color}70` }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', color: `${color}80`, fontWeight: 500 }}>Invite another member…</Typography>
                  </Box>
                </Box>
              </Box>
            );
          }

          // ── Step 4: Shared workspace (messages + TODO) ──────────────────
          const msgs = [
            { initials: 'MC', color: '#667eea', text: 'WP1 objectives draft is ready for review.', time: '10:14' },
            { initials: 'AO', color: '#764ba2', text: 'Reviewed — looks good. Shall we align the budget allocation next?', time: '10:31' },
            { initials: 'BN', color: '#2196f3', text: 'I\'ll have the task breakdown ready by end of day.', time: '11:02' },
          ];
          const todos = [
            { label: 'Define WP1 objectives',    who: 'Dr. Chen',     wp: 'WP1', due: 'Sep 10', done: true  },
            { label: 'Draft methodology section', who: 'Dr. Osei',     wp: 'WP2', due: 'Sep 12', done: true  },
            { label: 'Upload partner CVs',        who: 'Dr. Nakamura', wp: 'WP1', due: 'Sep 15', done: false },
            { label: 'Review budget allocation',  who: 'Dr. Dubois',   wp: 'WP3', due: 'Sep 20', done: false },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${color}30`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1, background: `linear-gradient(90deg, ${color}14 0%, ${color}06 100%)`, borderBottom: '1px solid', borderColor: `${color}20` }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800 }}>4</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color }}>CARC — Shared Workspace</Typography>
              </Box>
              <Box sx={{ display: 'flex', background: `linear-gradient(135deg, ${color}04 0%, ${color}02 100%)` }}>
                {/* Messages */}
                <Box sx={{ flex: 1, p: 2, borderRight: '1px solid', borderColor: 'divider', minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.25 }}>
                    <EmailIcon sx={{ fontSize: '0.85rem', color }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color }}>Consortium Messages</Typography>
                  </Box>
                  {msgs.map((m, mi) => (
                    <Box key={mi} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${m.color} 0%, ${m.color}99 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.62rem', fontWeight: 700 }}>{m.initials}</Box>
                      <Box sx={{ flex: 1, bgcolor: 'background.paper', borderRadius: 1.5, px: 1.1, py: 0.75, border: '1px solid', borderColor: 'divider' }}>
                        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', lineHeight: 1.45 }}>{m.text}</Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25 }}>{m.time}</Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25, alignItems: 'center' }}>
                    <Box sx={{ flex: 1, border: '1px solid', borderColor: `${color}25`, borderRadius: 1.5, px: 1.1, py: 0.6, bgcolor: 'background.paper' }}>
                      <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>Write a message…</Typography>
                    </Box>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default' }}>
                      <SendIcon sx={{ fontSize: '0.8rem', color: '#fff' }} />
                    </Box>
                  </Box>
                </Box>
                {/* TODO panel */}
                <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: '0.85rem', color }} />
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color }}>Proposal TODO</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.68rem', color: '#4caf50', fontWeight: 600 }}>2/4 done</Typography>
                  </Box>
                  {todos.map((t, ti) => (
                    <Box key={ti} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.85, opacity: t.done ? 0.7 : 1 }}>
                      {t.done
                        ? <CheckCircleIcon sx={{ fontSize: '0.95rem', color: '#4caf50', flexShrink: 0, mt: 0.1 }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: '0.95rem', color: `${color}60`, flexShrink: 0, mt: 0.1 }} />
                      }
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', fontWeight: 500, textDecoration: t.done ? 'line-through' : 'none', lineHeight: 1.3 }}>{t.label}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                          <Box sx={{ bgcolor: `${color}10`, borderRadius: 0.75, px: 0.6 }}>
                            <Typography sx={{ fontSize: '0.65rem', color, fontWeight: 600 }}>{t.wp}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{t.who}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: t.done ? 'text.disabled' : '#ff9800' }}>· {t.due}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          );
        }

        // ── Funding workflow: agency selector ──────────────────────────────
        if (block.type === 'funding_agency_selector') {
          const agencies = [
            { code: 'EC',   label: 'European Commission',      sub: 'Horizon Europe (HEU)',             calls: 180, color: '#003399', selected: true  },
            { code: 'ERC',  label: 'European Research Council', sub: 'Frontier research grants',         calls: 12,  color: '#1565c0', selected: false },
            { code: 'EIC',  label: 'European Innovation Council', sub: 'Pathfinder, Accelerator',       calls: 8,   color: '#6a1b9a', selected: false },
            { code: 'DFG',  label: 'Deutsche Forschungsgemeinschaft', sub: 'Germany · national',        calls: 34,  color: '#c62828', selected: false },
            { code: 'ANR',  label: 'Agence Nationale de la Recherche', sub: 'France · national',        calls: 27,  color: '#1b5e20', selected: false },
            { code: 'NWO',  label: 'Dutch Research Council',   sub: 'Netherlands · national',           calls: 19,  color: '#e65100', selected: false },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${ACCENT}22`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, background: `linear-gradient(90deg, ${ACCENT}14 0%, ${ACCENT}06 100%)`, borderBottom: '1px solid', borderColor: `${ACCENT}18` }}>
                <AccountBalanceIcon sx={{ fontSize: '1rem', color: ACCENT }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: ACCENT }}>Select a Funding Agency</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.4, borderRadius: 1.5, border: '1px solid', borderColor: `${ACCENT}25`, bgcolor: 'background.paper' }}>
                  <SearchIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>Search agencies...</Typography>
                </Box>
              </Box>
              {/* Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0, bgcolor: 'background.paper' }}>
                {agencies.map((ag, ai) => (
                  <Box key={ai} sx={{
                    p: 1.75,
                    borderRight: (ai % 3 < 2) ? '1px solid' : 'none', borderColor: 'divider',
                    borderBottom: ai < 3 ? '1px solid' : 'none',
                    bgcolor: ag.selected ? `${ACCENT}06` : 'transparent',
                    borderLeft: ag.selected ? `3px solid ${ACCENT}` : '3px solid transparent',
                    cursor: 'default',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: ag.selected ? ACCENT : `${ag.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: ag.selected ? '#fff' : ag.color, lineHeight: 1 }}>{ag.code}</Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: ag.selected ? 700 : 600, color: ag.selected ? ACCENT : 'text.primary', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ag.label}</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', mb: 0.75 }}>{ag.sub}</Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.75, py: 0.2, borderRadius: 1, bgcolor: ag.selected ? `${ACCENT}12` : 'action.disabledBackground' }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: ag.selected ? ACCENT : 'text.disabled' }}>{ag.calls} calls</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Footer hint */}
              <Box sx={{ px: 2.5, py: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: `${ACCENT}04` }}>
                <PushPinIcon sx={{ fontSize: '0.78rem', color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>Pin agencies you visit frequently — they appear at the top of this list on every visit.</Typography>
              </Box>
            </Box>
          );
        }

        // ── Funding workflow: programme selector ───────────────────────────
        if (block.type === 'funding_program_selector') {
          const PROG_COLOR = '#1565c0';
          const programmes = [
            { code: 'CL1', label: 'Cluster 1',  sub: 'Health',                              calls: 22 },
            { code: 'CL4', label: 'Cluster 4',  sub: 'Digital, Industry & Space',            calls: 35 },
            { code: 'CL5', label: 'Cluster 5',  sub: 'Climate, Energy & Mobility',           calls: 29 },
            { code: 'CL6', label: 'Cluster 6',  sub: 'Food, Bioeconomy & Natural Resources', calls: 47, active: true },
            { code: 'ERC', label: 'ERC',         sub: 'Frontier Research Grants',             calls: 12 },
            { code: 'MSCA',label: 'MSCA',        sub: 'Marie Skłodowska-Curie Actions',       calls: 18 },
            { code: 'EIC', label: 'EIC',         sub: 'European Innovation Council',          calls: 8  },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${PROG_COLOR}25`, overflow: 'hidden' }}>
              {/* Breadcrumb */}
              <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 0.5, background: `linear-gradient(90deg, ${PROG_COLOR}10 0%, ${PROG_COLOR}04 100%)`, borderBottom: '1px solid', borderColor: `${PROG_COLOR}18` }}>
                <AccountBalanceIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>Funding Agencies</Typography>
                <ChevronRightIcon sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: PROG_COLOR }}>European Commission</Typography>
              </Box>
              {/* Two-col layout */}
              <Box sx={{ display: 'flex', bgcolor: 'background.paper' }}>
                {/* Programme list */}
                <Box sx={{ width: 210, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider' }}>
                  {programmes.map((p, pi) => (
                    <Box key={pi} sx={{
                      display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.9,
                      borderBottom: pi < programmes.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
                      bgcolor: p.active ? `${PROG_COLOR}08` : 'transparent',
                      borderLeft: p.active ? `3px solid ${PROG_COLOR}` : '3px solid transparent',
                      cursor: 'default',
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: p.active ? 700 : 500, color: p.active ? PROG_COLOR : 'text.primary', lineHeight: 1.25 }}>{p.label}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>{p.sub}</Typography>
                      </Box>
                      <Box sx={{ px: 0.6, py: 0.15, borderRadius: 1, bgcolor: p.active ? `${PROG_COLOR}14` : 'action.disabledBackground', flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: p.active ? PROG_COLOR : 'text.disabled' }}>{p.calls}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                {/* Detail pane */}
                <Box sx={{ flex: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: `${PROG_COLOR}12`, border: '1px solid', borderColor: `${PROG_COLOR}25`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: PROG_COLOR, lineHeight: 1 }}>HEU</Typography>
                      <Typography sx={{ fontSize: '0.55rem', color: `${PROG_COLOR}80`, lineHeight: 1 }}>CL6</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>Cluster 6</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>Food, Bioeconomy, Natural Resources, Agriculture & Environment</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.55, mb: 1.5 }}>
                    Supports research and innovation addressing sustainable food systems, soil health, biodiversity, blue economy, and the circular bioeconomy. Projects typically require 3+ partner institutions across at least 3 countries.
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {[
                      { label: 'Open calls',    value: '47', color: '#4caf50' },
                      { label: 'Budget range',  value: '€3–10M', color: PROG_COLOR },
                      { label: 'Min partners',  value: '3 countries', color: '#ff9800' },
                      { label: 'TRL range',     value: 'TRL 3 → 7', color: '#9333ea' },
                    ].map(({ label, value, color }) => (
                      <Box key={label} sx={{ p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: `${color}06` }}>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mb: 0.3 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color }}>{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }

        // ── Funding workflow: call list ─────────────────────────────────────
        if (block.type === 'funding_call_selector') {
          const CALL_COLOR = '#5b6fd4';
          const calls = [
            { ref: 'SFS-01-2026', title: 'Sustainable Food Systems in a Changing Climate', days: 28, budget: '€5–8M',   open: true,  urgent: true,  bookmarked: true  },
            { ref: 'SFS-02-2026', title: 'Soil Health, Carbon Sequestration & Land Use',   days: 45, budget: '€4–6M',   open: true,  urgent: false, bookmarked: false },
            { ref: 'CE-03-2026',  title: 'Circular Economy Innovation in the Bioeconomy',  days: 62, budget: '€4–7M',   open: true,  urgent: false, bookmarked: false },
            { ref: 'SUSTAG-04',   title: 'Agricultural Sustainability & Rural Resilience',  days: 90, budget: '€3.5–5M', open: false, urgent: false, bookmarked: false },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${CALL_COLOR}25`, overflow: 'hidden' }}>
              {/* Breadcrumb */}
              <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 0.5, background: `linear-gradient(90deg, ${CALL_COLOR}10 0%, ${CALL_COLOR}04 100%)`, borderBottom: '1px solid', borderColor: `${CALL_COLOR}18` }}>
                <AccountBalanceIcon sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>European Commission</Typography>
                <ChevronRightIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>Cluster 6</Typography>
                <ChevronRightIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: CALL_COLOR }}>47 calls</Typography>
              </Box>
              {/* Search + filters */}
              <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.55, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <SearchIcon sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>Search calls by keyword or reference...</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {['All', 'Open', 'Closing Soon'].map((f, fi) => (
                    <Box key={f} sx={{ px: 0.85, py: 0.35, borderRadius: 5, cursor: 'default', border: '1px solid', borderColor: fi === 0 ? CALL_COLOR : 'divider', bgcolor: fi === 0 ? CALL_COLOR : 'transparent' }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: fi === 0 ? '#fff' : 'text.secondary' }}>{f}</Typography>
                    </Box>
                  ))}
                </Box>
                <FilterListIcon sx={{ fontSize: '1rem', color: 'text.disabled', flexShrink: 0 }} />
              </Box>
              {/* Call rows */}
              {calls.map((c, ci) => (
                <Box key={ci} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.1,
                  borderBottom: ci < calls.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
                  bgcolor: ci === 0 ? `${CALL_COLOR}06` : 'background.paper',
                  borderLeft: ci === 0 ? `3px solid ${CALL_COLOR}` : '3px solid transparent',
                  cursor: 'default',
                }}>
                  {/* Ref badge */}
                  <Box sx={{ flexShrink: 0, px: 0.75, py: 0.3, borderRadius: 1, border: '1px solid', borderColor: `${CALL_COLOR}30`, bgcolor: `${CALL_COLOR}08` }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: CALL_COLOR, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{c.ref}</Typography>
                  </Box>
                  {/* Title */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: ci === 0 ? 700 : 500, color: ci === 0 ? CALL_COLOR : 'text.primary', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.title}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{c.budget} per project</Typography>
                  </Box>
                  {/* Deadline */}
                  <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.35, px: 0.75, py: 0.3, borderRadius: 1, bgcolor: c.urgent ? 'rgba(244,67,54,0.08)' : 'action.disabledBackground', border: '1px solid', borderColor: c.urgent ? 'rgba(244,67,54,0.25)' : 'transparent' }}>
                    <AccessTimeIcon sx={{ fontSize: '0.72rem', color: c.urgent ? '#f44336' : 'text.disabled' }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: c.urgent ? 700 : 400, color: c.urgent ? '#f44336' : 'text.disabled' }}>{c.open ? `${c.days}d` : 'Opening soon'}</Typography>
                  </Box>
                  {/* Bookmark */}
                  {c.bookmarked
                    ? <BookmarkIcon sx={{ fontSize: '1rem', color: ACCENT, flexShrink: 0 }} />
                    : <BookmarkBorderIcon sx={{ fontSize: '1rem', color: 'text.disabled', flexShrink: 0 }} />
                  }
                </Box>
              ))}
            </Box>
          );
        }

        // ── Funding workflow: call detail ──────────────────────────────────
        if (block.type === 'funding_call_detail') {
          const CD = '#5b6fd4';
          const keywords = ['Food Security', 'Climate Adaptation', 'Agri-tech', 'Soil Microbiome', 'Sustainable Production', 'Cross-regional'];
          const matchedAreas = 4;
          const totalAreas  = 6;
          const matchPct    = Math.round(matchedAreas / totalAreas * 100);
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${CD}25`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 2.5, py: 1.5, background: `linear-gradient(90deg, ${CD}12 0%, ${CD}05 100%)`, borderBottom: '1px solid', borderColor: `${CD}18` }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase', color: 'text.disabled', mb: 0.4 }}>Call Reference</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.4 }}>
                      <Box sx={{ px: 0.85, py: 0.25, borderRadius: 1, bgcolor: CD, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#fff' }}>SFS-01-2026</Typography>
                      </Box>
                      <Box sx={{ px: 0.85, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: `${CD}30`, bgcolor: `${CD}08`, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: CD }}>Horizon Europe · Cluster 6</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.75, py: 0.25, borderRadius: 1, bgcolor: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', flexShrink: 0 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4caf50' }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#4caf50' }}>Open</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.4 }}>Sustainable Food Systems in a Changing Climate: Cross-Regional Approaches to Agri-Food Resilience</Typography>
                  </Box>
                  {/* Deadline ring */}
                  <Box sx={{ textAlign: 'center', px: 1.5, py: 0.75, borderRadius: 2, border: '2px solid rgba(244,67,54,0.35)', bgcolor: 'rgba(244,67,54,0.06)', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: '#f44336', lineHeight: 1 }}>28</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#f44336', fontWeight: 600, lineHeight: 1.2 }}>days<br />left</Typography>
                  </Box>
                </Box>
              </Box>
              {/* Key stats grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                {[
                  { icon: <EuroSymbolIcon />,     label: 'Budget',         value: '€5–8M',        sub: 'per project',    color: CD },
                  { icon: <CalendarTodayIcon />,  label: 'Deadline',       value: '15 Sep 2026',   sub: '17:00 Brussels', color: '#f44336' },
                  { icon: <PeopleIcon />,         label: 'Min. Partners',  value: '3 institutions',sub: '3 countries',   color: '#ff9800' },
                  { icon: <AccountTreeIcon />,    label: 'TRL Range',      value: 'TRL 3 → 7',     sub: 'expected',       color: '#9333ea' },
                ].map(({ icon, label, value, sub, color }, idx) => (
                  <Box key={label} sx={{ p: 1.5, borderRight: idx < 3 ? '1px solid' : 'none', borderColor: 'divider', textAlign: 'center' }}>
                    {React.cloneElement(icon, { sx: { fontSize: '1rem', color, mb: 0.5, display: 'block', mx: 'auto' } })}
                    <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mb: 0.3 }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color, lineHeight: 1.2 }}>{value}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{sub}</Typography>
                  </Box>
                ))}
              </Box>
              {/* Keywords */}
              <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: `${CD}04` }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.disabled', mb: 0.75 }}>Call Keywords</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                  {keywords.map(kw => (
                    <Box key={kw} sx={{ px: 0.9, py: 0.25, borderRadius: 5, bgcolor: `${CD}10`, border: '1px solid', borderColor: `${CD}25` }}>
                      <Typography sx={{ fontSize: '0.72rem', color: CD, fontWeight: 600 }}>{kw}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {/* Consortium match */}
              <Box sx={{ px: 2.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <CheckCircleIcon sx={{ fontSize: '0.9rem', color: '#4caf50' }} />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>Consortium Match — Climate Adaptation Research Consortium</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#4caf50' }}>{matchPct}%</Typography>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'action.disabledBackground', overflow: 'hidden', mb: 0.6 }}>
                  <Box sx={{ width: `${matchPct}%`, height: '100%', background: 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)', borderRadius: 3 }} />
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                  Your consortium covers <strong>{matchedAreas} of {totalAreas}</strong> required expertise areas.
                  Gap: <span style={{ color: '#ff9800', fontWeight: 600 }}>Cross-regional field trial capacity</span> and <span style={{ color: '#ff9800', fontWeight: 600 }}>Policy translation</span>.
                </Typography>
              </Box>
              {/* Actions */}
              <Box sx={{ px: 2.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: `${CD}04` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.55, borderRadius: 1.5, border: '1px solid', borderColor: CD, bgcolor: `${CD}08`, cursor: 'default' }}>
                  <BookmarkIcon sx={{ fontSize: '0.85rem', color: CD }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: CD }}>Bookmarked</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.55, borderRadius: 1.5, background: `linear-gradient(135deg, ${CD} 0%, #764ba2 100%)`, cursor: 'default' }}>
                  <OpenInNewIcon sx={{ fontSize: '0.82rem', color: '#fff' }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>Open call website</Typography>
                </Box>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <NotificationsActiveIcon sx={{ fontSize: '0.82rem', color: '#ff9800' }} />
                  <Typography sx={{ fontSize: '0.72rem', color: '#ff9800', fontWeight: 600 }}>Deadline alert set</Typography>
                </Box>
              </Box>
            </Box>
          );
        }

        if (block.type === 'funding_platform') {
          const calls = [
            { title: 'ERC Synergy Grant 2026', agency: 'European Research Council', program: 'ERC', budget: '€10M', days: 28, urgent: true,  partners: '2–4 teams' },
            { title: 'Horizon Europe Cluster 4 — Industry 5.0', agency: 'European Commission', program: 'HEU', budget: '€3–6M', days: 45, urgent: false, partners: '3+ countries' },
            { title: 'Marie Skłodowska-Curie DN 2026', agency: 'European Commission', program: 'MSCA', budget: '€4M', days: 62, urgent: false, partners: '3+ institutions' },
          ];
          const quickLinks = [
            { label: 'Horizon Europe', sub: 'European Commission' },
            { label: 'ERC Advanced Grant', sub: 'European Research Council' },
            { label: 'Pathfinder Open', sub: 'European Innovation Council' },
          ];
          const experts = [
            { initials: 'AK', name: 'Dr. A. Kovač', field: 'Genomics · ML', color: '#667eea' },
            { initials: 'MS', name: 'Dr. M. Svensson', field: 'Climate · Bioinformatics', color: '#ad89ad' },
          ];
          const detail = calls[0];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2.5, border: '1px solid', borderColor: `${ACCENT}22`, overflow: 'hidden' }}>
              {/* Header bar */}
              <Box sx={{
                px: 2.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: `linear-gradient(90deg, ${ACCENT}14 0%, ${ACCENT}06 100%)`,
                borderBottom: '1px solid', borderColor: `${ACCENT}18`,
              }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT }}>
                  Global Funding Opportunities — Platform View
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <NotificationsActiveIcon sx={{ fontSize: '0.85rem', color: '#f44336' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#f44336', fontWeight: 700 }}>3 new calls this week</Typography>
                </Box>
              </Box>

              {/* Latest calls carousel strip */}
              <Box sx={{
                px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(102,126,234,0.04) 0%, rgba(118,75,162,0.03) 100%)',
              }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>
                  Latest Opportunities
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                  {calls.map((c, ci) => (
                    <Box key={ci} sx={{
                      flexShrink: 0, width: 200, borderRadius: 1.5, p: 1.25,
                      border: '1px solid', borderColor: c.urgent ? 'rgba(244,67,54,0.3)' : `${ACCENT}20`,
                      bgcolor: c.urgent ? 'rgba(244,67,54,0.04)' : 'background.paper',
                      position: 'relative',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{
                          bgcolor: c.urgent ? 'rgba(244,67,54,0.12)' : `${ACCENT}10`,
                          color: c.urgent ? '#f44336' : ACCENT,
                          borderRadius: 0.75, px: 0.75, fontSize: '0.65rem', fontWeight: 700, lineHeight: '18px',
                        }}>{c.program}</Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <AccessTimeIcon sx={{ fontSize: '0.7rem', color: c.urgent ? '#f44336' : 'text.disabled' }} />
                          <Typography sx={{ fontSize: '0.68rem', color: c.urgent ? '#f44336' : 'text.disabled', fontWeight: c.urgent ? 700 : 400 }}>
                            {c.days}d
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {c.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>{c.agency}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Main two-col: quick access + call detail */}
              <Box sx={{ display: 'flex', minHeight: 0 }}>

                {/* Left: Quick Access + Suggested Experts */}
                <Box sx={{
                  width: 200, flexShrink: 0,
                  borderRight: '1px solid', borderColor: 'divider',
                }}>
                  {/* Quick access pins */}
                  <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <PushPinIcon sx={{ fontSize: '0.8rem', color: ACCENT }} />
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: ACCENT }}>
                        Quick Access
                      </Typography>
                    </Box>
                    {quickLinks.map((l, li) => (
                      <Box key={li} sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, py: 0.6,
                        borderBottom: li < quickLinks.length - 1 ? '1px solid' : 'none', borderColor: 'divider',
                      }}>
                        <BookmarkIcon sx={{ fontSize: '0.85rem', color: `${ACCENT}80`, flexShrink: 0 }} />
                        <Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>{l.label}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.2 }}>{l.sub}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Divider />

                  {/* Suggested experts */}
                  <Box sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <PeopleIcon sx={{ fontSize: '0.8rem', color: '#ad89ad' }} />
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ad89ad' }}>
                        Suggested Experts
                      </Typography>
                    </Box>
                    {experts.map((ex, ei) => (
                      <Box key={ei} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Box sx={{
                          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                          background: `linear-gradient(135deg, ${ex.color} 0%, ${ex.color}99 100%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: '0.6rem', fontWeight: 700,
                        }}>{ex.initials}</Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>{ex.name}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.2 }}>{ex.field}</Typography>
                        </Box>
                      </Box>
                    ))}
                    <Box sx={{
                      mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.4,
                      bgcolor: 'rgba(173,137,173,0.1)', borderRadius: 1, px: 1, py: 0.4,
                      cursor: 'default',
                    }}>
                      <SendIcon sx={{ fontSize: '0.7rem', color: '#ad89ad' }} />
                      <Typography sx={{ fontSize: '0.7rem', color: '#ad89ad', fontWeight: 600 }}>Suggest to consortium</Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Right: Call detail */}
                <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
                  {/* Agency + Program */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: 1, flexShrink: 0,
                      background: BRAND_GRADIENT,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <EuroSymbolIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: ACCENT }}>{detail.program}</Typography>
                        <VerifiedIcon sx={{ fontSize: '0.8rem', color: '#667eea' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>{detail.agency}</Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.4,
                      bgcolor: 'rgba(244,67,54,0.1)', borderRadius: 1, px: 0.9, py: 0.3,
                    }}>
                      <AccessTimeIcon sx={{ fontSize: '0.75rem', color: '#f44336' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#f44336', fontWeight: 700 }}>{detail.days} days left</Typography>
                    </Box>
                  </Box>

                  {/* Call title */}
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'text.primary', lineHeight: 1.3, mb: 1.25 }}>
                    {detail.title}
                  </Typography>

                  {/* Key info grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                    {[
                      { label: 'Budget', value: detail.budget, icon: <EuroSymbolIcon sx={{ fontSize: '0.8rem' }} /> },
                      { label: 'Partners', value: detail.partners, icon: <PeopleIcon sx={{ fontSize: '0.8rem' }} /> },
                      { label: 'Programme', value: 'ERC Work Programme 2026', icon: <MenuBookIcon sx={{ fontSize: '0.8rem' }} /> },
                      { label: 'Type', value: 'Research & Innovation', icon: <PsychologyIcon sx={{ fontSize: '0.8rem' }} /> },
                    ].map(({ label, value, icon }) => (
                      <Box key={label} sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 0.75,
                        p: 0.75, borderRadius: 1, bgcolor: `${ACCENT}07`,
                        border: '1px solid', borderColor: `${ACCENT}15`,
                      }}>
                        <Box sx={{ color: ACCENT, mt: 0.1, flexShrink: 0 }}>{icon}</Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>{value}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Action row */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1.25, py: 0.5, borderRadius: 1.5,
                      border: '1px solid', borderColor: `${ACCENT}30`,
                      cursor: 'default',
                    }}>
                      <BookmarkBorderIcon sx={{ fontSize: '0.85rem', color: ACCENT }} />
                      <Typography sx={{ fontSize: '0.78rem', color: ACCENT, fontWeight: 600 }}>Bookmark</Typography>
                    </Box>
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1.25, py: 0.5, borderRadius: 1.5,
                      background: BRAND_GRADIENT, cursor: 'default',
                    }}>
                      <OpenInNewIcon sx={{ fontSize: '0.85rem', color: '#fff' }} />
                      <Typography sx={{ fontSize: '0.78rem', color: '#fff', fontWeight: 600 }}>View Full Details</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        }

        if (block.type === 'action_buttons') return (
          <Box key={i} sx={{
            my: 4, borderRadius: 2.5,
            border: '1px solid', borderColor: `${ACCENT}22`,
            overflow: 'hidden',
          }}>
            {/* Header bar */}
            <Box sx={{
              px: 2.5, py: 1.25,
              background: `linear-gradient(90deg, ${ACCENT}14 0%, ${ACCENT}06 100%)`,
              borderBottom: '1px solid', borderColor: `${ACCENT}18`,
            }}>
              <Typography sx={{
                fontSize: '0.8rem', fontWeight: 700,
                letterSpacing: 1.2, textTransform: 'uppercase', color: ACCENT,
              }}>
                CollaborationHub — Researcher Card
              </Typography>
            </Box>

            {/* Mock researcher card strip */}
            <Box sx={{
              px: 2.5, py: 2,
              background: 'linear-gradient(135deg, rgba(102,126,234,0.04) 0%, rgba(118,75,162,0.04) 100%)',
              display: 'flex', alignItems: 'center', gap: 2,
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                flexShrink: 0,
              }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ height: 10, width: '40%', bgcolor: 'action.selected', borderRadius: 1, mb: 0.75 }} />
                <Box sx={{ height: 8, width: '60%', bgcolor: 'action.disabledBackground', borderRadius: 1 }} />
              </Box>
              {/* The four buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                {ACTION_BUTTONS_META.map(btn => (
                  <Box key={btn.key} sx={{
                    width: 34, height: 34, borderRadius: '50%',
                    bgcolor: btn.color, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#fff', cursor: 'default',
                    boxShadow: `0 2px 8px ${btn.color}55`,
                  }}>
                    {React.cloneElement(btn.icon, { sx: { fontSize: '1rem' } })}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Button legend */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              gap: 0,
            }}>
              {ACTION_BUTTONS_META.map((btn, idx) => (
                <Box key={btn.key} sx={{
                  p: 2, textAlign: 'center',
                  borderRight: idx < 3 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: '50%',
                    bgcolor: btn.color, mx: 'auto', mb: 1.25,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff',
                    boxShadow: `0 3px 12px ${btn.color}44`,
                  }}>
                    {React.cloneElement(btn.icon, { sx: { fontSize: '1.4rem' } })}
                  </Box>
                  <Typography sx={{
                    fontWeight: 700, fontSize: '1.2rem', mb: 0.5,
                    color: btn.color,
                  }}>
                    {btn.label}
                  </Typography>
                  <Typography sx={{
                    fontSize: '1.05rem', color: 'text.secondary',
                    lineHeight: 1.4,
                  }}>
                    {btn.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );

        if (block.type === 'interdisciplinary_map') {
          const disciplines = [
            { label: 'Biomedical Sciences', color: '#e53935', methods: ['Clinical trials', 'Molecular biology', 'Epidemiology'], icon: <PsychologyIcon /> },
            { label: 'Computer Science / AI', color: '#667eea', methods: ['Machine learning', 'Data pipelines', 'Validation'], icon: <AutoAwesomeIcon /> },
            { label: 'Social Sciences', color: '#ff9800', methods: ['Behavioural analysis', 'Ethics review', 'Policy impact'], icon: <PeopleIcon /> },
            { label: 'Engineering', color: '#4caf50', methods: ['System design', 'Prototyping', 'Tech transfer'], icon: <AccountTreeIcon /> },
          ];
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2, border: '1px solid', borderColor: `${ACCENT}20`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.25, background: `linear-gradient(90deg, ${ACCENT}12 0%, ${ACCENT}06 100%)`, borderBottom: '1px solid', borderColor: `${ACCENT}18` }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT }}>
                  Interdisciplinary Consortium — Expertise Map
                </Typography>
              </Box>
              {/* Central call banner */}
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: `${ACCENT}06` }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '50%', background: BRAND_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <EuroSymbolIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2 }}>Horizon Europe — EIC Pathfinder 2026</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>AI-Assisted Early Diagnosis for Chronic Disease · Budget €3.5M · 4 Partners required</Typography>
                </Box>
                <Box sx={{ ml: 'auto', px: 1.25, py: 0.4, borderRadius: 1, bgcolor: 'rgba(244,67,54,0.08)', border: '1px solid rgba(244,67,54,0.25)', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#f44336', fontWeight: 700 }}>42 days left</Typography>
                </Box>
              </Box>
              {/* Discipline grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                {disciplines.map((d, di) => (
                  <Box key={di} sx={{
                    px: 2.5, py: 2,
                    borderRight: di % 2 === 0 ? '1px solid' : 'none',
                    borderBottom: di < 2 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    borderLeft: `3px solid ${d.color}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: `${d.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {React.cloneElement(d.icon, { sx: { fontSize: '0.9rem', color: d.color } })}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2 }}>{d.label}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {d.methods.map(m => (
                        <Box key={m} sx={{ px: 0.75, py: 0.2, borderRadius: 4, bgcolor: `${d.color}10`, border: '1px solid', borderColor: `${d.color}28` }}>
                          <Typography sx={{ fontSize: '0.72rem', color: d.color, fontWeight: 600 }}>{m}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Coverage footer */}
              <Box sx={{ px: 2.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'background.paper' }}>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', flexShrink: 0 }}>Call expertise coverage</Typography>
                <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                  <Box sx={{ width: '83%', height: '100%', background: 'linear-gradient(90deg, #e53935, #667eea, #ff9800, #4caf50)', borderRadius: 3 }} />
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#4caf50', flexShrink: 0 }}>5 / 6 matched</Typography>
              </Box>
            </Box>
          );
        }

        if (block.type === 'interdisciplinary_wps') {
          const wps = [
            {
              num: 'WP1', title: 'Clinical Data Infrastructure', lead: 'Biomedical', leadColor: '#e53935',
              tasks: [
                { label: 'Patient cohort design', disciplines: ['#e53935', '#ff9800'] },
                { label: 'Ethics & consent framework', disciplines: ['#ff9800', '#e53935'] },
                { label: 'Data ingestion pipeline', disciplines: ['#e53935', '#667eea'] },
              ],
            },
            {
              num: 'WP2', title: 'AI Model Development & Validation', lead: 'Computer Science', leadColor: '#667eea',
              tasks: [
                { label: 'Feature engineering', disciplines: ['#667eea', '#e53935'] },
                { label: 'Model training & benchmarking', disciplines: ['#667eea'] },
                { label: 'Clinical validation protocol', disciplines: ['#667eea', '#e53935', '#ff9800'] },
              ],
            },
            {
              num: 'WP3', title: 'Implementation & Societal Impact', lead: 'Social Sciences', leadColor: '#ff9800',
              tasks: [
                { label: 'User acceptance study', disciplines: ['#ff9800', '#4caf50'] },
                { label: 'System integration & deployment', disciplines: ['#4caf50', '#667eea'] },
                { label: 'Policy brief & dissemination', disciplines: ['#ff9800', '#e53935'] },
              ],
            },
          ];
          const disciplineColors = { '#e53935': 'Biomed', '#667eea': 'CS/AI', '#ff9800': 'Social', '#4caf50': 'Eng' };
          return (
            <Box key={i} sx={{ my: 4, borderRadius: 2, border: '1px solid', borderColor: `${ACCENT}20`, overflow: 'hidden' }}>
              <Box sx={{ px: 2.5, py: 1.25, background: `linear-gradient(90deg, ${ACCENT}12 0%, ${ACCENT}06 100%)`, borderBottom: '1px solid', borderColor: `${ACCENT}18`, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT, flex: 1 }}>
                  Work Package Structure — Cross-Disciplinary Task Assignment
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {Object.entries(disciplineColors).map(([c, l]) => (
                    <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{l}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {wps.map((wp, wi) => (
                <Box key={wi} sx={{ borderBottom: wi < wps.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  {/* WP header */}
                  <Box sx={{ px: 2.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: `${wp.leadColor}06`, borderLeft: `3px solid ${wp.leadColor}` }}>
                    <Box sx={{ px: 0.75, py: 0.2, borderRadius: 0.75, bgcolor: wp.leadColor }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#fff' }}>{wp.num}</Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', flex: 1 }}>{wp.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Lead:</Typography>
                      <Box sx={{ px: 0.6, py: 0.15, borderRadius: 4, bgcolor: `${wp.leadColor}18`, border: '1px solid', borderColor: `${wp.leadColor}30` }}>
                        <Typography sx={{ fontSize: '0.68rem', color: wp.leadColor, fontWeight: 700 }}>{wp.lead}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  {/* Tasks */}
                  {wp.tasks.map((task, ti) => (
                    <Box key={ti} sx={{ px: 2.5, py: 0.85, display: 'flex', alignItems: 'center', gap: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0, ml: 1 }} />
                      <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', flex: 1 }}>{task.label}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.4 }}>
                        {task.disciplines.map((c, ci) => (
                          <Box key={ci} sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: c, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid white', ml: ci > 0 ? -0.75 : 0 }}>
                            <Typography sx={{ fontSize: '0.45rem', color: '#fff', fontWeight: 700 }}>{disciplineColors[c]?.slice(0,2)}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === 'user_personas') {
          const personas = [
            {
              icon: <SchoolIcon />,
              role: 'Undergraduate Student',
              institution: 'University of Helsinki · Year 3',
              color: '#4caf50',
              uses: ['Build a CV entry', 'Join a junior contributor role', 'Explore funding calls', 'Get referenced by a PI'],
            },
            {
              icon: <MenuBookIcon />,
              role: 'PhD / Postdoc',
              institution: 'ETH Zürich · Doctoral Programme',
              color: '#667eea',
              uses: ['Gain visibility across institutions', 'Find postdoctoral opportunities', 'Co-author proposals', 'Join MSCA networks'],
            },
            {
              icon: <PsychologyIcon />,
              role: 'Principal Investigator',
              institution: 'University of Amsterdam · Associate Prof.',
              color: ACCENT,
              uses: ['Recruit consortium partners', 'Manage proposal writing', 'Track funding calls', 'Coordinate multi-site teams'],
            },
            {
              icon: <BusinessIcon />,
              role: 'Industry R&D',
              institution: 'Applied Research Division',
              color: '#ff9800',
              uses: ['Find academic co-applicants', 'Access Horizon Europe calls', 'Fulfil academia-industry requirements', 'Verify partner credentials'],
            },
          ];
          return (
            <Box key={i} sx={{ my: 4 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
                {personas.map((p, pi) => (
                  <Box key={pi} sx={{
                    borderRadius: 2, border: '1px solid', borderColor: `${p.color}30`,
                    overflow: 'hidden',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: `0 4px 20px ${p.color}20` },
                  }}>
                    {/* Header */}
                    <Box sx={{ px: 2, py: 1.5, bgcolor: `${p.color}0e`, borderBottom: '1px solid', borderColor: `${p.color}20`, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff' }}>
                        {React.cloneElement(p.icon, { sx: { fontSize: '1.1rem' } })}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2 }}>{p.role}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.3 }}>{p.institution}</Typography>
                      </Box>
                    </Box>
                    {/* Uses list */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                      {p.uses.map((u, ui) => (
                        <Box key={ui} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.color, flexShrink: 0, mt: 0.55 }} />
                          <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.4 }}>{u}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        }

        if (block.type === 'cv_entry') {
          return (
            <Box key={i} sx={{
              my: 4, borderRadius: 2, border: '1px solid', borderColor: `${ACCENT}22`, overflow: 'hidden',
            }}>
              <Box sx={{ px: 2.5, py: 1, bgcolor: `${ACCENT}0c`, borderBottom: '1px solid', borderColor: `${ACCENT}18` }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: ACCENT }}>
                  CV Entry — Example
                </Typography>
              </Box>
              <Box sx={{ px: 2.5, py: 2.5, bgcolor: 'background.paper' }}>
                {/* Section heading */}
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'text.primary', mb: 1.5, pb: 0.5, borderBottom: '2px solid', borderColor: 'text.primary' }}>
                  Research Experience
                </Typography>
                {/* CV entry */}
                <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                  <Typography sx={{ fontSize: '0.88rem', color: 'text.disabled', whiteSpace: 'nowrap', minWidth: 90, fontFamily: 'monospace' }}>
                    Jan – Jun 2026
                  </Typography>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3 }}>
                      Junior Consortium Contributor
                    </Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary', mb: 0.75 }}>
                      Horizon Europe MSCA Doctoral Network Application · Coordinated by University of Amsterdam
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                      {[
                        'Contributed to Work Package 3 scope definition covering data infrastructure and open-access outputs.',
                        'Co-authored the dissemination section; proposal reference HORIZON-MSCA-2026-DN-01-01.',
                        'Gained direct exposure to EU research funding structures, consortium role assignment, and multi-partner budgeting.',
                      ].map((b, bi) => (
                        <Box component="li" key={bi} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: '0.88rem', color: 'text.secondary', lineHeight: 1.5 }}>{b}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                {/* Verification chip */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: `${ACCENT}08`, border: '1px solid', borderColor: `${ACCENT}20`, width: 'fit-content' }}>
                  <VerifiedIcon sx={{ fontSize: '0.9rem', color: ACCENT }} />
                  <Typography sx={{ fontSize: '0.8rem', color: ACCENT, fontWeight: 600 }}>
                    Verified via Otable · Consortium record publicly viewable
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        }

        if (block.type === 'steps') return (
          <Box key={i} component="ol" sx={{ my: 3, pl: 0, listStyle: 'none', counterReset: 'step' }}>
            {block.items.map((item, si) => (
              <Box key={si} component="li" sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '50%', bgcolor: ACCENT_LIGHT, color: ACCENT,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, flexShrink: 0, mt: 0.25,
                }}>{si + 1}</Box>
                <Typography sx={{ fontSize: '1.3rem', lineHeight: 1.8, color: 'text.secondary' }}>
                  <Box component="strong" sx={{ color: 'text.primary' }}>{item.title}.</Box> {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
        );

        if (block.type === 'svg_visual') return (
          <Box key={i} sx={{ my: 4 }}>
            <Box
              component="figure"
              sx={{ m: 0 }}
              dangerouslySetInnerHTML={{ __html: block.svg }}
            />
            {block.caption && (
              <Typography sx={{
                fontSize: '0.72rem', color: 'text.secondary', mt: 1.25,
                textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase',
              }}>{block.caption}</Typography>
            )}
          </Box>
        );

        return (
          <Typography key={i} sx={{
            fontSize: '1.35rem', lineHeight: 1.85, color: 'text.secondary', mb: 2.5,
          }}>{block.text}</Typography>
        );
      })}
    </Box>
  );
}

// ─── Article card ────────────────────────────────────────────────────────────

function ArticleCard({ article, onClick }) {
  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider',
      borderRadius: 2, height: '100%',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      '&:hover': { boxShadow: '0 4px 24px rgba(118,75,162,0.13)', borderColor: ACCENT },
    }}>
      <CardActionArea onClick={() => onClick(article)}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', p: 2.5 }}>

        {article.preview === 'funding_platform' ? (
          <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: `${ACCENT}20`, overflow: 'hidden' }}>
            {/* Mini carousel strip */}
            <Box sx={{
              px: 1.25, py: 0.9,
              background: `linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.05) 100%)`,
              borderBottom: '1px solid', borderColor: `${ACCENT}15`,
              display: 'flex', gap: 0.75, overflowX: 'hidden',
            }}>
              {['ERC Synergy · 28d ⚠', 'HEU Cluster 4 · 45d', 'MSCA DN · 62d'].map((label, li) => (
                <Box key={li} sx={{
                  flexShrink: 0, borderRadius: 1, px: 0.75, py: 0.4,
                  bgcolor: li === 0 ? 'rgba(244,67,54,0.08)' : `${ACCENT}08`,
                  border: '1px solid', borderColor: li === 0 ? 'rgba(244,67,54,0.25)' : `${ACCENT}18`,
                }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: li === 0 ? '#f44336' : ACCENT, whiteSpace: 'nowrap' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
            {/* Mini two-col body */}
            <Box sx={{ display: 'flex' }}>
              {/* Pinned sidebar */}
              <Box sx={{ width: 90, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', px: 1, py: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.6 }}>
                  <PushPinIcon sx={{ fontSize: '0.65rem', color: ACCENT }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: 0.6 }}>Pinned</Typography>
                </Box>
                {['Horizon Europe', 'ERC Adv.', 'Pathfinder'].map((l, li) => (
                  <Box key={li} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.35 }}>
                    <BookmarkIcon sx={{ fontSize: '0.6rem', color: `${ACCENT}70` }} />
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.2 }}>{l}</Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 0.6 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: '0.65rem', color: '#ad89ad' }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#ad89ad', textTransform: 'uppercase', letterSpacing: 0.6 }}>Experts</Typography>
                </Box>
                {[{ i: 'AK', c: '#667eea' }, { i: 'MS', c: '#ad89ad' }].map((ex, ei) => (
                  <Box key={ei} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: ex.c, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#fff', fontWeight: 700 }}>{ex.i}</Typography>
                    </Box>
                    <Box sx={{ height: 5, flex: 1, bgcolor: 'action.disabledBackground', borderRadius: 1 }} />
                  </Box>
                ))}
              </Box>
              {/* Call detail */}
              <Box sx={{ flex: 1, p: 0.75, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: 0.5, background: BRAND_GRADIENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <EuroSymbolIcon sx={{ fontSize: '0.65rem', color: '#fff' }} />
                  </Box>
                  <Box sx={{ height: 6, width: '50%', bgcolor: `${ACCENT}25`, borderRadius: 0.5 }} />
                  <Box sx={{ ml: 'auto', bgcolor: 'rgba(244,67,54,0.1)', borderRadius: 0.5, px: 0.5 }}>
                    <Typography sx={{ fontSize: '0.6rem', color: '#f44336', fontWeight: 700 }}>28d</Typography>
                  </Box>
                </Box>
                <Box sx={{ height: 7, width: '85%', bgcolor: 'action.selected', borderRadius: 0.5, mb: 0.4 }} />
                <Box sx={{ height: 5, width: '65%', bgcolor: 'action.disabledBackground', borderRadius: 0.5, mb: 0.75 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.4, mb: 0.75 }}>
                  {[EuroSymbolIcon, PeopleIcon].map((Icon, ii) => (
                    <Box key={ii} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, bgcolor: `${ACCENT}08`, borderRadius: 0.75, p: 0.4 }}>
                      <Icon sx={{ fontSize: '0.6rem', color: ACCENT }} />
                      <Box sx={{ height: 5, flex: 1, bgcolor: `${ACCENT}20`, borderRadius: 0.5 }} />
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Box sx={{ borderRadius: 0.75, px: 0.6, py: 0.25, border: '1px solid', borderColor: `${ACCENT}30` }}>
                    <BookmarkBorderIcon sx={{ fontSize: '0.6rem', color: ACCENT }} />
                  </Box>
                  <Box sx={{ borderRadius: 0.75, px: 0.75, py: 0.25, background: BRAND_GRADIENT }}>
                    <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 600 }}>View Details</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ) : article.preview === 'profile_card' ? (
          <Box sx={{
            mb: 1.5, width: '100%', borderRadius: 1.5,
            border: '1px solid', borderColor: `${ACCENT}20`,
            overflow: 'hidden',
          }}>
            {/* Avatar + name + badge */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: 1.5, py: 1.25,
              background: `linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.06) 100%)`,
              borderBottom: '1px solid', borderColor: `${ACCENT}15`,
            }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: '0.75rem',
              }}>MC</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2 }}>Dr. Maria Chen</Typography>
                  <VerifiedIcon sx={{ fontSize: '0.8rem', color: '#667eea' }} />
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', lineHeight: 1.2 }}>University of Amsterdam</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.4 }}>
                {['#ad89ad', '#667eea', '#2196f3', '#9e9e9e'].map((c, idx) => (
                  <Box key={idx} sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: c }} />
                ))}
              </Box>
            </Box>
            {/* Skill tags */}
            <Box sx={{ px: 1.5, py: 0.9, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {['Genomics', 'ML', 'Climate', 'Bioinformatics'].map(tag => (
                <Box key={tag} sx={{
                  bgcolor: `${ACCENT}10`, color: ACCENT, borderRadius: 4,
                  px: 0.8, fontSize: '0.68rem', fontWeight: 600, lineHeight: '18px',
                  border: `1px solid ${ACCENT}20`,
                }}>{tag}</Box>
              ))}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.3, ml: 'auto',
                bgcolor: 'rgba(102,126,234,0.10)', borderRadius: 4,
                px: 0.8, border: '1px solid rgba(102,126,234,0.2)',
              }}>
                <CheckCircleIcon sx={{ fontSize: '0.65rem', color: '#4caf50' }} />
                <Typography sx={{ fontSize: '0.68rem', color: '#4caf50', fontWeight: 700 }}>92%</Typography>
              </Box>
            </Box>
          </Box>
        ) : article.preview === 'action_buttons' ? (
          <Box sx={{
            mb: 1.5, width: '100%', borderRadius: 1.5,
            border: '1px solid', borderColor: `${ACCENT}20`,
            overflow: 'hidden',
          }}>
            {/* Mini mock card strip */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              px: 1.25, py: 0.75,
              background: `linear-gradient(90deg, ${ACCENT}10 0%, ${ACCENT}04 100%)`,
              borderBottom: '1px solid', borderColor: `${ACCENT}15`,
            }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                flexShrink: 0,
              }} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ height: 6, width: '45%', bgcolor: 'action.selected', borderRadius: 0.5, mb: 0.5 }} />
                <Box sx={{ height: 5, width: '65%', bgcolor: 'action.disabledBackground', borderRadius: 0.5 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.6, flexShrink: 0 }}>
                {ACTION_BUTTONS_META.map(btn => (
                  <Box key={btn.key} sx={{
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: btn.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff',
                  }}>
                    {React.cloneElement(btn.icon, { sx: { fontSize: '0.7rem' } })}
                  </Box>
                ))}
              </Box>
            </Box>
            {/* Button labels row */}
            <Box sx={{
              display: 'flex',
              '& > *': { flex: 1, textAlign: 'center', py: 0.6, fontSize: '0.72rem',
                fontWeight: 600, letterSpacing: 0.2 },
            }}>
              {ACTION_BUTTONS_META.map((btn, idx) => (
                <Typography key={btn.key} sx={{
                  fontSize: '0.72rem', fontWeight: 600, color: btn.color,
                  py: 0.6, flex: 1, textAlign: 'center',
                  borderRight: idx < 3 ? '1px solid' : 'none',
                  borderColor: `${ACCENT}12`,
                }}>
                  {btn.label}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : article.preview === 'rejection_preview' ? (() => {
          const scoreColor = '#ff9800';
          const sectionScores = [
            { label: 'Team',    score: 8, color: '#4caf50' },
            { label: 'Science', score: 7, color: '#4caf50' },
            { label: 'Impact',  score: 4, color: '#f44336' },
            { label: 'WP',      score: 6, color: '#ff9800' },
            { label: 'Budget',  score: 5, color: '#ff9800' },
          ];
          return (
            <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid rgba(244,67,54,0.2)', overflow: 'hidden' }}>
              {/* Cost header */}
              <Box sx={{ px: 1.25, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(244,67,54,0.06)', borderBottom: '1px solid rgba(244,67,54,0.12)' }}>
                <AccessTimeIcon sx={{ fontSize: '0.82rem', color: '#f44336' }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#f44336', fontWeight: 700 }}>400h invested · 88% rejection rate</Typography>
              </Box>
              {/* Cost bars + AI score side by side */}
              <Box sx={{ display: 'flex', gap: 0, bgcolor: 'background.paper' }}>
                {/* Mini bar chart */}
                <Box sx={{ flex: 1, p: 1.1, borderRight: '1px solid', borderColor: 'divider' }}>
                  {[
                    { label: 'PI',        pct: 110/390*100, color: '#5b6fd4' },
                    { label: 'Co-PI ×3',  pct: 165/390*100, color: '#764ba2' },
                    { label: 'Staff ×4',  pct: 120/390*100, color: '#2196f3' },
                    { label: 'Admin',     pct: 40/390*100,  color: '#9e9e9e' },
                  ].map((r, ri) => (
                    <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: ri < 3 ? 0.5 : 0 }}>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', width: 40, flexShrink: 0 }}>{r.label}</Typography>
                      <Box sx={{ flex: 1, height: 5, borderRadius: 1, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                        <Box sx={{ width: `${r.pct}%`, height: '100%', bgcolor: r.color, borderRadius: 1 }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
                {/* AI score column */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 1.5, py: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
                    <RateReviewIcon sx={{ fontSize: '0.75rem', color: '#7c3aed' }} />
                    <Typography sx={{ fontSize: '0.6rem', color: '#7c3aed', fontWeight: 700 }}>AI Review</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: scoreColor, lineHeight: 1 }}>6.2</Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>/ 10</Typography>
                </Box>
              </Box>
              {/* Section score mini bar */}
              <Box sx={{ display: 'flex', borderTop: '1px solid', borderColor: 'divider' }}>
                {sectionScores.map((s, si) => (
                  <Box key={si} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 0.5, borderRight: si < sectionScores.length - 1 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: `${s.color}06` }}>
                    <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled', mb: 0.25 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.score}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })() : article.preview === 'pw_overview' ? (() => {
          const PW = '#5b6fd4';
          const TABS_MINI = [
            { icon: <DescriptionIcon  sx={{ fontSize: '0.7rem' }} />, label: 'Abstract' },
            { icon: <FolderIcon       sx={{ fontSize: '0.7rem' }} />, label: 'Sections', active: true },
            { icon: <FolderSpecialIcon sx={{ fontSize: '0.7rem' }} />, label: 'Work Pkgs' },
            { icon: <AttachMoneyIcon  sx={{ fontSize: '0.7rem' }} />, label: 'Budget' },
            { icon: <PreviewIcon      sx={{ fontSize: '0.7rem' }} />, label: 'Preview' },
          ];
          return (
            <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: `${PW}25`, overflow: 'hidden' }}>
              {/* Mini tab bar */}
              <Box sx={{ display: 'flex', bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                {TABS_MINI.map((t, ti) => (
                  <Box key={ti} sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.8, py: 0.55, flexShrink: 0, borderBottom: t.active ? `2px solid ${PW}` : '2px solid transparent', color: t.active ? PW : 'text.disabled' }}>
                    {React.cloneElement(t.icon, { sx: { fontSize: '0.7rem', color: t.active ? PW : 'text.disabled' } })}
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: t.active ? 700 : 400, color: t.active ? PW : 'text.disabled', whiteSpace: 'nowrap' }}>{t.label}</Typography>
                  </Box>
                ))}
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75 }}>
                  <DownloadIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                </Box>
              </Box>
              {/* Section editor mini */}
              <Box sx={{ p: 1.25, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.75 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.primary', flex: 1 }}>2.1 Team Qualifications</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.65, py: 0.2, borderRadius: 5, bgcolor: '#ff980015', border: '1px solid #ff980035' }}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ff9800' }} />
                    <Typography sx={{ fontSize: '0.6rem', color: '#ff9800', fontWeight: 700 }}>In Review</Typography>
                  </Box>
                  <LockOpenIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                  <HistoryIcon  sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                </Box>
                {/* Word bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                  <Box sx={{ flex: 1, height: 4, borderRadius: 1, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                    <Box sx={{ width: '43%', height: '100%', bgcolor: PW, borderRadius: 1 }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.6rem', color: PW, fontWeight: 600, flexShrink: 0 }}>342 / 800</Typography>
                </Box>
                {/* Draft line skeletons */}
                {[90, 100, 72].map((w, wi) => (
                  <Box key={wi} sx={{ height: 5, width: `${w}%`, bgcolor: 'action.disabledBackground', borderRadius: 0.5, mb: 0.45 }} />
                ))}
                {/* Action strip */}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.9 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.3, borderRadius: 1, background: 'linear-gradient(135deg, #7c3aed, #9333ea)' }}>
                    <AutoAwesomeIcon sx={{ fontSize: '0.65rem', color: '#fff' }} />
                    <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>AI Generate</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.3, borderRadius: 1, border: '1px solid', borderColor: `${PW}35` }}>
                    <SaveIcon sx={{ fontSize: '0.65rem', color: PW }} />
                    <Typography sx={{ fontSize: '0.6rem', color: PW, fontWeight: 600 }}>Save</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <LockIcon sx={{ fontSize: '0.65rem', color: '#ff9800' }} />
                    <Typography sx={{ fontSize: '0.6rem', color: '#ff9800' }}>2.2 locked</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })() : article.preview === 'ai_assistant' ? (() => {
          const AI = '#7c3aed';
          return (
            <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: `${AI}30`, overflow: 'hidden' }}>
              {/* Header bar */}
              <Box sx={{ px: 1.25, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75, background: `linear-gradient(90deg, ${AI}14 0%, ${AI}04 100%)`, borderBottom: '1px solid', borderColor: `${AI}20` }}>
                <AutoAwesomeIcon sx={{ fontSize: '0.85rem', color: AI }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: AI }}>AI Proposal Assistant</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.35 }}>
                  <AutoAwesomeIcon sx={{ fontSize: '0.65rem', color: '#f59e0b' }} />
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' }}>21 credits</Typography>
                </Box>
              </Box>
              {/* Toolbar buttons */}
              <Box sx={{ px: 1.25, py: 0.75, display: 'flex', gap: 0.6, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.35, borderRadius: 1, background: `linear-gradient(135deg, ${AI} 0%, #9333ea 100%)` }}>
                  <PowerSettingsNewIcon sx={{ fontSize: '0.7rem', color: '#fff' }} />
                  <Typography sx={{ fontSize: '0.65rem', color: '#fff', fontWeight: 700 }}>AI On</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.35, borderRadius: 1, border: '1px solid', borderColor: `${AI}35`, bgcolor: `${AI}08` }}>
                  <BoltIcon sx={{ fontSize: '0.7rem', color: AI }} />
                  <Typography sx={{ fontSize: '0.65rem', color: AI, fontWeight: 700 }}>Generate</Typography>
                </Box>
                {[{ icon: <VisibilityOutlinedIcon sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />, label: 'Preview' }, { icon: <TuneIcon sx={{ fontSize: '0.7rem', color: 'text.disabled' }} />, label: 'Instructions' }].map(b => (
                  <Box key={b.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.35, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    {b.icon}
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{b.label}</Typography>
                  </Box>
                ))}
              </Box>
              {/* Draft text skeleton */}
              <Box sx={{ p: 1.25, bgcolor: 'background.paper' }}>
                <Box sx={{ p: 1, borderRadius: 1, bgcolor: `${AI}05`, border: '1px solid', borderColor: `${AI}15`, mb: 0.75 }}>
                  {[85, 100, 70].map((w, wi) => (
                    <Box key={wi} sx={{ height: 5, width: `${w}%`, bgcolor: 'action.disabledBackground', borderRadius: 0.5, mb: wi < 2 ? 0.5 : 0 }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Box sx={{ px: 0.75, py: 0.3, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Regenerate</Typography>
                  </Box>
                  <Box sx={{ px: 0.75, py: 0.3, borderRadius: 1, background: `linear-gradient(135deg, ${AI} 0%, #9333ea 100%)` }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#fff', fontWeight: 700 }}>Accept</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })() : article.preview === 'consortium_steps' ? (
          <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            {/* Step progress strip */}
            <Box sx={{
              display: 'flex', background: 'linear-gradient(90deg, #667eea14 0%, #4caf5006 100%)',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {[
                { n: 1, label: 'Define', color: '#667eea' },
                { n: 2, label: 'Discover', color: '#ad89ad' },
                { n: 3, label: 'Roles', color: '#2196f3' },
                { n: 4, label: 'Work', color: '#4caf50' },
              ].map((s, si) => (
                <Box key={s.n} sx={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  py: 0.6, borderRight: si < 3 ? '1px solid' : 'none', borderColor: 'divider',
                }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.3 }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.62rem', fontWeight: 800 }}>{s.n}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: s.color }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
            {/* Mini consortium card */}
            <Box sx={{ px: 1.25, py: 0.9, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 800 }}>MC</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Box sx={{ height: 6, width: '50%', bgcolor: 'action.selected', borderRadius: 0.5 }} />
                    <VerifiedIcon sx={{ fontSize: '0.7rem', color: '#667eea' }} />
                  </Box>
                  <Box sx={{ height: 5, width: '38%', bgcolor: 'action.disabledBackground', borderRadius: 0.5, mt: 0.4 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.4 }}>
                  {['#667eea', '#ad89ad', '#2196f3', '#4caf50'].map((c, ci) => (
                    <Box key={ci} sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: c }} />
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {['Soil Microbiome', 'Remote Sensing', 'Climate Modelling'].map(t => (
                  <Box key={t} sx={{ bgcolor: '#667eea10', border: '1px solid #667eea25', borderRadius: 5, px: 0.7, py: 0.15 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#667eea', fontWeight: 600 }}>{t}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : article.preview === 'funding_workflow' ? (() => {
          const CD = '#5b6fd4';
          const agencies = ['EC', 'ERC', 'DFG', 'ANR'];
          const steps = [
            { label: 'Agency',     value: 'European Commission', color: '#003399' },
            { label: 'Programme',  value: 'Cluster 6 · Bioeconomy', color: CD },
            { label: 'Call',       value: 'SFS-01-2026', color: '#7c3aed' },
          ];
          const kws = ['Food Security', 'Climate', 'Agri-tech'];
          return (
            <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: `${CD}25`, overflow: 'hidden' }}>
              {/* Agency chips strip */}
              <Box sx={{ display: 'flex', gap: 0.5, px: 1.25, py: 0.85, borderBottom: '1px solid', borderColor: 'divider', bgcolor: `${CD}06`, overflowX: 'auto' }}>
                {agencies.map((a, ai) => (
                  <Box key={a} sx={{ px: 0.75, py: 0.2, borderRadius: 1, border: '1px solid', borderColor: ai === 0 ? CD : 'divider', bgcolor: ai === 0 ? CD : 'transparent', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: ai === 0 ? '#fff' : 'text.disabled' }}>{a}</Typography>
                  </Box>
                ))}
                <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, border: '1px dashed', borderColor: 'divider', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>+2 more</Typography>
                </Box>
              </Box>
              {/* Breadcrumb trail */}
              {steps.map((s, si) => (
                <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.65, borderBottom: si < steps.length - 1 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: si === steps.length - 1 ? `${CD}06` : 'transparent' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: s.color, flexShrink: 0, ml: si * 0.75 }} />
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', flexShrink: 0 }}>{s.label}</Typography>
                  <ChevronRightIcon sx={{ fontSize: '0.7rem', color: 'text.disabled', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: si === steps.length - 1 ? 700 : 500, color: si === steps.length - 1 ? s.color : 'text.secondary' }}>{s.value}</Typography>
                </Box>
              ))}
              {/* Call detail strip */}
              <Box sx={{ px: 1.25, py: 0.85, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.5 }}>
                    {kws.map(kw => (
                      <Box key={kw} sx={{ px: 0.6, py: 0.1, borderRadius: 4, bgcolor: `${CD}10`, border: '1px solid', borderColor: `${CD}22` }}>
                        <Typography sx={{ fontSize: '0.58rem', color: CD, fontWeight: 600 }}>{kw}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                      <Box sx={{ width: '67%', height: '100%', bgcolor: '#4caf50', borderRadius: 2 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.6rem', color: '#4caf50', fontWeight: 700 }}>4/6 match</Typography>
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'center', px: 0.75, py: 0.4, borderRadius: 1.5, border: '1.5px solid rgba(244,67,54,0.4)', bgcolor: 'rgba(244,67,54,0.06)', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#f44336', lineHeight: 1 }}>28</Typography>
                  <Typography sx={{ fontSize: '0.55rem', color: '#f44336' }}>days</Typography>
                </Box>
              </Box>
            </Box>
          );
        })() : article.preview === 'interdisciplinary' ? (() => {
          const discs = [
            { label: 'Biomedical', color: '#e53935' },
            { label: 'CS / AI', color: '#667eea' },
            { label: 'Social Sci.', color: '#ff9800' },
            { label: 'Engineering', color: '#4caf50' },
          ];
          const tasks = [
            { text: 'Clinical validation protocol', ds: ['#e53935', '#667eea', '#ff9800'] },
            { text: 'Model training & benchmarking', ds: ['#667eea'] },
            { text: 'User acceptance study', ds: ['#ff9800', '#4caf50'] },
          ];
          return (
            <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: `${ACCENT}20`, overflow: 'hidden' }}>
              {/* Discipline chip strip */}
              <Box sx={{ px: 1.1, py: 0.75, display: 'flex', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: `${ACCENT}06`, flexWrap: 'wrap' }}>
                {discs.map(d => (
                  <Box key={d.label} sx={{ px: 0.65, py: 0.2, borderRadius: 4, bgcolor: `${d.color}15`, border: '1px solid', borderColor: `${d.color}30` }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: d.color }}>{d.label}</Typography>
                  </Box>
                ))}
              </Box>
              {/* Mini WP tasks */}
              {tasks.map((t, ti) => (
                <Box key={ti} sx={{ px: 1.1, py: 0.6, display: 'flex', alignItems: 'center', gap: 1, borderBottom: ti < tasks.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', flex: 1, lineHeight: 1.3 }}>{t.text}</Typography>
                  <Box sx={{ display: 'flex' }}>
                    {t.ds.map((c, ci) => (
                      <Box key={ci} sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: c, border: '1.5px solid white', ml: ci > 0 ? -0.5 : 0 }} />
                    ))}
                  </Box>
                </Box>
              ))}
              {/* Coverage bar */}
              <Box sx={{ px: 1.1, py: 0.65, display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', flexShrink: 0 }}>Coverage</Typography>
                <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'action.disabledBackground', overflow: 'hidden' }}>
                  <Box sx={{ width: '83%', height: '100%', background: 'linear-gradient(90deg, #e53935, #667eea, #ff9800, #4caf50)', borderRadius: 2 }} />
                </Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#4caf50', flexShrink: 0 }}>5/6</Typography>
              </Box>
            </Box>
          );
        })() : article.preview === 'target_groups' ? (() => {
          const miniPersonas = [
            { icon: <SchoolIcon sx={{ fontSize: '0.75rem' }} />, label: 'Undergrad', color: '#4caf50', tags: ['CV entry', 'Junior role'] },
            { icon: <MenuBookIcon sx={{ fontSize: '0.75rem' }} />, label: 'PhD / Postdoc', color: '#667eea', tags: ['Visibility', 'MSCA'] },
            { icon: <PsychologyIcon sx={{ fontSize: '0.75rem' }} />, label: 'PI', color: ACCENT, tags: ['Partners', 'Proposals'] },
            { icon: <BusinessIcon sx={{ fontSize: '0.75rem' }} />, label: 'Industry', color: '#ff9800', tags: ['H2020', 'Co-apply'] },
          ];
          return (
            <Box sx={{ mb: 1.5, width: '100%', borderRadius: 1.5, border: '1px solid', borderColor: `${ACCENT}20`, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ px: 1.25, py: 0.7, bgcolor: `${ACCENT}0a`, borderBottom: '1px solid', borderColor: `${ACCENT}15` }}>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.9, textTransform: 'uppercase', color: ACCENT }}>Who is Otable for?</Typography>
              </Box>
              {/* 2×2 grid of mini persona chips */}
              <Box sx={{ p: 0.85, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.6 }}>
                {miniPersonas.map((p, pi) => (
                  <Box key={pi} sx={{ borderRadius: 1, border: '1px solid', borderColor: `${p.color}28`, bgcolor: `${p.color}07`, p: 0.65 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff' }}>
                        {p.icon}
                      </Box>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.primary' }}>{p.label}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                      {p.tags.map(t => (
                        <Box key={t} sx={{ px: 0.5, py: 0.1, borderRadius: 3, bgcolor: `${p.color}15`, border: '1px solid', borderColor: `${p.color}25` }}>
                          <Typography sx={{ fontSize: '0.58rem', color: p.color, fontWeight: 600 }}>{t}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* CV entry strip */}
              <Box sx={{ px: 1.25, py: 0.7, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'background.paper' }}>
                <VerifiedIcon sx={{ fontSize: '0.75rem', color: ACCENT, flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.3 }}>
                  Junior Consortium Contributor · MSCA DN 2026 · <Box component="span" sx={{ color: ACCENT, fontWeight: 600 }}>Verified on Otable</Box>
                </Typography>
              </Box>
            </Box>
          );
        })() : (
          <Box sx={{ fontSize: '2.75rem', mb: 1.5, lineHeight: 1 }}>{article.coverEmoji}</Box>
        )}

        <CategoryChip category={article.category} />
        <Typography sx={{
          fontFamily: 'Georgia, serif', fontWeight: 700,
          fontSize: '1.55rem', lineHeight: 1.4,
          mt: 1.5, mb: 1.5, color: 'text.primary', textWrap: 'balance',
        }}>{article.title}</Typography>
        <Typography sx={{ fontSize: '1.25rem', lineHeight: 1.6, color: 'text.secondary', mb: 2, flexGrow: 1 }}>
          {article.subtitle}
        </Typography>
        {article.tags && article.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mb: 1.5 }}>
            {article.tags.map(tag => (
              <Box key={tag} sx={{ px: 0.9, py: 0.25, borderRadius: 5, bgcolor: `${ACCENT}10`, border: '1px solid', borderColor: `${ACCENT}25` }}>
                <Typography sx={{ fontSize: '1rem', color: ACCENT, fontWeight: 600, lineHeight: 1.2 }}>#{tag}</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: '1.45rem', color: 'text.disabled' }} />
            <Typography variant="body2" color="text.disabled">{article.date}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: '1.45rem', color: 'text.disabled' }} />
            <Typography variant="body2" color="text.disabled">{article.readTime}</Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}

// ─── Shared site header ───────────────────────────────────────────────────────

function BlogHeader({ onLogoClick, onLogin, isAuthenticated }) {
  return (
    <Box sx={{
      background: BRAND_GRADIENT, px: 3, py: 1.75,
      display: 'flex', alignItems: 'center', gap: 2,
    }}>
      <Box component="img" src={logo} alt="Otable"
        onClick={onLogoClick}
        sx={{ height: 40, width: 'auto', cursor: 'pointer', display: 'block' }} />
      <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
      <Typography sx={{
        color: 'rgba(255,255,255,0.85)', fontSize: '1.55rem',
        fontFamily: 'Georgia, serif', letterSpacing: 0.5, flexGrow: 1,
      }}>
        Blog
      </Typography>
      <Button
        size="small"
        startIcon={<LoginIcon sx={{ fontSize: '1.45rem !important' }} />}
        onClick={onLogin}
        sx={{
          color: '#fff', textTransform: 'none', fontSize: '1.55rem',
          border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 1.5, px: 1.5, py: 0.5,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: '#fff' },
        }}
      >
        {isAuthenticated ? 'Continue to Otable' : 'Sign in to Otable'}
      </Button>
    </Box>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────

function BlogFooter({ onLogin, isAuthenticated }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ bgcolor: ACCENT_LIGHT, py: 5, px: 2, mt: 2, borderTop: `1px solid`, borderColor: `${ACCENT}22` }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box component="img" src={logoAlt} alt="Otable"
              sx={{ height: 40, width: 'auto', mb: .5, display: 'block' }} />
            <Typography sx={{ color: 'text.secondary', fontSize: '1.15rem', lineHeight: 1.65 }}>
              Collaboration platform of the modern researcher
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={onLogin}
              sx={{
                background: BRAND_GRADIENT, color: '#fff', fontWeight: 700,
                textTransform: 'none', borderRadius: 2, px: 3,
                boxShadow: 'none', mr: 1.5, mb: 1,
                '&:hover': { filter: 'brightness(1.08)', boxShadow: 'none' },
              }}
            >
              {isAuthenticated ? 'Continue to Otable' : 'Sign in to Otable'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/blog/')}
              sx={{
                color: ACCENT, borderColor: `${ACCENT}60`,
                textTransform: 'none', borderRadius: 2, px: 3, mb: 1,
                '&:hover': { borderColor: ACCENT, bgcolor: `${ACCENT}08` },
              }}
            >
              All Articles
            </Button>
            <Typography sx={{ color: 'text.disabled', fontSize: '0.93rem', mt: 1.5 }}>
              © 2026 Helixome Life Sciences and Information Technologies Ltd. All rights reserved. 

            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ─── Blog index (article list + sidebar) ─────────────────────────────────────

function BlogIndex({ onSelect, onLogoClick, onLogin, isAuthenticated, activeCategory, onCategory, activeDate, onDate, activeTag, onTag }) {
  const filtered = useMemo(() => ARTICLES.filter(a => {
    if (activeCategory && a.category !== activeCategory) return false;
    if (activeDate     && a.date     !== activeDate)     return false;
    if (activeTag      && !(a.tags || []).includes(activeTag)) return false;
    return true;
  }), [activeCategory, activeDate, activeTag]);

  return (
    <Box>
      <BlogHeader onLogoClick={onLogoClick} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      {/* Hero */}
      <Box sx={{
        background: BRAND_GRADIENT, pt: 1, pb: 9, px: 2, textAlign: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260,
          borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 180, height: 180,
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

        <Box
          sx={{ height: 60, width: 'auto', mb: 3, display: 'block', mx: 'auto' }} />
        <Typography sx={{
          fontFamily: 'Georgia, serif',
          fontSize: { xs: '2.75rem', md: '3.25rem' },
          fontWeight: 700, color: '#fff', mb: 2, textWrap: 'balance', lineHeight: 1.25,
        }}>
          Research Collaboration, Explained
        </Typography>
        <Typography sx={{
          fontSize: '1.55rem', color: 'rgba(255,255,255,0.8)',
          maxWidth: 520, mx: 'auto', lineHeight: 1.45, mb: 3,
        }}>
          Guides, opinions, and deep-dives on building better research teams,
          finding funding, and writing proposals that succeed.
        </Typography>
        <Button
          variant="contained"
          startIcon={<LoginIcon />}
          onClick={onLogin}
          sx={{
            bgcolor: '#fff', color: ACCENT, fontWeight: 700,
            textTransform: 'none', borderRadius: 2, px: 3,
            boxShadow: 'none',
            '&:hover': { bgcolor: ACCENT_LIGHT, boxShadow: 'none' },
          }}
        >
          {isAuthenticated ? 'Continue to Otable' : 'Sign in to Otable'}
        </Button>
      </Box>

      {/* Main two-column layout */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>

          {/* Article grid */}
          <Grid item xs={12} md={8}>
            {(activeCategory || activeDate || activeTag) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '1.15rem', color: 'text.secondary' }}>
                  Showing {filtered.length} {filtered.length === 1 ? 'article' : 'articles'}
                  {activeCategory ? ` in "${activeCategory}"` : ''}
                  {activeDate ? ` from ${activeDate}` : ''}
                  {activeTag ? ` tagged "${activeTag}"` : ''}
                </Typography>
                <Chip
                  label="Clear filter"
                  size="small"
                  onDelete={() => { onCategory(null); onDate(null); onTag(null); }}
                  sx={{ fontSize: '1.15rem' }}
                />
              </Box>
            )}
            {filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">No articles match this filter.</Typography>
                <Button onClick={() => { onCategory(null); onDate(null); onTag(null); }}
                  sx={{ mt: 2, textTransform: 'none', color: ACCENT }}>
                  Clear filter
                </Button>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filtered.map(article => (
                  <Grid item xs={12} sm={6} key={article.slug}>
                    <ArticleCard article={article} onClick={onSelect} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={3.5}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <BlogSidebar
                activeCategory={activeCategory}
                onCategory={onCategory}
                activeDate={activeDate}
                onDate={onDate}
                activeTag={activeTag}
                onTag={onTag}
                onArticle={onSelect}
                onLogin={onLogin}
              />
            </Box>
          </Grid>

        </Grid>
      </Container>

      <BlogFooter onLogin={onLogin} isAuthenticated={isAuthenticated} />
    </Box>
  );
}

// ─── Article view (content + sidebar) ────────────────────────────────────────

function ArticleView({ article, onBack, onSelect, onLogoClick, onLogin, isAuthenticated, onCategory, onDate }) {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [article.slug]);

  return (
    <Box>
      <ReadingProgress />
      <BlogHeader onLogoClick={onLogoClick} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      {/* Article header */}
      <Box sx={{ background: BRAND_GRADIENT, pt: 6, pb: 8, px: 2 }}>
        <Container maxWidth="lg">
          <Button startIcon={<ArrowBackIcon />} onClick={onBack}
            sx={{
              color: 'rgba(255,255,255,0.75)', mb: 3, textTransform: 'none',
              fontSize: '1.25rem', fontWeight: 500, borderRadius: 2,
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}>
            All articles
          </Button>
          <Box><CategoryChip category={article.category} active/></Box>
          
          <Typography sx={{
            fontFamily: 'Georgia, serif',
            fontSize: { xs: '2.25rem', md: '3.25rem' },
            fontWeight: 700, color: '#fff', mt: 2, mb: 2,
            lineHeight: 1.25, textWrap: 'balance', maxWidth: 900,
          }}>
            {article.title}
          </Typography>
          <Typography sx={{
            fontSize: '1.25rem', color: 'rgba(255,255,255,0.8)',
            mb: 3, lineHeight: 1.65, maxWidth: 620,
          }}>
            {article.subtitle}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mb: article.tags?.length ? 2 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CalendarTodayIcon sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.6)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem' }}>
                {article.date}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AccessTimeIcon sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.6)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem' }}>
                {article.readTime}
              </Typography>
            </Box>
          </Box>
          {article.tags && article.tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {article.tags.map(tag => (
                <Box key={tag} sx={{ px: 1.1, py: 0.35, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>#{tag}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* Two-column body */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5}>

          {/* Article content */}
          <Grid item xs={12} md={8}>
            <ArticleContent blocks={article.content} />

            <Divider sx={{ my: 5 }} />

            <Typography variant="h6" sx={{
              fontFamily: 'Georgia, serif', fontWeight: 700, mb: 3, color: 'text.primary',
            }}>
              More from Otable
            </Typography>
            <Grid container spacing={2}>
              {ARTICLES.filter(a => a.slug !== article.slug).slice(0, 2).map(a => (
                <Grid item xs={12} sm={6} key={a.slug}>
                  <ArticleCard article={a} onClick={onSelect} />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack}
                sx={{
                  textTransform: 'none', borderRadius: 2,
                  borderColor: ACCENT, color: ACCENT,
                  '&:hover': { bgcolor: ACCENT_LIGHT, borderColor: ACCENT },
                }}>
                Back to all articles
              </Button>
              <Button variant="contained" startIcon={<LoginIcon />} onClick={onLogin}
                sx={{
                  textTransform: 'none', borderRadius: 2,
                  background: BRAND_GRADIENT,
                  '&:hover': { background: BRAND_GRADIENT, filter: 'brightness(1.08)' },
                }}>
                {isAuthenticated ? 'Continue to Otable' : 'Sign in to Otable'}
              </Button>
            </Box>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={3.5}>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <BlogSidebar
                activeCategory={null}
                onCategory={onCategory}
                activeDate={null}
                onDate={onDate}
                activeTag={null}
                onTag={() => {}}
                onArticle={onSelect}
                onLogin={onLogin}
              />
            </Box>
          </Grid>

        </Grid>
      </Container>

      <BlogFooter onLogin={onLogin} isAuthenticated={isAuthenticated} />
    </Box>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────────

export default function BlogPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserContext();

  const [activeCategory, setActiveCategory] = useState(null);
  const [activeDate,     setActiveDate]     = useState(null);
  const [activeTag,      setActiveTag]      = useState(null);

  const article = slug ? ARTICLES.find(a => a.slug === slug) : null;

  const authDest = isAuthenticated ? '/consortium/' : '/login/';

  const handleSelect   = (a)  => navigate(`/blog/${a.slug}/`);
  const handleBack     = ()   => navigate('/blog/');
  const handleLogo     = ()   => navigate(authDest);
  const handleLogin    = ()   => navigate(authDest);
  const handleCategory = (c)  => { setActiveCategory(c); navigate('/blog/'); };
  const handleDate     = (d)  => { setActiveDate(d); navigate('/blog/'); };
  const handleTag      = (t)  => { setActiveTag(t); navigate('/blog/'); };

  if (slug && !article) {
    return (
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Article not found</Typography>
        <Button onClick={handleBack}
          sx={{ background: BRAND_GRADIENT, color: '#fff', textTransform: 'none', borderRadius: 2, mt: 1 }}>
          Back to Blog
        </Button>
      </Container>
    );
  }

  return article
    ? (
      <ArticleView
        article={article}
        onBack={handleBack}
        onSelect={handleSelect}
        onLogoClick={handleLogo}
        onLogin={handleLogin}
        isAuthenticated={isAuthenticated}
        onCategory={handleCategory}
        onDate={handleDate}
      />
    ) : (
      <BlogIndex
        onSelect={handleSelect}
        onLogoClick={handleLogo}
        onLogin={handleLogin}
        isAuthenticated={isAuthenticated}
        activeCategory={activeCategory}
        onCategory={setActiveCategory}
        activeDate={activeDate}
        onDate={setActiveDate}
        activeTag={activeTag}
        onTag={handleTag}
      />
    );
}
